/**
 * Copyright (c) 2024-2026 Creative Agentics Limited. All rights reserved.
 * Proprietary and confidential. Unauthorised use, reproduction or distribution
 * of this software, in whole or in part, is strictly prohibited.
 * See LICENSE.txt for full terms.
 */

import { preScreenNotChecked } from "./shared/prescreen-status.js";
import { useState, useRef, useEffect, useCallback } from "react";
import { captureError } from './sentry';
import { supabase } from "./supabase";
import { FINANCIAL_BANDS } from "./shared/financial-ranges.js";
import { preSubmitReviewAction, storedRequestItems, softCloseItems, invitesSubmit } from "./shared/pre-submit-gate.js";
import { isTypedSubmit, dfSubmitDecision, RAISE_POINTS_FIRST, SUBMIT_HOLD_TEXT, SUBMIT_NOT_SAVED_TEXT, submittedText } from "./shared/df-submit-gate.js";
import { shouldRunFinish, preSubmitLoopHalted } from "./shared/submit-guards.js";
import { afterUploadTrigger, guardCompletionClaim } from "./shared/followup-prompt.js";
import { analysisRunState } from "./shared/next-action.js";
import { startDfRun, dfRunStateFor, makeRunDriver } from "./shared/df-run.js";
import { plainText } from './shared/plain-text.js';
import { BUILD_VERSION } from './shared/build-version.js';
import { firmQuestionRows } from './shared/firm-questions.js';
import { awaitsClientAnswer } from './shared/alex-waiting.js';
import PrefiledDocsCard from './PrefiledDocsCard';
import { analysisDoneClientLine, ANALYSIS_RUNNING_CLIENT_LINE } from './shared/analysis-client-line.js';
import { UPLOAD_ACCEPT, splitUploads, MAX_UPLOAD_LABEL } from './shared/upload-types.js';
import { UPLOAD_FAILURE, uploadFailure, uploadEach, readingMayStart, uploadFailureMessage, failedFileLabel, readingHeldLine, DF_CONTINUE_LINE, DF_RETURN_LINE, storeFile, recordFailure, notePicked, retryHelps, failedReplacedBy, recordedEntries, fileExt } from './shared/upload-batch.js';
import { coverChecklist } from './shared/checklist-cover.js';

const STAGES = ["welcome","company","ownership","regulatory","classification","aml","sow","sof","documents","summary","report"];

// Individual onboarding (21/10/2026): an individual client's interview is one
// conversation about the person, then the review and the submission.
const INDIVIDUAL_STAGES = ["person","summary","report"];

const STAGE_LABELS = {
  person:"About you",
  welcome:"Welcome", company:"Company Details", ownership:"Ownership",
  regulatory:"Regulatory", classification:"Client Classification", aml:"AML & Clients", sow:"Source of Wealth",
  sof:"Source of Funds", documents:"Documents", summary:"Review", report:"MLRO Report"
};

// Item d: Alex's system prompts (SYSTEM_PROMPT, LANGUAGE_CLAUSE, SOW_SYSTEM_PROMPT and
// the interview turn's builder) moved to src/shared/client-alex-prompt.js and are built
// on the server (api/_client-alex.js). The page sends the conversation and its turn
// context, never a system prompt, and is no longer sent the report or the hidden
// collected_data keys the prompt read (src/shared/client-allowlist.js).
import { clientDocVerdict, CLIENT_DOC_RECEIVED } from './shared/client-allowlist.js';

const renderMarkdown = (text) => {
  if (!text) return null;
  return text.split('\n').map((line, i) => {
    // Horizontal rule
    if (/^---+$/.test(line.trim())) return <hr key={i} style={{ border: 'none', borderTop: '1px solid rgba(255,255,255,0.1)', margin: '8px 0' }} />;
    // Replace **bold** with <strong>
    const parts = line.split(/(\*\*[^*]+\*\*)/g).map((part, j) => {
      if (part.startsWith('**') && part.endsWith('**')) {
        return <strong key={j}>{part.slice(2, -2)}</strong>;
      }
      return part;
    });
    return <span key={i}>{parts}<br/></span>;
  });
};

// v1_973: the portal is white labelled. The welcome names the firm onboarding the
// client (its brand display name, else its firm name), never the software; with
// no firm name to hand it is a neutral greeting, never the software name.
const welcomeLine = (firm, name) => {
  const f = String(firm || '').trim();
  const n = String(name || '').trim();
  if (f) return n ? `Welcome to ${f}, ${n}.` : `Welcome to ${f}.`;
  return n ? `Welcome, ${n}.` : 'Welcome.';
};

const welcomeMsg = (firm) => `${welcomeLine(firm)}

I'm Alex, your dedicated onboarding specialist. My job is to work with you, not just collect information from you.

Think of this as a conversation with a senior compliance professional whose goal is to understand your business properly and present it in the best possible light. I'll ask questions, explore your structure, and help you build a complete, well-contextualised file that gives our compliance team everything they need to make a confident decision.

A few things worth knowing:

This is a conversation, not a form. There are no trick questions, I'm here to help, not to catch you out.
It usually takes 30 to 45 minutes, and you don't need to finish in one sitting, you can return at any time and I'll remember exactly where we left off.
If anything isn't clear or you'd prefer to discuss something directly, our team is available at compliance@compliantid.com
Everything you share is treated in strict confidence.

Worth having to hand before we start (don't worry if you don't have everything, we can note anything missing and you can send it later):

Your company registration details (legal name, number, jurisdiction)
The full names, nationalities and individual email addresses of your directors and shareholders
Recent accounts or a bank statement showing the company as account holder
If you'll be funding with crypto, your wallet addresses

Before we begin, I just need to confirm one quick thing, please tick the box below to confirm you're happy with how we handle your information, then we can get started.`;

// v1_821: read from the build (scripts/build-version.cjs), never a fixed string.
const CLIENT_BUILD = 'CompliantID client ' + BUILD_VERSION;

// Folder drops sweep up .DS_Store, Thumbs.db and whatever else the system keeps beside the
// files; those are quietly ignored. Every other file is checked against the one shared type
// list (src/shared/upload-types.js, upload fix batch 15/10/2026): an accepted file is added,
// a refused one is named with its reason, never dropped without a word.
const CID_SYSTEM_FILE_RE = /^(thumbs\.db|desktop\.ini|icon\r?)$/i;
const cidUsefulFile = (f) => !!f && !!f.name && !f.name.startsWith('.') && !CID_SYSTEM_FILE_RE.test(f.name);

// Walk a dropped folder. The browser gives directories as entries rather than files, so
// without this a client who drags the folder their corporate documents live in gets
// nothing at all and no explanation.
const cidReadEntry = (entry, out, depth = 0) => new Promise((resolve) => {
  if (!entry || depth > 6) return resolve();
  if (entry.isFile) {
    entry.file((f) => { if (cidUsefulFile(f)) out.push(f); resolve(); }, () => resolve());
    return;
  }
  if (!entry.isDirectory) return resolve();
  const reader = entry.createReader();
  const batch = () => reader.readEntries(async (entries) => {
    if (!entries.length) return resolve();
    for (const e of entries) await cidReadEntry(e, out, depth + 1);
    batch();
  }, () => resolve());
  batch();
});

const cidFilesFromDrop = async (dataTransfer) => {
  const out = [];
  const items = dataTransfer && dataTransfer.items ? Array.from(dataTransfer.items) : [];
  const entries = items.map(it => (it.webkitGetAsEntry ? it.webkitGetAsEntry() : null)).filter(Boolean);
  if (entries.length) {
    for (const e of entries) await cidReadEntry(e, out);
    return out;
  }
  return Array.from((dataTransfer && dataTransfer.files) || []).filter(cidUsefulFile);
};
try { console.log('[CompliantID] build:', CLIENT_BUILD); } catch (_) {}

// Sonnet 5's adaptive reasoning can prepend thinking blocks to a response, so
// the text is NOT necessarily content[0]. Join all text-type blocks instead.
// Reading content[0].text on a heavy case silently yields an empty string.
function modelText(data) {
  return ((data && data.content) || []).filter(b => b && b.type === 'text').map(b => b.text).join('\n');
}

// The messages API requires strictly alternating user/assistant roles. Real
// transcripts contain consecutive same-role messages (reply continuations,
// system-saved notices), which 400 the call — merge each run into one message.
function alternateRoles(msgs) {
  const out = [];
  for (const m of (msgs || [])) {
    if (!m || !m.content || !String(m.content).trim()) continue;
    const last = out[out.length - 1];
    if (last && last.role === m.role) last.content = last.content + '\n\n' + m.content;
    else out.push({ role: m.role, content: m.content });
  }
  while (out.length && out[0].role !== 'user') out.shift();
  return out;
}

// ─── MODEL JSON REPAIR ──────────────────────────────────────────────────────
// Claude occasionally returns JSON with an unescaped quote, a literal newline
// inside a string value, a trailing comma — or, on very large reports, output
// truncated mid-structure by the token limit. Repair strategy: let JSON.parse
// find each fault and fix exactly that one thing, iteratively (the parser's
// error position is authoritative and every fix is verified by re-parse), and
// if the document is structurally incomplete, salvage it by closing the open
// string and containers before retrying. A no-progress guard stops the loop,
// so this can never mangle already-valid JSON.
function repairModelJson(input) {
  const s = String(input || '').replace(/```json/gi, '').replace(/```/g, '').replace(/\r/g, '').trim();
  const oStart = s.indexOf('{'); const aStart = s.indexOf('[');
  const isArray = aStart !== -1 && (oStart === -1 || aStart < oStart);
  const start = isArray ? aStart : oStart;
  const closerEnd = isArray ? s.lastIndexOf(']') : s.lastIndexOf('}');
  if (start === -1) throw new Error('No JSON found in model response. It began: ' + s.slice(0, 160));

  const attemptLoop = (text) => {
    let out = text;
    const posFromError = (msg) => {
      const m = /position (\d+)/.exec(msg || '');
      if (m) return parseInt(m[1], 10);
      // Newer V8 gives a context snippet instead of a position — locate it.
      const sn = /"([^"]{6,60})"\.\.\. is not valid JSON/.exec(msg || '') || /\.\.\."([^"]{6,60})"/.exec(msg || '');
      if (sn) { const idx = out.indexOf(sn[1]); if (idx !== -1) return idx + Math.floor(sn[1].length / 2); }
      return null;
    };
    const escapeQuoteBefore = (pos) => {
      let q = out.lastIndexOf('"', Math.min(pos, out.length - 1));
      while (q > 0) {
        if (out[q - 1] === '\\') { q = out.lastIndexOf('"', q - 1); continue; } // already escaped
        let k = q - 1; while (k >= 0 && /\s/.test(out[k])) k--;
        const prev = out[k];
        // Never escape an OPENING quote (one that follows a structural character).
        if (prev === ':' || prev === ',' || prev === '{' || prev === '[') { q = out.lastIndexOf('"', q - 1); continue; }
        break;
      }
      if (q <= 0) return null;
      out = out.slice(0, q) + '\\"' + out.slice(q + 1);
      return true;
    };
    let lastSig = '';
    let triedCommaSweep = false;
    for (let attempt = 0; attempt < 80; attempt++) {
      try { return { parsed: JSON.parse(out) }; }
      catch (e) {
        const msg = e.message || '';
        const pos = posFromError(msg);
        if (pos == null) {
          if (!triedCommaSweep && /Unexpected token/i.test(msg)) {
            triedCommaSweep = true;
            out = out.replace(/,\s*([}\]])/g, '$1');
            continue;
          }
          return { failed: out };
        }
        const sig = pos + ':' + out.length + ':' + msg.slice(0, 30);
        if (sig === lastSig) return { failed: out }; // no progress — stop
        lastSig = sig;
        const c = out[Math.min(pos, out.length - 1)];
        if (/Bad control character/i.test(msg)) {
          const rep = c === '\n' ? '\\n' : c === '\t' ? '\\t' : ' ';
          out = out.slice(0, pos) + rep + out.slice(pos + 1);
          continue;
        }
        if (c === '}' || c === ']') {
          let k = pos - 1; while (k >= 0 && /\s/.test(out[k])) k--;
          if (out[k] === ',') { out = out.slice(0, k) + out.slice(k + 1); continue; }
        }
        // Everything else is a string that closed prematurely on an unescaped
        // quote. Escape the quote that did it and let the parser re-check.
        if (!escapeQuoteBefore(pos)) return { failed: out };
      }
    }
    return { failed: out };
  };

  // Pass 1: the span up to the last closer (the normal, complete case).
  let firstFail = null;
  if (closerEnd > start) {
    const r = attemptLoop(s.slice(start, closerEnd + 1));
    if (r.parsed) return r.parsed;
    firstFail = r.failed;
  }

  // Pass 2: TRUNCATION SALVAGE. Take the text to its true end, walk the
  // structure, then close the open string and containers so the parser gets a
  // complete document. A truncated tail loses its final partial field, which
  // the repairing caller treats as a normal (re-runnable) result — far better
  // than failing the whole action.
  let t = s.slice(start).replace(/\s+$/, '');
  let inStr = false, esc = false;
  const stack = [];
  for (let i = 0; i < t.length; i++) {
    const c = t[i];
    if (inStr) {
      if (esc) { esc = false; continue; }
      if (c === '\\') { esc = true; continue; }
      if (c === '"') inStr = false;
      continue;
    }
    if (c === '"') inStr = true;
    else if (c === '{' || c === '[') stack.push(c);
    else if (c === '}' || c === ']') stack.pop();
  }
  if (esc) t = t.slice(0, -1);                    // dangling backslash
  if (inStr) t += '"';                            // close the cut string
  // Clean the dangling tail so the document closes validly. Handles: a
  // trailing ':' whose value never arrived, a trailing ',', and a closed
  // string that is a KEY missing its colon (only when the open container is
  // an object — inside an array a trailing string is a valid element).
  let guard = 0;
  while (guard++ < 8) {
    t = t.replace(/\s+$/, '');
    if (/:\s*$/.test(t)) {
      // orphan key with no value — remove the whole pair
      t = t.replace(/:\s*$/, '');
      const qEnd = t.length - 1;
      if (t[qEnd] === '"') {
        let q = qEnd - 1;
        while (q > 0 && !(t[q] === '"' && t[q - 1] !== '\\')) q--;
        t = t.slice(0, q);
      }
      continue;
    }
    if (/,\s*$/.test(t)) { t = t.replace(/,\s*$/, ''); continue; }
    if (t.endsWith('"') && stack.length && stack[stack.length - 1] === '{') {
      // A closed string at the tail of an OBJECT: key (drop) or value (keep)?
      let q = t.length - 2;
      while (q > 0 && !(t[q] === '"' && t[q - 1] !== '\\')) q--;
      let k = q - 1; while (k >= 0 && /\s/.test(t[k])) k--;
      if (t[k] === '{' || t[k] === ',') { t = t.slice(0, q); continue; } // dangling key
    }
    break;
  }
  while (stack.length) t += (stack.pop() === '{' ? '}' : ']');
  const r2 = attemptLoop(t);
  if (r2.parsed) return r2.parsed;

  const tail = String(firstFail || r2.failed || s).slice(-160);
  throw new Error('The model returned malformed JSON that could not be repaired. It ended with: \u2026' + tail);
}

// ─── CLIENT STATUS PANEL ────────────────────────────────────────────────────
// Post-submission status the CONTACT can see and act on. Approval almost never
// waits on the contact; it waits on the slowest third party (a director's ID
// check, a UBO's declaration). This panel makes that visible and hands the
// contact a resend button, because a colleague chasing a colleague moves far
// faster than any automated reminder. Data comes from /api/client-read
// resource 'status', which whitelists client-safe fields only.
// Client-paid onboarding: the payment instructions shown after submission,
// on the status panel and on the documents-received screen, until the officer
// confirms receipt. Details come from the firm's configuration via
// load-invite; amount and state come from the session itself.
// ─── LIVE ANALYSIS STRIP ─────────────────────────────────────────────────────
// Renders the engine's real checkpoints while documents are analysed: each line
// is a step that genuinely ran, materialising as it happens. Premium and calm,
// never theatrical — a soft rise on arrival, a teal shimmer through the active
// line, a drawn tick on completion, and a single condensed summary once done.
// STALLED RUNS ARE SAID, NOT SHIMMERED (v1_617). A run the platform kills at its
// time ceiling never writes a failure: its last step stays 'active' forever and
// the strip would shimmer on it for the rest of the session. If the newest step
// has been active longer than the engine can possibly run (its ceiling is 800s;
// 15 minutes leaves margin for clock skew), the strip says so plainly instead.
// v1_802: that rule now lives in analysisRunState (src/shared/next-action.js),
// shared with the officer's view.
// The two required financial-range questions shown as their OWN step after the
// client accepts the terms (v1_647) — separate from consent. Framed as a
// declaration the firm is required to record (VQF customer-profile figures);
// both bands are required before the client can continue. The stored value is
// still labelled "client indicated" on every report — it is an indicated band,
// not a verified figure. The step wrapper enforces "both required"; this renders
// the content only.
function FinancialRangeQuestions({ revenue, assets, onRevenue, onAssets }) {
  const group = (title, sel, on) => (
    <div style={{ marginBottom: 14 }}>
      <div style={{ fontSize: 13.5, color: "rgba(232,232,224,0.8)", fontWeight: 600, marginBottom: 8 }}>{title}</div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
        {FINANCIAL_BANDS.map(b => {
          const on_ = sel === b.value;
          return (
            <button key={b.value} type="button" onClick={() => on(b.value)}
              style={{ padding: "9px 13px", borderRadius: 8, fontSize: 13, cursor: "pointer", fontFamily: "inherit",
                background: on_ ? "rgba(19,164,164,0.25)" : "rgba(255,255,255,0.04)",
                border: `1px solid ${on_ ? "#13a4a4" : "rgba(132,241,237,0.15)"}`,
                color: on_ ? "#e8e8e0" : "rgba(232,232,224,0.6)" }}>{b.label}</button>
          );
        })}
      </div>
    </div>
  );
  return (
    <div>
      <div style={{ fontSize: 13.5, color: "rgba(232,232,224,0.6)", lineHeight: 1.7, marginBottom: 18 }}>
        An approximate figure for the business's annual revenue and total assets is required to complete this onboarding. Please select the band that best fits each. Both are required to continue.
      </div>
      {group("Annual revenue (USD)", revenue, onRevenue)}
      {group("Total assets (USD)", assets, onAssets)}
    </div>
  );
}

function AnalysisStrip({ entries, complete }) {
  if (!entries || !entries.length) return null;
  const doneEntry = entries.find(e => e.key === 'complete');
  const failedEntry = entries.find(e => e.status === 'failed');
  // v1_802: one rule for running and stopped (analysisRunState), shared with the
  // officer's view: the engine runs as a chain of calls, and the gap between two
  // of them is running, not stopped.
  const stalled = !doneEntry && !failedEntry && analysisRunState(entries).stopped;
  if (failedEntry || stalled) {
    const checkpointed = entries.some(e => e.key === 'checkpoint' && e.status === 'done');
    return (
      <div className="as-summary" style={{ display:"flex", alignItems:"flex-start", gap:10, padding:"10px 16px", margin:"0 40px 10px", borderRadius:9, background:"rgba(217,119,6,0.07)", border:"1px solid rgba(217,119,6,0.35)" }}>
        <svg width="15" height="15" viewBox="0 0 15 15" style={{ flexShrink:0, marginTop:2 }}><circle cx="7.5" cy="7.5" r="6.8" fill="rgba(217,119,6,0.12)" stroke="#d97706" strokeWidth="1" /><path d="M7.5 4v4.2M7.5 10.6v.4" fill="none" stroke="#f59e0b" strokeWidth="1.6" strokeLinecap="round" /></svg>
        <span style={{ fontSize:12, color:"rgba(232,232,224,0.8)", lineHeight:1.55 }}>
          {checkpointed
            ? "The analysis of your documents did not fully complete, but the people, ownership and documents from your file are saved. Nothing you have uploaded or said is lost, and the compliance team can finish the analysis from their side. You can carry on here as normal."
            : "The analysis of your documents did not complete. Nothing you have uploaded or said is lost: your documents and answers are safely on file. The compliance team will need to run the analysis again from their side before your file can be submitted, and this screen will update when they do. You can close this page at any time; your link brings you back to this point."}
        </span>
      </div>
    );
  }
  if (complete && doneEntry) {
    return (
      <div className="as-summary" style={{ display:"flex", alignItems:"center", gap:10, padding:"9px 16px", margin:"0 40px 10px", borderRadius:9, background:"rgba(19,164,164,0.07)", border:"1px solid rgba(19,164,164,0.3)" }}>
        <svg width="15" height="15" viewBox="0 0 15 15" style={{ flexShrink:0 }}><circle cx="7.5" cy="7.5" r="6.8" fill="rgba(19,164,164,0.15)" stroke="#13a4a4" strokeWidth="1" /><path className="as-tick" d="M4.4 7.8l2.1 2.1 4.1-4.4" fill="none" stroke="#84f1ed" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" /></svg>
        <span style={{ fontSize:12, color:"rgba(232,232,224,0.75)", lineHeight:1.5 }}>{analysisDoneClientLine(doneEntry)}</span>
      </div>
    );
  }
  // While the analysis runs the client reads one fixed line (29/10/2026, owner
  // decision): the stage lines are the officer's wording ("Checking official
  // registers, public records and adverse media", "Screening 4 named parties
  // against global sanctions and PEP lists") and never reach the client.
  return (
    <div style={{ margin:"0 40px 10px", padding:"12px 16px", borderRadius:10, background:"rgba(10,31,51,0.85)", border:"1px solid rgba(132,241,237,0.18)", position:"relative", overflow:"hidden" }}>
      <div className="as-edge" style={{ position:"absolute", left:0, top:0, bottom:0, width:2, background:"linear-gradient(180deg,#13a4a4,#84f1ed)", opacity:0.7 }} />
      <div style={{ display:"flex", alignItems:"baseline", justifyContent:"space-between", gap:8, marginBottom:8 }}>
        <span style={{ fontSize:10, fontWeight:700, letterSpacing:"1px", textTransform:"uppercase", color:"rgba(132,241,237,0.65)" }}>Analysing your file</span>
        <span style={{ fontSize:10, color:"rgba(232,232,224,0.5)", whiteSpace:"nowrap" }}>usually 3 to 5 minutes</span>
      </div>
      <div className="as-line" data-analysis-client-line style={{ display:"flex", alignItems:"center", gap:9, padding:"3px 0" }}>
        <span className="as-spin" style={{ width:11, height:11, borderRadius:"50%", border:"1.5px solid rgba(132,241,237,0.25)", borderTopColor:"#84f1ed", flexShrink:0, display:"inline-block" }} />
        <span className="as-shimmer" style={{ fontSize:12, color:"rgba(232,232,224,0.85)", lineHeight:1.5 }}>{ANALYSIS_RUNNING_CLIENT_LINE}</span>
      </div>
    </div>
  );
}

function FeePaymentCard({ amount, currency, reference, billing, payeeName }) {
  const bank = billing && billing.bankAccount;
  const wallet = billing && billing.walletAddress;
  // Stablecoin equivalence: the firm states its rate per 1 unit of the fee
  // currency; without one, the token amount mirrors the fee figure (only
  // appropriate for USD-denominated fees). Rounded UP to whole tokens so a
  // client can never underpay by rounding.
  const walletAmount = (billing && billing.walletRate) ? Math.ceil(amount * billing.walletRate) : amount;
  // Collapsible: the client must be able to see and track the case status
  // underneath; a permanent full-height payment card buries it.
  const [open, setOpen] = useState(true);
  const row = (k, v) => v ? (
    <div key={k} style={{ display:"flex", gap:10, fontSize:12, lineHeight:1.7 }}>
      <span style={{ color:"rgba(255,255,255,0.4)", width:110, flexShrink:0 }}>{k}</span>
      <span style={{ color:"#f5f5f0", fontFamily:"monospace", wordBreak:"break-all" }}>{v}</span>
    </div>
  ) : null;
  if (!open) {
    return (
      <div style={{ display:"flex", alignItems:"center", gap:10, padding:"9px 14px", borderRadius:8, background:"rgba(245,158,11,0.08)", border:"1px solid rgba(245,158,11,0.3)", marginBottom:16 }}>
        <span style={{ fontSize:12, fontWeight:600, color:"#f59e0b", flex:1 }}>Payment required: {amount} {currency} · Ref {reference}</span>
        <button onClick={() => setOpen(true)} style={{ background:"transparent", border:"none", color:"rgba(245,158,11,0.85)", fontSize:11.5, cursor:"pointer", textDecoration:"underline", fontFamily:"inherit", padding:0, whiteSpace:"nowrap" }}>Show payment details</button>
      </div>
    );
  }
  return (
    <div style={{ padding:"14px 16px", borderRadius:8, background:"rgba(245,158,11,0.08)", border:"1px solid rgba(245,158,11,0.3)", marginBottom:16 }}>
      <div style={{ display:"flex", alignItems:"flex-start", gap:10 }}>
        <div style={{ fontSize:13, fontWeight:600, color:"#f59e0b", marginBottom:4, flex:1 }}>Payment required before review begins</div>
        <button onClick={() => setOpen(false)} style={{ background:"transparent", border:"none", color:"rgba(245,158,11,0.7)", fontSize:11.5, cursor:"pointer", textDecoration:"underline", fontFamily:"inherit", padding:0, whiteSpace:"nowrap" }}>Minimise</button>
      </div>
      <div style={{ fontSize:12, color:"rgba(255,255,255,0.6)", lineHeight:1.6, marginBottom:10 }}>
        As agreed when you began, a case preparation fee of <strong style={{ color:"#f5f5f0" }}>{amount} {currency}</strong> is payable to {payeeName || "the compliance provider"} before your case enters review. Please quote the reference <strong style={{ color:"#f5f5f0", fontFamily:"monospace" }}>{reference}</strong>. These details have also been sent to you by email so they can be forwarded to whoever handles payments.
      </div>
      {bank && (
        <div style={{ marginBottom: wallet ? 10 : 0 }}>
          <div style={{ fontSize:10.5, color:"rgba(255,255,255,0.4)", textTransform:"uppercase", letterSpacing:"0.5px", marginBottom:4 }}>Bank transfer</div>
          {row("Account name", bank.accountName)}
          {row("Bank", bank.bankName)}
          {row("IBAN", bank.iban)}
          {row("BIC / SWIFT", bank.bic)}
          {row("Sort code", bank.sortCode)}
          {row("Account number", bank.accountNumber)}
          {row("Reference", reference)}
        </div>
      )}
      {wallet && (
        <div>
          <div style={{ fontSize:10.5, color:"rgba(255,255,255,0.4)", textTransform:"uppercase", letterSpacing:"0.5px", marginBottom:4 }}>Or by stablecoin</div>
          {row("Network", billing.walletChain)}
          {row("Token", billing.walletToken)}
          {row("Address", wallet)}
          {row("Amount", `${walletAmount} ${billing.walletToken || currency}`)}
          {walletAmount !== amount && <div style={{ fontSize:11, color:"rgba(255,255,255,0.4)", lineHeight:1.6, marginTop:2 }}>Equivalent of {amount} {currency}.</div>}
          <div style={{ fontSize:11, color:"rgba(255,255,255,0.4)", lineHeight:1.6, marginTop:4 }}>Send only {billing.walletToken || "the stated token"} on the {billing.walletChain || "stated"} network to this address.</div>
        </div>
      )}
      <div style={{ fontSize:11, color:"rgba(255,255,255,0.4)", lineHeight:1.6, marginTop:10 }}>
        This fee covers preparation of your compliance case. It has no bearing on the onboarding decision, which rests solely with the compliance officer. Once payment is received your case moves into review automatically — press Refresh to see the latest status.
      </div>
      {/compliant\s*id/i.test(payeeName || '') && (
        <div style={{ fontSize:10.5, color:"rgba(255,255,255,0.35)", marginTop:8 }}>
          Payments are collected by <span style={{ fontWeight:700, color:"rgba(245,245,240,0.7)" }}>Compliant<span style={{ color:"#13a4a4" }}>ID</span></span>
        </div>
      )}
    </div>
  );
}

// One-tap experience feedback: shown once, after submission, before decision.
// Explicitly stated to have no bearing on the review, and never rendered on
// the officer's case screen — it aggregates in the firm's Analytics only.
function FeedbackCard({ onRate, onDismiss }) {
  const [rated, setRated] = useState(0);
  const [hover, setHover] = useState(0);
  const [comment, setComment] = useState("");
  const [sent, setSent] = useState(false);
  if (sent) {
    return <div style={{ padding:"10px 16px", borderRadius:8, background:"rgba(19,164,164,0.07)", border:"1px solid rgba(19,164,164,0.25)", marginBottom:16, fontSize:12, color:"rgba(232,232,224,0.6)" }}>Thank you — your feedback helps us improve the experience.</div>;
  }
  return (
    <div style={{ padding:"12px 16px", borderRadius:8, background:"rgba(255,255,255,0.02)", border:"1px solid rgba(132,241,237,0.15)", marginBottom:16 }}>
      <div style={{ display:"flex", alignItems:"center", gap:10 }}>
        <div style={{ fontSize:12.5, color:"rgba(232,232,224,0.75)", flex:1 }}>How was the onboarding experience so far?</div>
        <button onClick={onDismiss} style={{ background:"transparent", border:"none", color:"rgba(255,255,255,0.3)", fontSize:11, cursor:"pointer", fontFamily:"inherit", padding:0 }}>Skip</button>
      </div>
      <div style={{ display:"flex", gap:6, margin:"8px 0 4px" }}>
        {[1,2,3,4,5].map(n => (
          <button key={n} onClick={() => setRated(n)} onMouseEnter={() => setHover(n)} onMouseLeave={() => setHover(0)} style={{ background:"transparent", border:"none", cursor:"pointer", padding:2, fontSize:20, lineHeight:1, color: (hover || rated) >= n ? "#f5c518" : "rgba(255,255,255,0.18)", transition:"color 0.12s" }} aria-label={`${n} star${n === 1 ? '' : 's'}`}>★</button>
        ))}
      </div>
      {rated > 0 && (
        <div style={{ display:"flex", gap:8, marginTop:6 }}>
          <input value={comment} onChange={e => setComment(e.target.value)} placeholder="Anything we could do better? (optional)" maxLength={500} style={{ flex:1, padding:"7px 11px", background:"rgba(255,255,255,0.04)", border:"1px solid rgba(255,255,255,0.1)", borderRadius:7, color:"#e8e8e0", fontSize:12, fontFamily:"inherit", outline:"none" }} />
          <button onClick={() => { setSent(true); onRate(rated, comment); }} style={{ padding:"7px 16px", background:"#13a4a4", border:"none", borderRadius:7, color:"#071727", fontSize:12, fontWeight:600, cursor:"pointer", fontFamily:"inherit" }}>Send</button>
        </div>
      )}
      <div style={{ fontSize:10.5, color:"rgba(255,255,255,0.3)", marginTop:8, lineHeight:1.5 }}>Your feedback is about the experience only and has no bearing on the review of your application.</div>
    </div>
  );
}

// v1_636: the client's checklist of request items. Used in two places with one
// behaviour: at the soft close (mode 'close', items Alex asked before SUBMIT that
// are still open) and on the status panel after submission (mode 'status',
// officer-ratified items). Every action is a state transition on the item via
// /api/request-items; nothing here ever blocks submission.
function RequestItemsCard({ items, sessionId, token, mode, onChanged, onAddNow, supportEmail }) {
  const [busy, setBusy] = useState(null);
  const [mode2, setMode2] = useState({}); // itemId -> 'date' | 'na' | 'delegate' | null
  const [val, setVal] = useState({});
  const [alsoSel, setAlsoSel] = useState({}); // v1_681: anchorId -> [extra itemIds] batched into one delegation
  const toggleAlso = (anchor, id) => setAlsoSel(s => { const cur = s[anchor] || []; return { ...s, [anchor]: cur.includes(id) ? cur.filter(x => x !== id) : [...cur, id] }; });
  const list = (items || []).filter(i => i && i.id);
  if (!list.length) return null;
  const open = list.filter(i => ['requested', 'partial', 'asked_in_interview', 'promised', 'delegated'].includes(i.state));
  const act = async (itemId, action, extra) => {
    setBusy(itemId);
    try {
      // v1_921: one line can stand for the same ask sent in two wordings; the
      // client's answer reaches every one of them.
      const ids = [itemId, ...(((list.find(x => x.id === itemId) || {}).mergedIds) || [])];
      let last = null;
      for (const id of ids) {
        const r = await fetch('/api/request-items', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ sessionId, token, action, itemId: id, ...extra }) });
        const b = await r.json().catch(() => ({}));
        if (!r.ok) { alert(b.error || 'Sorry, that did not save. Please try again.'); last = null; break; }
        last = b;
      }
      if (last) { setMode2(m => ({ ...m, [itemId]: null })); if (onChanged) onChanged(last.item); }
    } catch (_) { alert('Sorry, that did not save. Please try again.'); }
    setBusy(null);
  };
  const fmt = (iso) => { try { return new Date(iso).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' }); } catch (_) { return ''; } };
  const minDate = new Date(Date.now() + 86400000).toISOString().slice(0, 10);
  const badge = (i) => i.state === 'promised' && i.dueAt ? { t: `By ${fmt(i.dueAt)}`, c: 'rgba(132,241,237,0.9)' }
    : i.state === 'delegated' ? { t: `With ${i.owner?.name || 'someone else'}`, c: 'rgba(132,241,237,0.9)' }
    : i.state === 'not_applicable' ? { t: 'Explained, waiting for our team to confirm', c: 'rgba(255,255,255,0.55)' }
    : i.state === 'answered' ? { t: 'Sent, being checked', c: 'rgba(132,241,237,0.6)' }
    : i.state === 'partial' ? { t: 'More needed', c: '#f2c14e' } : null;
  // v1_900: an explanation is not a received item until the team confirms it.
  // v1_921: the status checklist lists only what is still owed (an answered item
  // leaves it), so it counts what is still needed rather than a received count
  // that could never rise.
  const received = list.filter(i => ['answered', 'resolved', 'withdrawn'].includes(i.state)).length;
  return (
    <div style={{ border: '1px solid rgba(132,241,237,0.18)', background: 'rgba(19,164,164,0.06)', borderRadius: 10, padding: '14px 16px', marginBottom: 12 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 8 }}>
        <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: '0.8px' }}>{mode === 'close' ? 'Before you submit' : 'Your checklist'}</div>
        <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.35)' }}>{mode === 'status' ? `${list.length} still needed` : `${received} of ${list.length} received`}</div>
      </div>
      {mode === 'close' && <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.55)', lineHeight: 1.6, marginBottom: 10 }}>You can submit now; these just save you a follow up later. Add anything you have to hand, or tell us a date, or mark anything that does not apply.</div>}
      {mode === 'status' && open.length > 0 && (
        <div style={{ fontSize: 11.5, color: 'rgba(255,255,255,0.55)', lineHeight: 1.55, marginBottom: 10 }}>
          Some of these held by someone else, such as your accountant, lawyer or compliance officer? On any item choose <strong style={{ color: 'rgba(255,255,255,0.8)' }}>“Someone else has this”</strong>, then tick any others they hold to send them <strong style={{ color: 'rgba(255,255,255,0.8)' }}>all on one secure link</strong>. We collect and chase them for you.
        </div>
      )}
      {list.map(i => {
        const b = badge(i);
        const isOpen = ['requested', 'partial', 'asked_in_interview', 'promised', 'delegated'].includes(i.state);
        const m = mode2[i.id];
        return (
          <div key={i.id} style={{ padding: '8px 0', borderTop: '1px solid rgba(132,241,237,0.08)' }}>
            <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
              <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', marginTop: 6, background: isOpen ? 'rgba(255,255,255,0.35)' : '#13a4a4', flexShrink: 0 }} />
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 12.5, color: isOpen ? 'rgba(255,255,255,0.8)' : 'rgba(255,255,255,0.45)', lineHeight: 1.5 }}>{plainText(i.label)}</div>
                {b && <div style={{ fontSize: 11, color: b.c, marginTop: 2 }}>{b.t}</div>}
                {/* v1_674: the compliance team has suggested a third party may hold this. The
                    client alone names and authorises them — opens the existing delegate UI. */}
                {isOpen && i.delegationRequested && i.state !== 'delegated' && m !== 'delegate' && (
                  <div style={{ marginTop: 6, padding: '8px 10px', background: 'rgba(19,164,164,0.1)', border: '1px solid rgba(132,241,237,0.25)', borderRadius: 7 }}>
                    <div style={{ fontSize: 11.5, color: 'rgba(255,255,255,0.7)', lineHeight: 1.5, marginBottom: 6 }}>Our compliance team thinks someone else (for example your accountant or lawyer) may be able to provide this more easily. If you agree, you can name them and we will send them a secure link showing only this item. You stay in control and will be copied on it.</div>
                    <button onClick={() => setMode2(x => ({ ...x, [i.id]: 'delegate' }))} disabled={busy === i.id} style={btnS(true)}>Pass this to someone else</button>
                  </div>
                )}
                {isOpen && !m && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 6 }}>
                    {/* v1_727: EVERY item carries BOTH Upload now and Answer now,
                        whatever its type. A client with a file should never have to
                        leave the panel or work out how the item was classified.
                        Both actions run the identical paths as the chat paperclip
                        (Upload -> onAddNow -> fileInputRef -> handleFileUpload;
                        Answer -> inline box -> request-items 'answered'). */}
                    {onAddNow && <button onClick={() => onAddNow(i)} disabled={busy === i.id} style={btnS(true)}>Upload now</button>}
                    {/* v1_900: a document item is answered by uploading it; a written
                        explanation goes through "Can't provide / not relevant" below, to
                        the compliance team for a decision, never as answered. */}
                    {i.type !== 'document' && <button onClick={() => setMode2(x => ({ ...x, [i.id]: 'answer' }))} disabled={busy === i.id} style={btnS(false)}>Answer now</button>}
                    {/* v1_900: a document item is provided by uploading it. "I have sent
                        this" marked a document item done with no file (Diment, 28/09:
                        two items "uploaded", nothing on the case). It stays for a
                        question answered in the chat. */}
                    {i.type !== 'document' && <button onClick={() => act(i.id, 'answered', { evidence: 'answered in chat' })} disabled={busy === i.id} style={btnS(false)}>I have sent this</button>}
                    <button onClick={() => setMode2(x => ({ ...x, [i.id]: 'date' }))} disabled={busy === i.id} style={btnS(false)}>{i.state === 'promised' ? 'New date' : 'I will send by a date'}</button>
                    <button onClick={() => setMode2(x => ({ ...x, [i.id]: 'na' }))} disabled={busy === i.id} style={btnS(false)}>Can't provide / not relevant</button>
                    <button onClick={() => setMode2(x => ({ ...x, [i.id]: 'delegate' }))} disabled={busy === i.id} style={btnS(false)}>{i.state === 'delegated' ? 'Pass to someone else' : 'Someone else has this'}</button>
                  </div>
                )}
                {isOpen && m === 'delegate' && (() => {
                  // v1_681: one contact, one link, one email for SEVERAL items. The
                  // anchor item i is always included; other still-open items the
                  // client also holds can be ticked to go on the SAME link. Items
                  // already delegated are not offered (they are with someone already).
                  const others = open.filter(x => x.id !== i.id && ['requested', 'partial', 'asked_in_interview', 'promised'].includes(x.state));
                  const also = (alsoSel[i.id] || []).filter(id => others.some(o => o.id === id));
                  const chosen = [i.id, ...also];
                  return (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 6 }}>
                    <div style={{ fontSize: 11.5, color: 'rgba(255,255,255,0.5)', lineHeight: 1.5 }}>They will get one secure link showing only the item{chosen.length > 1 ? 's' : ''} you pick here, and we will let you know when they have provided {chosen.length > 1 ? 'them' : 'it'}. You will be copied on the email.</div>
                    {others.length > 0 && (
                      <div style={{ border: '1px solid rgba(132,241,237,0.15)', borderRadius: 7, padding: '8px 10px' }}>
                        <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', marginBottom: 4 }}>Also ask the same person for:</div>
                        {others.map(o => (
                          <label key={o.id} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 12, color: 'rgba(255,255,255,0.75)', padding: '3px 0', cursor: 'pointer' }}>
                            <input type="checkbox" checked={also.includes(o.id)} onChange={() => toggleAlso(i.id, o.id)} style={{ marginTop: 3 }} />
                            <span style={{ lineHeight: 1.4 }}>{plainText(o.label)}</span>
                          </label>
                        ))}
                      </div>
                    )}
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      <input type="text" placeholder="Their name" value={val[i.id + ':n'] || ''} onChange={e => setVal(v => ({ ...v, [i.id + ':n']: e.target.value }))} style={{ flex: 1, minWidth: 140, padding: '6px 8px', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(132,241,237,0.2)', borderRadius: 6, color: '#e8e8e0', fontSize: 12 }} />
                      <input type="email" placeholder="Their email" value={val[i.id + ':e'] || ''} onChange={e => setVal(v => ({ ...v, [i.id + ':e']: e.target.value }))} style={{ flex: 1, minWidth: 180, padding: '6px 8px', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(132,241,237,0.2)', borderRadius: 6, color: '#e8e8e0', fontSize: 12 }} />
                    </div>
                    <input type="text" placeholder="Optional note to them" value={val[i.id + ':m'] || ''} onChange={e => setVal(v => ({ ...v, [i.id + ':m']: e.target.value }))} style={{ padding: '6px 8px', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(132,241,237,0.2)', borderRadius: 6, color: '#e8e8e0', fontSize: 12 }} />
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button onClick={async () => {
                        const nm = (val[i.id + ':n'] || '').trim(), em = (val[i.id + ':e'] || '').trim();
                        if (!nm || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(em)) { alert('Please give their name and a valid email address.'); return; }
                        setBusy(i.id);
                        try {
                          const r = await fetch('/api/delegate-item', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ action: 'assign', sessionId, token, itemIds: chosen, name: nm, email: em, message: (val[i.id + ':m'] || '').trim() }) });
                          const b = await r.json().catch(() => ({}));
                          if (!r.ok) alert(b.error || 'Sorry, that did not send. Please try again.');
                          else {
                            setMode2(x => ({ ...x, [i.id]: null }));
                            setAlsoSel(s => ({ ...s, [i.id]: [] }));
                            const done = Array.isArray(b.items) ? b.items : [{ id: i.id, owner: b.item && b.item.owner }];
                            if (onChanged) done.forEach(it => onChanged({ id: it.id, state: 'delegated', dueAt: null, note: '', owner: it.owner || (b.owner ? { name: b.owner.name } : null) }));
                          }
                        } catch (_) { alert('Sorry, that did not send. Please try again.'); }
                        setBusy(null);
                      }} disabled={busy === i.id} style={btnS(true)}>{chosen.length > 1 ? `Send one link for ${chosen.length} items` : 'Send them the link'}</button>
                      <button onClick={() => { setMode2(x => ({ ...x, [i.id]: null })); setAlsoSel(s => ({ ...s, [i.id]: [] })); }} style={btnS(false)}>Cancel</button>
                    </div>
                  </div>
                  );
                })()}
                {isOpen && m === 'date' && (
                  <div style={{ display: 'flex', gap: 6, marginTop: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                    <input type="date" min={minDate} value={val[i.id] || ''} onChange={e => setVal(v => ({ ...v, [i.id]: e.target.value }))} style={{ padding: '6px 8px', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(132,241,237,0.2)', borderRadius: 6, color: '#e8e8e0', fontSize: 12 }} />
                    <button onClick={() => val[i.id] && act(i.id, 'promise', { dueAt: val[i.id] })} disabled={busy === i.id || !val[i.id]} style={btnS(true)}>Confirm date</button>
                    <button onClick={() => setMode2(x => ({ ...x, [i.id]: null }))} style={btnS(false)}>Cancel</button>
                  </div>
                )}
                {isOpen && m === 'na' && (() => {
                  // The client EXPLAINS, they do not decide: a required reason is
                  // sent to the compliance team as a claim to accept or push back on.
                  // This never removes the item — the reason box is mandatory and the
                  // Send button stays disabled until a short reason is given.
                  const reasonOk = (val[i.id] || '').trim().length >= 3;
                  return (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 6 }}>
                    <div style={{ fontSize: 11.5, color: 'rgba(255,255,255,0.55)', lineHeight: 1.5 }}>Tell us why you cannot provide this, or why it does not apply. It goes to our compliance team as your explanation. The item stays on your checklist until they confirm it, or ask you for it again.</div>
                    <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                      <input type="text" placeholder="Briefly, why can't you provide this, or why does it not apply?" value={val[i.id] || ''} onChange={e => setVal(v => ({ ...v, [i.id]: e.target.value }))} style={{ flex: 1, minWidth: 180, padding: '6px 8px', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(132,241,237,0.2)', borderRadius: 6, color: '#e8e8e0', fontSize: 12 }} />
                      <button onClick={() => reasonOk && act(i.id, 'not_applicable', { reason: val[i.id] })} disabled={busy === i.id || !reasonOk} style={{ ...btnS(true), opacity: (busy === i.id || !reasonOk) ? 0.5 : 1, cursor: (busy === i.id || !reasonOk) ? 'default' : 'pointer' }}>Send to our team</button>
                      <button onClick={() => setMode2(x => ({ ...x, [i.id]: null }))} style={btnS(false)}>Cancel</button>
                    </div>
                    <div style={{ fontSize: 10.5, color: 'rgba(255,255,255,0.4)' }}>A short reason is required. Our compliance team reviews it and will come back to you if they still need the item.</div>
                  </div>
                  );
                })()}
                {/* v1_694: inline answer box for a question item — "Answer now"
                    lands here. The typed answer is recorded against the item and
                    marks it answered, so it reaches the compliance team without a
                    separate chat message. */}
                {isOpen && m === 'answer' && (() => {
                  const answerOk = (val[i.id] || '').trim().length >= 2;
                  return (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 6 }}>
                    <div style={{ fontSize: 11.5, color: 'rgba(255,255,255,0.55)', lineHeight: 1.5 }}>Type your answer to this and we'll record it against your file for our compliance team.</div>
                    <textarea rows={3} placeholder="Your answer…" value={val[i.id] || ''} onChange={e => setVal(v => ({ ...v, [i.id]: e.target.value }))} style={{ padding: '8px 10px', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(132,241,237,0.2)', borderRadius: 6, color: '#e8e8e0', fontSize: 12, fontFamily: 'inherit', lineHeight: 1.5, resize: 'vertical' }} />
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button onClick={() => { if (answerOk) { act(i.id, 'answered', { evidence: 'answered on the checklist', reason: val[i.id].trim() }); setMode2(x => ({ ...x, [i.id]: null })); } }} disabled={busy === i.id || !answerOk} style={{ ...btnS(true), opacity: (busy === i.id || !answerOk) ? 0.5 : 1, cursor: (busy === i.id || !answerOk) ? 'default' : 'pointer' }}>Send answer</button>
                      <button onClick={() => setMode2(x => ({ ...x, [i.id]: null }))} style={btnS(false)}>Cancel</button>
                    </div>
                  </div>
                  );
                })()}
              </div>
            </div>
          </div>
        );
      })}
      {mode === 'status' && supportEmail && <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.3)', marginTop: 8, lineHeight: 1.6 }}>Anything you mark here goes straight to our compliance team. Questions: {supportEmail}.</div>}
    </div>
  );
}
function btnS(primary) {
  return { padding: '5px 10px', borderRadius: 6, fontSize: 11.5, cursor: 'pointer', border: primary ? 'none' : '1px solid rgba(132,241,237,0.25)', background: primary ? '#13a4a4' : 'transparent', color: primary ? '#071727' : 'rgba(232,232,224,0.8)', fontFamily: 'inherit' };
}

// v1_813: THE OUTCOME THE CLIENT SEES after the decision, one wording for both
// return paths (the status panel and the documents first card). A decided
// documents first case showed "Thank you, your documents are with the
// compliance team" with no outcome at all, before and after the welcome email
// (walk, runs 23 to 25/09). A decline never gives reasons (tipping off): the
// outcome is in the email, as the decline notification says. No hyphens or
// dashes: this is client text.
function clientOutcome(status, welcomeSent, contactEmail) {
  if (status === 'approved') return {
    tone: 'approved',
    title: 'Approved. Welcome aboard.',
    body: welcomeSent
      ? 'The compliance review is complete and your application has been approved. Your welcome email with the next steps has been sent' + (contactEmail ? ' to ' + contactEmail : '') + '.'
      : 'The compliance review is complete and your application has been approved. The team will send your welcome email with the next steps.',
  };
  if (status === 'declined') return { tone: 'neutral', title: 'Review complete.', body: 'Please check your email for the outcome.' };
  // v1_905: a file the second signatory returned reads to the client exactly as
  // one awaiting final sign off; the return is the firm's internal step.
  if (status === 'pending_director_review' || status === 'director_returned') return { tone: 'neutral', title: 'Review complete, final sign off in progress.', body: 'The compliance review of your application is complete and it is with the firm for final sign off. We will be in touch' + (contactEmail ? ' at ' + contactEmail : '') + '.' };
  return null;
}

// v1_833: THE QUESTIONS THE FIRM ASKS EVERY CLIENT (policy questionnaire
// section 14). One card, shown on the documents first checklist, below the
// interview chat, and on a return visit (the status panel both modes share).
// Each answer is saved on its own through /api/firm-question-answer, which
// records it against the question on the case.
function FirmQuestionsCard({ questions, collected, sessionId, token, via, onSaved, intro, oneAtATime }) {
  const [draft, setDraft] = useState({});
  const [editing, setEditing] = useState({});
  const [busy, setBusy] = useState(null);
  const [err, setErr] = useState(null);
  const rows = firmQuestionRows(questions, collected).filter(r => !r.retired);
  if (!rows.length) return null;
  const open = rows.filter(r => !r.answered).length;
  // Below the chat the card shows the next unanswered question only, so it
  // never crowds the conversation off a phone screen.
  const firstOpen = rows.find(r => !r.answered);
  const shown = oneAtATime && firstOpen ? [firstOpen] : rows;
  const save = async (q) => {
    const text = String(draft[q] || '').trim();
    if (text.length < 2) return;
    setBusy(q); setErr(null);
    try {
      const r = await fetch('/api/firm-question-answer', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ sessionId, token, question: q, answer: text, via }) });
      const b = await r.json().catch(() => ({}));
      if (!r.ok) setErr(b.error || 'Your answer did not save. Please try again.');
      else { setEditing(e => ({ ...e, [q]: false })); if (onSaved) onSaved(b.answers || []); }
    } catch (_) { setErr('Your answer did not save. Please try again.'); }
    setBusy(null);
  };
  const btn = (primary, disabled) => ({ padding: '7px 13px', background: primary ? 'rgba(132,241,237,0.14)' : 'transparent', border: '1px solid ' + (primary ? 'rgba(132,241,237,0.35)' : 'rgba(255,255,255,0.15)'), borderRadius: 7, color: primary ? '#84f1ed' : 'rgba(232,232,224,0.6)', fontSize: 12.5, fontWeight: primary ? 600 : 400, cursor: disabled ? 'default' : 'pointer', opacity: disabled ? 0.5 : 1, fontFamily: 'inherit' });
  return (
    <div data-firm-questions-card={open} style={{ border: '1px solid rgba(132,241,237,0.18)', background: 'rgba(19,164,164,0.06)', borderRadius: 10, padding: '14px 16px', marginBottom: 14, textAlign: 'left' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 10, flexWrap: 'wrap', marginBottom: 4 }}>
        <div style={{ fontSize: 13.5, fontWeight: 600, color: '#f5f5f0' }}>Questions we ask every client</div>
        <div style={{ fontSize: 11.5, color: open ? 'rgba(232,232,224,0.5)' : '#84f1ed' }}>{open ? `${rows.length - open} of ${rows.length} answered` : 'All answered, thank you'}</div>
      </div>
      <div style={{ fontSize: 12, color: 'rgba(232,232,224,0.5)', lineHeight: 1.55, marginBottom: 10 }}>{intro || 'We ask these of every client, whatever the business. Type your answer under each one and it is saved to your file.'}</div>
      {shown.map((r, i) => {
        const edit = !r.answered || editing[r.question];
        const ok = String(draft[r.question] || '').trim().length >= 2;
        return (
          <div key={i} data-firm-question={r.answered && !editing[r.question] ? 'answered' : 'open'} style={{ padding: '10px 0', borderTop: i ? '1px solid rgba(255,255,255,0.06)' : 'none' }}>
            <div style={{ fontSize: 13.5, color: '#e8e8e0', lineHeight: 1.5, marginBottom: 6 }}>{r.question}</div>
            {edit ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <textarea rows={2} placeholder="Your answer" value={draft[r.question] ?? (r.answer || '')} onChange={e => setDraft(d => ({ ...d, [r.question]: e.target.value }))}
                  style={{ padding: '8px 10px', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(132,241,237,0.2)', borderRadius: 7, color: '#e8e8e0', fontSize: 13, fontFamily: 'inherit', lineHeight: 1.5, resize: 'vertical', width: '100%' }} />
                <div style={{ display: 'flex', gap: 6 }}>
                  <button type="button" onClick={() => save(r.question)} disabled={busy === r.question || !ok} style={btn(true, busy === r.question || !ok)}>{busy === r.question ? 'Saving' : 'Save answer'}</button>
                  {r.answered && <button type="button" onClick={() => setEditing(e => ({ ...e, [r.question]: false }))} style={btn(false, false)}>Cancel</button>}
                </div>
              </div>
            ) : (
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10 }}>
                <div style={{ fontSize: 13, color: 'rgba(232,232,224,0.7)', lineHeight: 1.5, whiteSpace: 'pre-wrap', minWidth: 0, overflowWrap: 'anywhere' }}><span style={{ color: '#84f1ed' }}>✓ </span>{r.answer}</div>
                <button type="button" onClick={() => { setDraft(d => ({ ...d, [r.question]: r.answer })); setEditing(e => ({ ...e, [r.question]: true })); }} style={{ ...btn(false, false), flexShrink: 0, padding: '4px 10px', fontSize: 11.5 }}>Change</button>
              </div>
            )}
          </div>
        );
      })}
      {err && <div style={{ fontSize: 12, color: '#ff9aa2', marginTop: 6 }}>{err}</div>}
    </div>
  );
}

function ClientStatusPanel({ data, onResend, resending, onRefresh, refreshing, supportEmail, collapsed, onToggle, payment, activity, feedback, requestItems, sessionId, token, onItemsChanged, onUploadFor, firmQuestions }) {
  if (!data) return null;

  const isDecided = data.status === 'approved' || data.status === 'declined';
  const isApproved = data.status === 'approved';
  const inReview = !isDecided;
  // v1_813: the outcome box, shared with the documents first card.
  const outcome = clientOutcome(data.status, data.welcomeSent, null);

  const pendingPeople = (data.people || []).filter(p => p.status !== 'complete');
  const pendingForms = (data.forms || []).filter(f => f.status !== 'complete');
  // Questions the compliance team has asked. Previously invisible to the client: only document
  // gaps were shown, so anything that was not a document could be chased by email alone and the
  // client had no way to see what was still wanted or what had already landed.
  const reqs = (data.outstandingRequests || []).filter(r => r && r.item);
  // v1_921: the request items an officer sent are the client's one list.
  const waitingCount = pendingPeople.length + pendingForms.length + reqs.length + (requestItems || []).length;

  const resends = data.resends || {};
  const canResendNow = (kind, id) => {
    const last = resends[`${kind}:${id}`];
    return !last || (Date.now() - new Date(last).getTime()) > 24 * 60 * 60 * 1000;
  };

  const dot = (color) => <span style={{ display:"inline-block", width:8, height:8, borderRadius:"50%", background:color, flexShrink:0 }} />;
  const personStatusLabel = (p) => p.status === 'complete' ? 'Complete'
    : p.status === 'in_progress' ? 'Started, not yet finished'
    : p.status === 'attention' ? 'Our compliance team will be in touch'
    : p.status === 'not_sent' ? 'Invitation coming from our compliance team'
    : 'Link sent, awaiting completion';
  const personStatusColor = (p) => p.status === 'complete' ? '#13a4a4'
    : p.status === 'in_progress' ? '#84f1ed'
    : p.status === 'attention' ? '#f59e0b'
    : 'rgba(255,255,255,0.35)';

  const steps = [
    { label: 'Submitted', done: true },
    { label: 'In review', done: true, active: inReview },
    { label: 'Decision', done: isDecided }
  ];

  return (
    <div style={{ marginBottom:0, background:"#0a1f33", border:"1px solid rgba(132,241,237,0.25)", borderRadius:12, overflow:"hidden" }}>
      <div style={{ padding:"14px 20px", borderBottom:"1px solid rgba(132,241,237,0.08)", display:"flex", justifyContent:"space-between", alignItems:"center" }}>
        <div style={{ fontSize:11, color:"#84f1ed", textTransform:"uppercase", letterSpacing:"1px", fontWeight:600 }}>Your application status</div>
        <div style={{ display:"flex", gap:8 }}>
          <button onClick={onRefresh} disabled={refreshing} style={{ fontSize:11, padding:"3px 10px", background:"transparent", border:"1px solid rgba(132,241,237,0.2)", borderRadius:5, color:"rgba(132,241,237,0.7)", cursor: refreshing ? "default" : "pointer", fontFamily:"inherit", opacity: refreshing ? 0.5 : 1 }}>{refreshing ? "Updating..." : "Refresh"}</button>
          {onToggle && <button onClick={onToggle} style={{ fontSize:11, padding:"3px 10px", background:"transparent", border:"1px solid rgba(132,241,237,0.2)", borderRadius:5, color:"rgba(132,241,237,0.7)", cursor:"pointer", fontFamily:"inherit" }}>{collapsed ? "Show" : "Hide"}</button>}
        </div>
      </div>
      {!collapsed && <div style={{ padding:"18px 20px" }}>
        <div style={{ display:"flex", alignItems:"center", marginBottom: waitingCount || isDecided || outcome || data.infoRequested ? 18 : 4 }}>
          {steps.map((st, i) => (
            <div key={st.label} style={{ display:"flex", alignItems:"center", flex: i < steps.length - 1 ? 1 : "none" }}>
              <div style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:6 }}>
                <div style={{ width:22, height:22, borderRadius:"50%", border:"2px solid " + (st.done ? "#13a4a4" : "rgba(255,255,255,0.15)"), background: st.done && !st.active ? "#13a4a4" : "transparent", display:"flex", alignItems:"center", justifyContent:"center", fontSize:11, color:"white" }}>
                  {st.done && !st.active ? "\u2713" : st.active ? "\u25cf" : ""}
                </div>
                <span style={{ fontSize:11, color: st.active ? "#84f1ed" : st.done ? "rgba(132,241,237,0.6)" : "rgba(255,255,255,0.25)", whiteSpace:"nowrap" }}>{st.label}</span>
              </div>
              {i < steps.length - 1 && <div style={{ flex:1, height:2, background: steps[i+1].done ? "#13a4a4" : "rgba(255,255,255,0.08)", margin:"0 10px", marginBottom:20 }} />}
            </div>
          ))}
        </div>

        {payment && !isDecided && <FeePaymentCard {...payment} />}
        {feedback && !isDecided && <FeedbackCard onRate={feedback.onRate} onDismiss={feedback.onDismiss} />}
        {Array.isArray(activity) && activity.length > 0 && (
          <div style={{ marginBottom:16 }}>
            <div style={{ fontSize:10.5, fontWeight:700, letterSpacing:"0.8px", textTransform:"uppercase", color:"rgba(132,241,237,0.6)", marginBottom:6 }}>What's happening with your case</div>
            {activity.slice(0, 6).map((e, i) => (
              <div key={e.at + e.label} style={{ display:"flex", alignItems:"baseline", gap:9, padding:"3px 0" }}>
                <span style={{ width:6, height:6, borderRadius:"50%", background: i === 0 ? "#13a4a4" : "rgba(132,241,237,0.25)", flexShrink:0, position:"relative", top:-1 }} />
                <span style={{ fontSize:12, color: i === 0 ? "rgba(232,232,224,0.8)" : "rgba(232,232,224,0.5)", flex:1, lineHeight:1.5 }}>{e.label}</span>
                <span style={{ fontSize:10.5, color:"rgba(255,255,255,0.3)", whiteSpace:"nowrap" }}>{new Date(e.at).toLocaleDateString('en-GB', { day:'numeric', month:'short' })}</span>
              </div>
            ))}
          </div>
        )}

        {outcome && (
          <div data-client-outcome={outcome.tone} style={{ padding:"12px 16px", borderRadius:8, background: isApproved ? "rgba(19,164,164,0.1)" : "rgba(255,255,255,0.03)", border: "1px solid " + (isApproved ? "rgba(19,164,164,0.3)" : "rgba(255,255,255,0.1)"), marginBottom: waitingCount ? 16 : 0 }}>
            <div style={{ fontSize:13, fontWeight:600, color: isApproved ? "#13a4a4" : "rgba(255,255,255,0.75)" }}>
              {outcome.title}
            </div>
            <div style={{ fontSize:12, color:"rgba(255,255,255,0.6)", lineHeight:1.6, marginTop:4 }}>{outcome.body}</div>
          </div>
        )}

        {data.infoRequested && !isDecided && (
          <div style={{ padding:"12px 16px", borderRadius:8, background:"rgba(245,158,11,0.08)", border:"1px solid rgba(245,158,11,0.25)", marginBottom:16 }}>
            <div style={{ fontSize:13, fontWeight:600, color:"#f59e0b", marginBottom:2 }}>More information requested</div>
            <div style={{ fontSize:12, color:"rgba(255,255,255,0.55)", lineHeight:1.6 }}>Our compliance team has a few questions. Continue with Alex below and your file will update automatically.</div>
          </div>
        )}

        {waitingCount > 0 && !isDecided && (
          <div style={{ fontSize:12, color:"rgba(255,255,255,0.45)", marginBottom:10, lineHeight:1.6 }}>
            Your review is progressing. The items below are what we are still waiting on. Chasing these internally is the fastest way to speed up your approval.
          </div>
        )}

        {(data.people || []).length > 0 && (
          <div style={{ marginBottom: pendingForms.length || (requestItems || []).length ? 14 : 0 }}>
            <div style={{ fontSize:11, color:"rgba(255,255,255,0.35)", textTransform:"uppercase", letterSpacing:"0.8px", marginBottom:8 }}>Identity verification</div>
            {(data.people || []).map((p, i) => (
              <div key={i} style={{ display:"flex", alignItems:"center", gap:10, padding:"7px 0", borderBottom:"1px solid rgba(255,255,255,0.04)" }}>
                {dot(personStatusColor(p))}
                <div style={{ flex:1, minWidth:0 }}>
                  <span style={{ fontSize:13, color:"rgba(255,255,255,0.8)" }}>{p.name}</span>
                  {p.role && <span style={{ fontSize:11, color:"rgba(255,255,255,0.3)", marginLeft:8 }}>{p.role}</span>}
                  <div style={{ fontSize:11, color:"rgba(255,255,255,0.35)", marginTop:1 }}>{personStatusLabel(p)}</div>
                </div>
                {p.status !== 'complete' && p.status !== 'attention' && p.status !== 'not_sent' && p.id && p.hasEmail && (
                  canResendNow('verification', p.id)
                    ? <button onClick={() => onResend('verification', p.id)} disabled={resending === `verification:${p.id}`} style={{ fontSize:11, padding:"4px 12px", background:"rgba(132,241,237,0.08)", border:"1px solid rgba(132,241,237,0.3)", borderRadius:6, color:"#84f1ed", cursor:"pointer", fontFamily:"inherit", whiteSpace:"nowrap", opacity: resending === `verification:${p.id}` ? 0.5 : 1 }}>{resending === `verification:${p.id}` ? "Sending..." : "Resend link"}</button>
                    : <span style={{ fontSize:11, color:"rgba(255,255,255,0.25)", whiteSpace:"nowrap" }}>Reminder sent</span>
                )}
              </div>
            ))}
          </div>
        )}

        {(data.forms || []).length > 0 && (
          <div style={{ marginBottom: (requestItems || []).length ? 14 : 0 }}>
            <div style={{ fontSize:11, color:"rgba(255,255,255,0.35)", textTransform:"uppercase", letterSpacing:"0.8px", marginBottom:8 }}>Declarations</div>
            {(data.forms || []).map((f, i) => (
              <div key={i} style={{ display:"flex", alignItems:"center", gap:10, padding:"7px 0", borderBottom:"1px solid rgba(255,255,255,0.04)" }}>
                {dot(f.status === 'complete' ? '#13a4a4' : 'rgba(255,255,255,0.35)')}
                <div style={{ flex:1, minWidth:0 }}>
                  <span style={{ fontSize:13, color:"rgba(255,255,255,0.8)" }}>Form {f.formType}</span>
                  <span style={{ fontSize:11, color:"rgba(255,255,255,0.3)", marginLeft:8 }}>{f.name}</span>
                  <div style={{ fontSize:11, color:"rgba(255,255,255,0.35)", marginTop:1 }}>{f.status === 'complete' ? 'Signed' : 'Sent, awaiting signature'}</div>
                </div>
                {f.status !== 'complete' && f.route === 'sumsub_form' && f.id && f.hasEmail && (
                  canResendNow('form', f.id)
                    ? <button onClick={() => onResend('form', f.id)} disabled={resending === `form:${f.id}`} style={{ fontSize:11, padding:"4px 12px", background:"rgba(132,241,237,0.08)", border:"1px solid rgba(132,241,237,0.3)", borderRadius:6, color:"#84f1ed", cursor:"pointer", fontFamily:"inherit", whiteSpace:"nowrap", opacity: resending === `form:${f.id}` ? 0.5 : 1 }}>{resending === `form:${f.id}` ? "Sending..." : "Resend link"}</button>
                    : <span style={{ fontSize:11, color:"rgba(255,255,255,0.25)", whiteSpace:"nowrap" }}>Reminder sent</span>
                )}
              </div>
            ))}
          </div>
        )}

        {/* v1_724: the legacy "please provide" list has been retired. The single
            item surface is now the "Your checklist" card (RequestItemsCard) below,
            which carries per-item actions including "Upload now" on document items,
            so the client never has to leave the panel to supply what it asks for.
            The old read-only list forced a paperclip detour and duplicated the card. */}
        {(requestItems || []).length > 0 && !isDecided && (
          <RequestItemsCard items={requestItems} sessionId={sessionId} token={token} mode="status" onChanged={onItemsChanged} onAddNow={onUploadFor} supportEmail={supportEmail} />
        )}
        {firmQuestions && !isDecided && (
          <FirmQuestionsCard questions={firmQuestions.questions} collected={firmQuestions.collected} sessionId={sessionId} token={token} via="return" onSaved={firmQuestions.onSaved} />
        )}
        {/* v1_921: the "Outstanding documents" list (the analysis's document gaps)
            is retired. Only request items an officer round sent reach the client,
            in the checklist above. */}

        {waitingCount === 0 && !isDecided && (
          <div style={{ fontSize:12, color:"rgba(255,255,255,0.45)", lineHeight:1.6 }}>
            Everything we need from your side is in. Your file is with our compliance team and we will email you as soon as there is an update.
          </div>
        )}
      </div>}
    </div>
  );
}

// Token-validated reads of the client's OWN session/messages/documents via the
// server (service role). Replaces direct anon-key table reads so onboarding tables
// need no anon RLS policy and no client can read another firm's data.
async function clientRead(resource, sessionId, token, columns) {
  try {
    const res = await fetch('/api/client-read', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ resource, sessionId, token, columns })
    });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) {
      console.error('[client-read] FAILED HTTP', res.status, ', ', JSON.stringify(body));
      return { data: resource === 'session' ? null : [], _error: { status: res.status, ...body } };
    }
    return body;
  } catch (e) {
    console.error('[client-read] threw', e?.message);
    return { data: resource === 'session' ? null : [], _error: { thrown: e?.message } };
  }
}

export default function App() {
  const [tokenValid, setTokenValid] = useState(false);
  const [tokenChecked, setTokenChecked] = useState(false);
  const [stage, setStage] = useState("welcome");
  const [messages, setMessages] = useState([{ role: "assistant", content: welcomeMsg(null) }]);
  const [input, setInput] = useState("");
  // consentGiven / consentConfirmed / consentTimestamp keep their names for backward compatibility
  // with existing records, but what they now evidence is that the PRIVACY NOTICE was shown and
  // acknowledged, not consent to processing. AML processing rests on legal obligation and
  // legitimate interests: consent would not be freely given (the client cannot be onboarded
  // without the checks) and could not be honoured if withdrawn (records must be retained and
  // screening must continue). What is worth recording is that the client was informed, which is
  // itself an Article 13 obligation.
  // Upload progress. With analysis now reading the file from storage, the upload is awaited before
  // anything else happens, so a large document means a genuinely long silent wait. Three animated
  // dots do not tell a client whether a 25MB scan is uploading or whether the page has died, and
  // the instinct when nothing moves is to press the button again or close the tab.
  const [docProgress, setDocProgress] = useState(null); // { name, sizeMb, phase, index, total, startedAt }
  const [progressElapsed, setProgressElapsed] = useState(0);
  const [consentGiven, setConsentGiven] = useState(false);
  const [standardConsentTicked, setStandardConsentTicked] = useState(false);
  // Client-indicated financial ranges collected as their own required declaration
  // step after consent (v1_647). Single-select bands; both required to continue;
  // stored as client-indicated, never verified.
  const [financialRevenueBand, setFinancialRevenueBand] = useState(null);
  const [financialAssetsBand, setFinancialAssetsBand] = useState(null);
  const [financialsGiven, setFinancialsGiven] = useState(false); // its own step, after consent (v1_647)
  const [acceptingConsent, setAcceptingConsent] = useState(false);
  const [isReturningClient, setIsReturningClient] = useState(false);
  const [isSowInterview, setIsSowInterview] = useState(false);
  // Individual onboarding (21/10/2026): the client is a natural person (the
  // officer said so at invitation; read only on this page).
  const [isIndividualClient, setIsIndividualClient] = useState(false);
  const [sowParentSessionId, setSowParentSessionId] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  // Live submission latch (v1_653). A ref set SYNCHRONOUSLY at the moment of
  // submission, so the async pre-submit readiness review reads the true state at
  // injection time rather than the stale `submitted` captured in its closure.
  // Submission is a hard stop for any further client-side question generation.
  const submittedRef = useRef(false);
  // 11/10/2026: where this visit starts in the conversation (the messages restored
  // when the page opened), so an unanswered point is asked once per visit.
  const visitStartRef = useRef(null);
  // What the server decided for the last typed SUBMIT: { go } or { hold }.
  const submitStateRef = useRef(null);
  // v1_694: guard so only one client-answer reconciliation runs at a time (it
  // fires after each client message; a slow one must not stack).
  const reconcileInFlightRef = useRef(false);
  // finishDocumentsFirstChat's OWN run-once latch (v1_668), kept SEPARATE from
  // submittedRef. The SUBMIT handler sets submittedRef true at acceptance to
  // hard-stop the pre-submit review loop before the deferred finish runs; that
  // is a different job from "the finish has already started". Collapsing the two
  // (guarding finish on submittedRef) made finish early-return and never complete
  // the case — status stuck in_progress, submittedAt unstamped, the input never
  // locked, no server automation, though Alex had already told the client it was
  // submitted (Sage Capital AG, session 65c8a17c, staging 18/09/26).
  const finishStartedRef = useRef(false);
  // v1_636: the request item the client pressed "Upload now" on; the next successful
  // upload resolves that item explicitly rather than by guessing from the filename.
  const uploadForItemRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [collectedData, setCollectedData] = useState({});
  const [uploadedDocs, setUploadedDocs] = useState([]);
  const [report, setReport] = useState(null);
  const [stageProgress, setStageProgress] = useState({});
  // Documents-first onboarding state (additive; only used for documents_first invites)
  const [isDocumentsFirst, setIsDocumentsFirst] = useState(false);
  const [dfFiles, setDfFiles] = useState([]);
  // Invite with documents (approved 14/10/2026): the firm supplied documents
  // before the invitation (collected_data.prefiled, set by the officer only).
  const [dfPrefiled, setDfPrefiled] = useState(false);
  const [prefiledBusy, setPrefiledBusy] = useState(false);
  // 29/10/2026: a refused confirm or upload from the chat's card is said beside the
  // card (dfStatus shows only on the intake screen, so in the chat it was silent).
  const [prefiledNotice, setPrefiledNotice] = useState('');
  const [dfDragOver, setDfDragOver] = useState(false);
  const [dfDropNote, setDfDropNote] = useState('');
  const [dfRefused, setDfRefused] = useState([]);
  // Owner decisions 1 and 2 (02/11/2026): each chosen file's upload state, keyed by
  // the File itself: { state: 'uploaded', entry } or { state: 'failed', kind, refusal }.
  // A file uploaded once is never sent again; reading waits while one is failed.
  const [dfUploads, setDfUploads] = useState(() => new Map());
  const dfUploadsRef = useRef(dfUploads);
  const putDfUploads = (m) => { dfUploadsRef.current = m; setDfUploads(m); };
  const dfStateOf = (f) => dfUploadsRef.current.get(f) || null;
  // The list as last set, for the handlers that run between renders.
  const dfFilesRef = useRef(dfFiles);
  dfFilesRef.current = dfFiles;
  const putDfFiles = (next) => { dfFilesRef.current = next; setDfFiles(next); };
  const [dfRetrying, setDfRetrying] = useState(false);
  // A result of uploadEach as the file's state. A failed record step keeps the
  // stored path, so a retry records that file instead of storing it again.
  const dfStateFromResult = (r) => r.ok ? { state: 'uploaded', entry: r.entry } : { state: 'failed', kind: r.kind, refusal: r.refusal, ...(r.path ? { path: r.path } : {}) };
  const dfFailureStatus = (files, states) => {
    const list = Array.from(files || []);
    const failed = list.filter(f => (states.get(f) || {}).state === 'failed').map(f => ({ name: f.name, kind: states.get(f).kind, refusal: states.get(f).refusal }));
    return uploadFailureMessage(failed, list.filter(f => (states.get(f) || {}).state === 'uploaded').length);
  };
  // The picker used to replace the whole selection, so a client who chose five documents,
  // then reopened it to add a sixth, was left holding only the sixth. Every one of them
  // read that as the platform deleting their uploads. Add, never replace, and de-duplicate
  // on name and size so choosing the same file twice is harmless.
  const addDfFiles = (incoming) => {
    const { ok: list, refused } = splitUploads(Array.from(incoming || []).filter(cidUsefulFile));
    setDfRefused(refused);
    if (!list.length) { setDfDropNote(refused.length ? '' : 'Nothing there we could read. We accept PDFs, photos, Word (.docx), Excel (.xlsx) and text files.'); return; }
    notePicked(list);
    // Owner decision 1: a new copy of a file that did not upload takes its place
    // (the client is told to save a new copy and choose it). Matched on the name
    // and the size it had when picked, or the name alone (failedReplacedBy): the
    // browser can read an unreadable file's size as 0. Computed outside the state
    // updater, so nothing runs twice.
    const key = (f) => `${f.name}|${f.size}`;
    const prev0 = dfFilesRef.current || [];
    const replaced = failedReplacedBy(prev0, list, dfStateOf);
    const prev = prev0.filter(f => !replaced.includes(f));
    const seen = new Set(prev.map(key));
    const added = list.filter(f => !seen.has(key(f)));
    const dupes = list.length - added.length;
    const next = [...prev, ...added];
    putDfFiles(next);
    setDfDropNote(
      added.length
        ? `Added ${added.length} file${added.length === 1 ? '' : 's'}${dupes ? `. ${dupes} was already in the list.` : '.'}`
        : 'Those are already in the list.'
    );
    if (replaced.length) {
      const m = new Map(dfUploadsRef.current); replaced.forEach(f => m.delete(f)); putDfUploads(m);
      setDfStatus(dfFailureStatus(next, m));
      // Owner decision 2: with no failed file left, the new copy is uploaded and
      // reading continues by itself (the client already pressed Submit).
      if (!next.some(f => (m.get(f) || {}).state === 'failed') && dfConsent && !dfBusy) runDocumentsFirst({ files: next, auto: true });
    }
  };
  // Review fix S3 (return visit): the uploads this case already recorded for the
  // intake come back as "Uploaded" entries, read from the server's record, so a
  // client who left with a file that did not upload never sends the others again.
  const loadIntakeUploads = async (sid) => {
    try {
      const r = await fetch('/api/save-document', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ action: 'intake-uploads', sessionId: sid, token: tokenRef.current }) });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) { try { captureError(new Error('Documents first: the uploads already recorded could not be read (status ' + r.status + ')'), { extra: { kind: 'documents_first_return' } }); } catch (_) {} return; }
      const entries = recordedEntries(j.uploads);
      const have = new Set((dfFilesRef.current || []).map(f => `${f.name}|${f.size}`));
      const fresh = entries.filter(e => !have.has(`${e.file.name}|${e.file.size}`));
      if (!fresh.length) return;
      const m = new Map(dfUploadsRef.current); fresh.forEach(e => m.set(e.file, e.state)); putDfUploads(m);
      putDfFiles([...fresh.map(e => e.file), ...(dfFilesRef.current || [])]);
      setDfDropNote(`${fresh.length} document${fresh.length === 1 ? '' : 's'} you uploaded before ${fresh.length === 1 ? 'is' : 'are'} already here and will not be sent again.`);
      // Owner, 03/11/2026: the intake was never submitted, so the last upload did not finish.
      setDfStatus(DF_RETURN_LINE);
    } catch (e) { try { captureError(e instanceof Error ? e : new Error(String(e)), { extra: { kind: 'documents_first_return' } }); } catch (_) {} }
  };
  const [dfBusy, setDfBusy] = useState(false);
  const [dfStatus, setDfStatus] = useState("");
  const [dfDone, setDfDone] = useState(false);
  const [dfBoard, setDfBoard] = useState(null);
  const [caseActivity, setCaseActivity] = useState([]);
  const [feedbackDismissed, setFeedbackDismissed] = useState(false);
  // Live analysis progress: the engine's real checkpoints, rendered as a strip
  // while the client waits. Only ever shows what genuinely ran.
  const [analysisProgress, setAnalysisProgress] = useState([]);
  const [dfContact, setDfContact] = useState({ name: "", email: "", company: "" });
  const [dfStep, setDfStep] = useState("consent"); // consent gate FIRST — no data entry before acceptance (UK GDPR)
  const [dfConsentAt, setDfConsentAt] = useState(null); // ISO timestamp of the moment the client accepted
  const [inviteExpired, setInviteExpired] = useState(false);
  // ── OTP-1 (v1_462): email-code gate. When load-invite answers 401 with
  // otpRequired, the portal shows a verification screen before anything else.
  // A verified browser holds an HttpOnly cookie pass, so every existing
  // fetch() in this file carries it automatically with no call-site changes.
  const [otpGate, setOtpGate] = useState(null); // { maskedEmail } | null
  const [otpCode, setOtpCode] = useState("");
  const [otpError, setOtpError] = useState(null);
  const [otpBusy, setOtpBusy] = useState(false);
  const [otpResendIn, setOtpResendIn] = useState(0);
  const [otpRetry, setOtpRetry] = useState(0); // bumping re-runs the invite loader
  const otpAutoSentRef = useRef(false);
  useEffect(() => {
    if (otpResendIn <= 0) return;
    const t = setTimeout(() => setOtpResendIn(s => s - 1), 1000);
    return () => clearTimeout(t);
  }, [otpResendIn]);
  const otpRequestCode = useCallback(async () => {
    const t = new URLSearchParams(window.location.search).get("token");
    if (!t) return;
    setOtpError(null);
    try {
      const r = await fetch("/api/otp-request", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ scope: "client", token: t }) });
      const d = await r.json().catch(() => ({}));
      if (r.ok && d.sent) {
        setOtpGate(g => ({ ...(g || {}), maskedEmail: d.maskedEmail || (g && g.maskedEmail) }));
        setOtpResendIn(30);
      } else if (!r.ok) {
        setOtpError(d.error || "The code could not be sent. Please try again shortly.");
      }
    } catch (_) {
      setOtpError("The code could not be sent. Please check your connection and try again.");
    }
  }, []);
  const otpVerifyCode = useCallback(async () => {
    const t = new URLSearchParams(window.location.search).get("token");
    if (!t || otpBusy) return;
    setOtpBusy(true); setOtpError(null);
    try {
      const r = await fetch("/api/otp-verify", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ scope: "client", token: t, code: otpCode.trim() }) });
      const d = await r.json().catch(() => ({}));
      if (r.ok && d.ok) {
        setOtpGate(null); setOtpCode(""); setTokenChecked(false);
        otpAutoSentRef.current = false;
        setOtpRetry(n => n + 1); // re-run the loader; the pass cookie now opens the file
      } else {
        setOtpError(d.error || "That code did not verify. Please try again.");
        if (d.requestNew) setOtpCode("");
      }
    } catch (_) {
      setOtpError("Verification failed. Please check your connection and try again.");
    } finally {
      setOtpBusy(false);
    }
  }, [otpCode, otpBusy]);
  const [dfAnswers, setDfAnswers] = useState({});
  const [dfFirmChecklist, setDfFirmChecklist] = useState(null);
  // v1_833: the questions the firm asks every client (policy section 14).
  const [firmQuestionList, setFirmQuestionList] = useState([]);
  // v1_830: the firm's beneficial ownership threshold (at or above), for the
  // checklist line and the interview; 25 until the firm is read.
  const [ownershipThreshold, setOwnershipThreshold] = useState(25);
  const [dfConsent, setDfConsent] = useState(false);
  // Client-paid onboarding: the fee this case carries (set at invitation),
  // whether it has been accepted/paid, and the payee details from the firm's
  // configuration. Payment state only ever moves to 'paid' server-side via
  // the officer's confirmation — the client app reads it, never writes it.
  const [feeInfo, setFeeInfo] = useState(null); // { amount, currency, paymentStatus, acceptedAt, billing }
  const [dfListStatus, setDfListStatus] = useState("");
  const [branding, setBranding] = useState(null);
  // White-label identity (firms.branding) with safe fallbacks to CompliantID defaults
  const brandName = (branding && branding.displayName) || null;
  const brandLogo = (branding && branding.logoUrl) || null;
  const brandTagline = (branding && branding.tagline) || 'Client Onboarding';
  const supportEmail = (branding && branding.supportEmail) || 'compliance@compliantid.com';
  const assistantName = (branding && branding.assistantName) || 'Alex';
  const hidePoweredBy = !!(branding && branding.hidePoweredBy);
  // White-label privacy policy: a tenant (data controller) can set branding.privacyPolicyUrl on
  // their firm record so their own clients see the tenant's policy. Falls back to CompliantID's.
  const privacyPolicyUrl = (branding && branding.privacyPolicyUrl) || '/privacy.html'; // hosted in public/ — tenant firms override via firms.branding.privacyPolicyUrl
  const messagesEndRef = useRef(null);
  // v1_729: the chat transcript scroll container. Scrolling it directly (scrollTop
  // = scrollHeight) is deterministic where scrollIntoView on a nested flex/overflow
  // chain landed partway down, and it drives the jump-to-latest control.
  const chatScrollRef = useRef(null);
  const chatAtBottomRef = useRef(true);
  const [showJumpLatest, setShowJumpLatest] = useState(false);
  const CHAT_NEAR_BOTTOM_PX = 80;
  const isChatNearBottom = () => {
    const el = chatScrollRef.current;
    if (!el) return true;
    return el.scrollHeight - el.scrollTop - el.clientHeight <= CHAT_NEAR_BOTTOM_PX;
  };
  const scrollChatToBottom = (behavior = 'auto') => {
    const el = chatScrollRef.current;
    if (!el) return;
    try { el.scrollTo({ top: el.scrollHeight, behavior }); } catch (_) { el.scrollTop = el.scrollHeight; }
  };
  const onChatScroll = () => {
    const atBottom = isChatNearBottom();
    chatAtBottomRef.current = atBottom;
    setShowJumpLatest(prev => (prev === !atBottom ? prev : !atBottom));
  };
  const fileInputRef = useRef(null);
  const sessionIdRef = useRef(null);
  const tokenRef = useRef(null);

  // Post-submission status page state. statusData is the client-safe payload
  // from /api/client-read resource 'status'; fetched once the file is in the
  // submitted family and refreshed on demand.
  const [statusData, setStatusData] = useState(null);
  const [statusRefreshing, setStatusRefreshing] = useState(false);
  const [resendingItem, setResendingItem] = useState(null);
  // Live list of documents on the case file (session_documents), refreshed on
  // resume. This is Alex's source of truth for what has been received; the
  // interview-time uploadedDocs state only knows THIS visit's uploads.
  const [docsOnFile, setDocsOnFile] = useState([]);
  const [statusCollapsed, setStatusCollapsed] = useState(false);
  const loadDocsOnFile = useCallback(async (sid) => {
    try {
      const dr = await clientRead('documents', sid || sessionIdRef.current, tokenRef.current);
      const rows = (dr?.data || [])
        .filter(d => String(d.assessment || '').toLowerCase() !== 'superseded')
        .map(d => ({ id: d.id, name: d.document_name || d.file_name || 'Document', assessment: d.assessment || 'Received', suppliedVia: d.supplied_via || null, state: d.state || null }));
      // Owner 15/10/2026: the kind the server named for a firm supplied document whose reading worked (checklist marking).
      const providedAs = Object.fromEntries((dr?.data || []).map(d => [d.id, d.provided_as || null]));
      for (const r of rows) r.providedAs = providedAs[r.id] || null;
      setDocsOnFile(rows);
      return rows;
    } catch (_) { return []; }
  }, []);

  const fetchClientStatus = useCallback(async (sid) => {
    const sessionId = sid || sessionIdRef.current;
    const token = tokenRef.current;
    if (!sessionId || !token) return;
    setStatusRefreshing(true);
    try {
      const res = await clientRead('status', sessionId, token);
      if (res?.data) {
        setStatusData(res.data);
        // v1_730: keep Alex's context in step with the checklist. A checklist
        // (panel) answer updates statusData here but NOT collectedData.requestItems,
        // which is what the chat system prompt reads — so without this mirror, an
        // item the client actioned on the panel stayed "open" in Alex's context and
        // was re-asked next turn. Mirror the item states by id into
        // collectedData.requestItems. Covers every panel action (answer, promise,
        // delegate, not-applicable) and both onboarding modes.
        const sri = Array.isArray(res.data.requestItems) ? res.data.requestItems : null;
        if (sri && sri.length) setCollectedData(prev => {
          if (!prev || !Array.isArray(prev.requestItems) || !prev.requestItems.length) return prev;
          const byId = new Map(sri.map(i => [i.id, i]));
          let changed = false;
          const next = prev.requestItems.map(x => {
            const u = byId.get(x.id);
            if (u && (u.state !== x.state || (u.dueAt || null) !== (x.dueAt || null))) {
              changed = true;
              return { ...x, state: u.state, dueAt: u.dueAt != null ? u.dueAt : x.dueAt, note: u.note || x.note, ...(u.owner ? { owner: { kind: 'delegate', name: u.owner.name } } : {}) };
            }
            return x;
          });
          return changed ? { ...prev, requestItems: next } : prev;
        });
        console.log('[STATUS] tracker loaded:', res.data.status, '| people:', (res.data.people || []).length, '| forms:', (res.data.forms || []).length);
      }
      else console.error('[STATUS] tracker fetch returned no data:', JSON.stringify(res || null).slice(0, 200));
      // Client-safe activity feed: what the platform has done on the case,
      // whitelist-filtered server-side. Non-fatal in every direction.
      try { const act = await clientRead('activity', sessionId, token); if (Array.isArray(act?.data)) setCaseActivity(act.data); } catch (_) {}
      loadDocsOnFile(sessionId);
    } catch (e) { console.error('[STATUS] tracker fetch failed:', e?.message); }
    setStatusRefreshing(false);
  }, []);

  const handleClientResend = useCallback(async (kind, id) => {
    const key = `${kind}:${id}`;
    setResendingItem(key);
    try {
      const res = await fetch('/api/client-resend', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sessionId: sessionIdRef.current, token: tokenRef.current, kind, id })
      });
      const body = await res.json().catch(() => ({}));
      if (res.ok && body.sentAt) {
        setStatusData(prev => prev ? { ...prev, resends: { ...(prev.resends || {}), [key]: body.sentAt } } : prev);
      } else if (res.status === 429 && body.lastSentAt) {
        setStatusData(prev => prev ? { ...prev, resends: { ...(prev.resends || {}), [key]: body.lastSentAt } } : prev);
      } else {
        alert('The reminder could not be sent right now. Please try again shortly or contact ' + supportEmail + '.');
      }
    } catch (e) {
      alert('The reminder could not be sent right now. Please try again shortly or contact ' + supportEmail + '.');
    }
    setResendingItem(null);
  }, [supportEmail]);

  // Load the status panel whenever the file enters (or is resumed in) the
  // submitted family. SOW interviews have no status page.
  useEffect(() => {
    if (submitted && !isSowInterview && sessionIdRef.current && tokenRef.current) fetchClientStatus();
  }, [submitted, isSowInterview, fetchClientStatus]);

  // Shared return-visit opening (v1_725). ONE behaviour and ONE guard for BOTH
  // onboarding modes (standard and documents-first). On a return with an open
  // MLRO round it posts the fixed opening line at most once per request round
  // (guard: mlroQuestionsDeliveredAt >= mlroRequestSentAt), then waits; if the
  // round was already greeted it silently restores the transcript. It never
  // regenerates or walks the question list — the questions stay in
  // collectedData so the chat handler answers item-specifics from the real open
  // items. Returns the delivered timestamp when it greeted (null when it stood
  // down) so the caller folds it into its single collected_data write and
  // nothing clobbers it back. Replaces two paths that had drifted apart: the
  // standard inline greeting (guarded) and the documents-first
  // enterDocumentsFirstChat(mode 'mlro_questions') greeting (unguarded — it
  // re-greeted on every return and reset the delivered stamp, stacking five
  // greetings on CompliantID f8d452f3 in one day, 21/09/26).
  const postReturnGreetingOnce = async ({ sessionId, restored, cd, name }) => {
    const _sentAt = cd && cd.mlroRequestSentAt ? new Date(cd.mlroRequestSentAt).getTime() : 0;
    // The guard reads its OWN stamp. mlroQuestionsDeliveredAt is the delivery
    // record, and api/load-invite stamps it server side on the first open of a
    // round, before this runs; guarding on it read every round as already
    // greeted, so the greeting never appeared (found by the end to end walk).
    const _greetedAt = cd && cd.returnGreetedAt ? new Date(cd.returnGreetedAt).getTime() : 0;
    const alreadyGreeted = _sentAt > 0 && _greetedAt >= _sentAt;
    if (alreadyGreeted) { visitStartRef.current = (restored || []).length; setMessages(restored || []); return null; }
    const _fn = (name || '').trim().split(/\s+/)[0] || name || 'there';
    const resumeMsg = `Welcome back, ${_fn}. The items we still need are listed in your checklist. You can answer each one or upload it there directly. If anything is unclear, or you would rather talk it through, tell me here and I will help.`;
    setMessages([...(restored || []), { role: 'assistant', content: resumeMsg }]);
    try { await fetch('/api/save-message', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ sessionId, role: 'assistant', content: resumeMsg, token: tokenRef.current }) }); } catch (_) {}
    return new Date().toISOString();
  };
  const dfAutoPendingRef = useRef(false);
  const dfAutoDoneRef = useRef(false);
  // v1_894: Alex has replied at least once with the documents file in hand. A
  // first SUBMIT is accepted only after that (src/shared/df-submit-gate.js).
  const dfSpokenWithFileRef = useRef(false);
  const dfFinalSeenRef = useRef(false); // v1_617: the post-checkpoint poll has absorbed the engine's final write
  const dfRunRef = useRef(null); // v1_804: the engine run this page started ({ id, since }), followed by its runId
  // v1_805: this page drives the engine: a stage the strip shows as pending is
  // advanced from here (no engine call starts another; Vercel refuses that).
  const dfDriverRef = useRef(null);
  const dfDrive = (ap) => {
    if (!dfDriverRef.current) dfDriverRef.current = makeRunDriver((p) => fetch('/api/df-process', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ sessionId: sessionIdRef.current, advance: true, runId: p.runId, token: tokenRef.current }) }));
    try { return dfDriverRef.current(ap); } catch (_) { return null; }
  };
  const dfAckPendingRef = useRef(false); // board landed while Alex's question was awaiting an answer
  // Once an interview leaves brief scope it never returns to it (v1_387). A
  // signal seen once has been seen; a later quiet turn does not unsee it.
  const interviewEverFullRef = useRef(false);

  // ── Database functions ──────────────────────────────────────────

  // v1_826: the portal never creates a case with the public key. Every case
  // is created by an invitation (a server route), and the database refuses a
  // case written by an anonymous caller. This legacy path only ran for a
  // visitor with no invitation link, and that insert already had no policy
  // allowing it; it now makes no call at all.
  const createSession = async (name, email) => {
    try {
      console.warn('createSession: no invitation link, so no case is created (cases are created by an invitation)');
      return null;
    } catch (e) {
      console.error('createSession error:', e);
    }
  };

  // Elapsed seconds while a document is in flight. Shown so the client can see time passing and
  // knows the page is alive, which is the whole point of the indicator.
  useEffect(() => {
    if (!docProgress) { setProgressElapsed(0); return; }
    const t = setInterval(() => setProgressElapsed(Math.floor((Date.now() - docProgress.startedAt) / 1000)), 1000);
    return () => clearInterval(t);
  }, [docProgress]);

  const updateSession = useCallback(async (updates) => {
    if (!sessionIdRef.current) return;
    try {
      const res = await fetch("/api/update-session", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ sessionId: sessionIdRef.current, updates, token: tokenRef.current })
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        console.error('updateSession API error:', res.status, err);
        return false;
      }
      return true;
    } catch (e) {
      console.error('updateSession error:', e);
      return false;
    }
  }, []);

  const saveMessage = useCallback(async (role, content) => {
    if (!sessionIdRef.current) return;
    try {
      await fetch("/api/save-message", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        // v1_923: Alex's words are stored without dashes too (the officer reads the
        // stored transcript); the chat already shows them through plainText.
        body: JSON.stringify({ sessionId: sessionIdRef.current, role, content: role === 'assistant' ? plainText(content) : content, token: tokenRef.current })
      });
    } catch (e) {
      console.error('saveMessage error:', e);
    }
  }, []);

  const saveDocument = useCallback(async (docName, docType, assessment, analysis, fileSize, fileBlob = null, mimeType = null, knownFilePath = null) => {
    if (!sessionIdRef.current) return { ok: false, error: 'no session' };
    try {
      // Save document record via server-side API
      // v1_900: the result is returned, never ignored: a record that did not save
      // is a file that did not reach the case, and the upload says so.
      const res = await fetch("/api/save-document", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          sessionId: sessionIdRef.current,
          token: tokenRef.current,
          document: {
            document_name: docName,
            document_type: docType,
            assessment,
            analysis,
            file_size: fileSize,
            file_url: null,
            // The upload now happens BEFORE analysis, so the path is usually known by the time the
            // row is written and no second update round trip is needed.
            file_path: knownFilePath || null,
            mime_type: mimeType
          }
        })
      });

      // Phase 2: no browser upload here any more. When the signed upload failed, the row is
      // recorded without a file (file_path 'pending') and the officer sees it has no stored file.
      void fileBlob;
      const body = await res.json().catch(() => ({}));
      return res.ok ? { ok: true, id: body.id || null } : { ok: false, error: body.error || ('status ' + res.status), refused: !!body.refused };
    } catch (e) {
      console.error('saveDocument error:', e);
      return { ok: false, error: (e && e.message) || 'network error' };
    }
  }, []);

  // First population is a RETURNING client's whole history landing in one
  // update: jump instantly to the latest message so they meet the questions
  // waiting for them, not the top of a three-day transcript. Every change
  // after that scrolls as messages arrive live, but only when the client is
  // already at the bottom, so it never yanks them away from reading earlier
  // messages (the jump-to-latest control brings them back on demand).
  const firstScrollDoneRef = useRef(false);
  useEffect(() => {
    if (!messages.length) return;
    if (!firstScrollDoneRef.current) {
      firstScrollDoneRef.current = true;
      // v1_729: scroll the transcript CONTAINER directly (scrollTop = scrollHeight)
      // rather than messagesEndRef.scrollIntoView — the latter landed partway down
      // on the return visit inside the nested flex/overflow chain. Re-assert bottom
      // every frame for ~2s so it holds once late layout (markdown, fonts, the
      // panel column) settles, then stop; and stop the instant the client scrolls,
      // so it never fights the reader (DEF-21).
      let cancelled = false;
      const stop = () => { cancelled = true; };
      window.addEventListener('wheel', stop, { once: true, passive: true });
      window.addEventListener('touchmove', stop, { once: true, passive: true });
      const start = (typeof performance !== 'undefined' ? performance.now() : Date.now());
      let raf = 0;
      const tick = () => {
        if (cancelled) return;
        scrollChatToBottom('auto');
        const now = (typeof performance !== 'undefined' ? performance.now() : Date.now());
        if (now - start < 2000) raf = requestAnimationFrame(tick);
        else { chatAtBottomRef.current = true; setShowJumpLatest(false); }
      };
      raf = requestAnimationFrame(tick);
      return () => { cancelled = true; if (raf) cancelAnimationFrame(raf); window.removeEventListener('wheel', stop); window.removeEventListener('touchmove', stop); };
    }
    // Later message changes: only follow the conversation if the client is at the
    // bottom; if they have scrolled up, leave them and show jump-to-latest.
    if (chatAtBottomRef.current) scrollChatToBottom('smooth');
    else setShowJumpLatest(true);
  }, [messages]);

  // Check for invite token on load — handles both new and returning clients
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get("token");
    if (!token) { setTokenChecked(true); return; }

    const loadInvite = async () => {
      try {
        console.log(`[CompliantID] Loading invite token: ${token.slice(0, 8)}...`);
        
        // Use server-side invite lookup to bypass RLS issues
        const inviteResponse = await fetch(`/api/load-invite?token=${encodeURIComponent(token)}`);
        
        if (!inviteResponse.ok) {
          const errorBody = await inviteResponse.text().catch(() => '(no response body)');
          console.error(`[CompliantID] Invite API failed: ${inviteResponse.status} ${inviteResponse.statusText}`, errorBody);
          
          // 404 = genuine "invitation only" case
          if (inviteResponse.status === 404) {
            console.log('[CompliantID] Invite not found - showing invitation only screen');
            setTokenChecked(true); 
            setTokenValid(false); 
            return;
          }

          // 401 = OTP-1 gate: the case requires an emailed verification code
          // before any of it leaves the server. Show the code screen, and send
          // the first code automatically so the legitimate holder's inbox is
          // already waiting for them.
          if (inviteResponse.status === 401) {
            let gateBody = {};
            try { gateBody = JSON.parse(errorBody); } catch (_) {}
            if (gateBody.otpRequired) {
              if (gateBody.branding) setBranding(gateBody.branding);
              setOtpGate({ maskedEmail: gateBody.maskedEmail || null });
              if (!otpAutoSentRef.current) {
                otpAutoSentRef.current = true;
                otpRequestCode();
              }
              setTokenChecked(true);
              setTokenValid(false);
              return;
            }
          }

          // 410 = the invite link has expired without ever being opened
          if (inviteResponse.status === 410) {
            console.log('[CompliantID] Invite expired - showing expired screen');
            setInviteExpired(true);
            setTokenChecked(true);
            setTokenValid(false);
            return;
          }
          
          // Other errors = server/config issues  
          throw new Error(`Invite lookup failed: ${inviteResponse.status} ${inviteResponse.statusText}`);
        }

        const { invite, branding: firmBranding, firmName: inviteFirmName, clientBilling, ownershipThreshold: firmThreshold } = await inviteResponse.json();
        if (Number(firmThreshold) > 0 && Number(firmThreshold) <= 25) setOwnershipThreshold(Number(firmThreshold));
        setBranding(firmBranding || null);
        // v1_973: the firm the welcome names, read from THIS response. brandName is
        // React state from the render that started this load (null on a first load;
        // only an OTP retry re-ran it with the gate's branding), so a greeting built
        // from it named no firm at all for most clients.
        const welcomeFirm = (firmBranding && firmBranding.displayName) || inviteFirmName || null;
        // Client-paid onboarding: this case carries a fee. Held in state for the
        // service page (before consent) and the payment card (after submission).
        if (invite.fee_amount || invite.fee_mode === 'schedule') {
          setFeeInfo({
            mode: invite.fee_mode || 'fixed',
            schedule: invite.fee_schedule || null,
            category: invite.fee_category || null,
            amount: invite.fee_amount,
            currency: invite.fee_currency || (invite.fee_schedule && invite.fee_schedule.currency) || 'GBP',
            paymentStatus: invite.payment_status || (invite.fee_amount ? 'unpaid' : null),
            acceptedAt: invite.fee_terms_accepted_at || null,
            billing: clientBilling || null
          });
        }
        console.log(`[CompliantID] Invite loaded successfully for ${invite.contact_name} at ${invite.company_name}`);

        setTokenValid(true);
        const name = invite.contact_name;
        const email = invite.contact_email;
        const company = invite.company_name;
        const individualClient = invite.collected_data?.clientKind === 'individual';
        setIsIndividualClient(individualClient);

        // Handle SOW interview. In CompliantID the invite row IS the SOW session (created by
        // /api/sow-invite as an onboarding_sessions row with status 'sow_interview'), so detect
        // via the sow=1 link param, the session status, or the collected_data marker — not a
        // Sage-style invite_type column — and use invite.id directly rather than creating a
        // duplicate session row.
        // Documents-first onboarding — client uploads a document pack instead of doing
        // the Alex interview; documents are analysed, pre-screened and assembled into the
        // decision board server-side (/api/df-process), then land in the dashboard with the
        // same report shape as a conversational case. Detected via invite_type, mirroring SOW.
        if (invite.invite_type === 'documents_first' || invite.collected_data?.onboardingMode === 'documents_first') {
          tokenRef.current = token;
          setIsDocumentsFirst(true);
          setDfContact({ name, email, company });
          const dfSessionId = invite.id || invite.session_id;
          sessionIdRef.current = dfSessionId;
          try {
            const { data: dfSess } = await clientRead('session', dfSessionId, tokenRef.current, 'status, report, collected_data, firm_id');
            const cd = dfSess?.collected_data || {};
            // Invite with documents: the list of what the firm already holds is read
            // on every visit, the first one included (before, only once submitted).
            if (cd.prefiled === true) { setDfPrefiled(true); loadDocsOnFile(dfSessionId); }
            // Review fix S3 (return visit): before the intake is submitted, the uploads
            // the case already recorded show as uploaded and are not sent again.
            if (!Array.isArray(cd.docManifest)) loadIntakeUploads(dfSessionId);
            // v1_833: answers already given to the firm's questions show on the checklist.
            if (Array.isArray(cd.firmQuestions)) setCollectedData(prev => ({ ...prev, firmQuestions: cd.firmQuestions }));
            if (dfSess?.firm_id) {
              // Item d: the firm's prompt texts and features are read by the server's prompt
              // builder, never carried in the page's collected data.
              try { const fr = await clientRead('firm', dfSessionId, token); const firmData = fr?.data; setDfFirmChecklist(firmData?.documentChecklist || null); setFirmQuestionList(Array.isArray(firmData?.firmQuestions) ? firmData.firmQuestions : []); if (Number(firmData?.ownershipThreshold) > 0) setOwnershipThreshold(Number(firmData.ownershipThreshold)); } catch (_) {}
            }
            const base = { ...cd, contactName: cd.contactName || name, contactEmail: cd.contactEmail || email, companyName: company };
            // Status panel for documents-first files: any submitted-family
            // return (including a follow-up) shows the case tracker above chat.
            if (['completed', 'pending_info', 'approved', 'declined', 'pending_director_review', 'director_returned'].includes(dfSess?.status)) {
              fetchClientStatus(dfSessionId);
            }
            // MID-ROUND RE-ENTRY (v1_625). The working list is cleared the first
            // time the client enters the conversation, so a client who leaves
            // and returns found no pending questions and dropped out of the
            // round. Fall back to the durable record of the requested questions
            // while the round is open (sent after the last follow-up submission),
            // exactly as the standard flow does.
            const _pendingWork = cd.pendingMlroQuestions || [];
            const _sentAtDf = cd.mlroRequestSentAt ? new Date(cd.mlroRequestSentAt).getTime() : 0;
            const _doneAtDf = cd.mlroFollowupSubmittedAt ? new Date(cd.mlroFollowupSubmittedAt).getTime() : 0;
            const _roundOpen = _sentAtDf > 0 && _sentAtDf >= _doneAtDf && dfSess?.status !== 'approved' && dfSess?.status !== 'declined';
            const pending = _pendingWork.length > 0 ? _pendingWork : (_roundOpen ? (cd.mlroRequestedQuestions || []) : []);
            if (pending.length > 0) {
              // Documents-first return with an open MLRO round. v1_725: use the
              // SAME shared opening as the standard flow (postReturnGreetingOnce)
              // instead of enterDocumentsFirstChat(mode 'mlro_questions'), which
              // had no greeted-once guard and regenerated a fresh walk-the-list
              // greeting on every return while clobbering mlroQuestionsDeliveredAt
              // back to its stale value (five stacked greetings on CompliantID
              // f8d452f3 in one day, 21/09/26). Set up the board + follow-up
              // context here with NO LLM greeting, restore the transcript, then
              // post the fixed line at most once. pendingMlroQuestions stays
              // populated so the chat handler answers item-specifics from the
              // real questions, and the delivered stamp is folded into the single
              // collected_data write so nothing resets it.
              const activity = DF_ACTIVITY_QUESTIONS.filter(a => !pending.some(p => (p || '').trim() === a.trim()));
              // Item d: the page holds only the board's company name; the server reconciles
              // the points against the stored board when it builds Alex's prompt (v1_641).
              const boardRep = dfSess?.report || null;
              const dfQuestions = [...pending, ...activity];
              let restored = [];
              try { const { data: msgs } = await clientRead('messages', dfSessionId, tokenRef.current); restored = (msgs || []).filter(m => m.role === 'user' || m.role === 'assistant').map(m => ({ role: m.role, content: m.content })); } catch (_) {}
              const merged = { ...base, onboardingMode: 'documents_first', documentsFirstChat: true, boardReady: !!boardRep, documentsFirstQuestions: dfQuestions, pendingMlroQuestions: pending };
              setCollectedData(merged);
              setStage('mlro_followup');
              setIsReturningClient(true);
              setConsentGiven(true);
              setIsDocumentsFirst(false);
              const deliveredStamp = await postReturnGreetingOnce({ sessionId: dfSessionId, restored, cd, name });
              const toPersist = deliveredStamp ? { ...merged, mlroQuestionsDeliveredAt: deliveredStamp, returnGreetedAt: deliveredStamp } : merged;
              try { await fetch('/api/update-session', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ sessionId: dfSessionId, updates: { collected_data: toPersist }, token }) }); } catch (_) {}
              if (deliveredStamp) setCollectedData(prev => ({ ...prev, mlroQuestionsDeliveredAt: deliveredStamp, returnGreetedAt: deliveredStamp }));
              setTokenChecked(true);
              return;
            } else if (dfSess && dfSess.status === 'in_progress' && cd.documentsFirstChat) {
              // Resuming an in-progress documents-first conversation
              if (cd.boardReady && dfSess.report) {
                setDfBoard(dfSess.report);
                await enterDocumentsFirstChat(dfSess.report, cd.documentsFirstQuestions || [], base);
              } else {
                // Documents still being analysed (or processing stalled). Re-enter pending mode;
                // if processing looks dead (>15 min), re-fire it from the stored manifest.
                const startedAt = cd.dfProcessingStartedAt ? new Date(cd.dfProcessingStartedAt).getTime() : 0;
                // v1_802: a run of the engine is a chain of calls and can take
                // longer than fifteen minutes in all; never re-fire (a new run
                // supersedes the one in progress) while the strip reads running.
                let runStillGoing = false;
                try { const { data: apRow } = await clientRead('session', dfSessionId, tokenRef.current, 'analysis_progress'); runStillGoing = analysisRunState(apRow && apRow.analysis_progress).running; } catch (_) {}
                const stale = !runStillGoing && (!startedAt || (Date.now() - startedAt > 15 * 60 * 1000));
                if (stale && Array.isArray(cd.docManifest) && cd.docManifest.length) {
                  startDfRun(dfRunRef, { sessionId: dfSessionId, docs: cd.docManifest, context: cd.dfContext || {}, token: tokenRef.current });
                }
                await enterDocumentsFirstChatPending(cd.documentsFirstQuestions && cd.documentsFirstQuestions.length ? cd.documentsFirstQuestions : DF_ACTIVITY_QUESTIONS, base);
              }
              setTokenChecked(true);
              return;
            } else if (dfSess && (dfSess.status === 'completed' || dfSess.status === 'approved' || dfSess.status === 'declined' || dfSess.status === 'pending_director_review' || dfSess.status === 'director_returned')) {
              setDfDone(true);
            }
          } catch (e) { console.error('documents-first load error', e); }
          // Client-paid onboarding: the service page comes FIRST, before even the
          // data-protection notice, so the fee obligation is accepted before any
          // effort is invested. A returning client who already accepted goes
          // straight to consent as normal.
          if ((invite.fee_amount || invite.fee_mode === 'schedule') && !invite.fee_terms_accepted_at) setDfStep('fee');
          // New documents-first invite (no conversation yet) — fall through to the
          // intake/upload screen rendered by the isDocumentsFirst branch.
          setTokenChecked(true);
          return;
        }

        const sowParam = params.get('sow') === '1';
        const sowParentId = invite.collected_data?.parentSessionId || invite.parent_session_id || null;
        if (sowParam || invite.parent_session_id || invite.invite_type === 'sow_interview' || invite.status === 'sow_interview' || invite.collected_data?.inviteType === 'sow_interview') {
          setIsSowInterview(true);
          setSowParentSessionId(sowParentId);
          tokenRef.current = token;
          // The invite row IS the SOW session — use its id directly (no orphaned duplicate).
          let sowSessionId = invite.id || invite.session_id;
          if (!sowSessionId) {
            // v1_826: no case is created with the public key. The invite row is
            // the SOW session (load-invite returns the row by its token), so it
            // always carries an id and this branch does not run in practice.
            console.error('SOW interview: the invitation row has no id, so no session is created');
          }
          sessionIdRef.current = sowSessionId;
          // v1_898: the person's name and address come from the invitation row.
          // Without them the first chat message was stored as the person's name
          // (handleSend's welcome step), so the interview's own prompt read
          // "UBO NAME: <their first answer>" and collected_data.contactName held
          // chat text (Diment 151952c2: a LinkedIn link and a sentence).
          setCollectedData(prev => ({ ...prev, contactName: name || prev.contactName, contactEmail: email || prev.contactEmail, prefilled_companyName: prev.prefilled_companyName || company }));

          // Check if returning SOW client (already consented and started)
          const { data: sowSessionData } = await clientRead('session', sowSessionId, tokenRef.current, 'collected_data, status');
          const sowAlreadyConsented = sowSessionData?.collected_data?.consentConfirmed;
          const sowComplete = sowSessionData?.status === 'sow_complete';
          const sowReopenedFlag = sowSessionData?.collected_data?.sowReopened;

          // Treat as returning if consented, complete, OR reopened by MLRO
          if (sowAlreadyConsented || sowComplete || sowReopenedFlag) {
            setConsentGiven(true);
            setIsReturningClient(true);
            // Only lock if complete AND not reopened by MLRO
            if (sowComplete && !sowReopenedFlag) setSubmitted(true);
            // Ensure parentSessionId is saved
            if (sowParentId && !sowSessionData?.collected_data?.parentSessionId) {
              await fetch("/api/update-session", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ sessionId: sowSessionId, updates: {
                  collected_data: { ...(sowSessionData?.collected_data || {}), parentSessionId: sowParentId }
                }, token: tokenRef.current })
              });
            }
            // Restore previous messages
            const { data: sowMsgs } = await clientRead('messages', sowSessionId, tokenRef.current);
            const restored = (sowMsgs || []).map(m => ({ role: m.role, content: m.content }));
            if (sowReopenedFlag && !sowComplete) {
              // Reopened by MLRO — add a continuity greeting
              try {
                const response = await fetch("/api/chat", {
                  method: "POST",
                  headers: { "Content-Type": "application/json" },
                  // Item d: the system prompt is built on the server (the person's name and company from the case).
                  body: JSON.stringify({
                    purpose: "sow_return",
                    token: tokenRef.current,
                    sessionId: sessionIdRef.current || null,
                    turn: {},
                    messages: [...restored, { role: "user", content: "I have returned after receiving a follow-up request from the compliance team." }]
                  })
                });
                const data = await response.json();
                const resumeMsg = modelText(data) || `Welcome back ${name}. Our compliance team has a follow-up question for you. Please go ahead whenever you are ready.`;
                const replyIsJson = resumeMsg.trim().startsWith('{') || resumeMsg.includes('\`\`\`json');
                if (!replyIsJson) {
                  restored.push({ role: "assistant", content: resumeMsg });
                  await fetch("/api/save-message", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ sessionId: sowSessionId, role: "assistant", content: resumeMsg, token: tokenRef.current }) });
                }
              } catch(e) {
                restored.push({ role: "assistant", content: `Welcome back ${name}. Our compliance team has a follow-up question, please go ahead whenever you are ready.` });
              }
            }
            if (restored.length) visitStartRef.current = restored.length; setMessages(restored);
            setTokenChecked(true);
            return;
          }

          // New SOW client — show welcome with consent checkbox
          setConsentGiven(false);
          const sowWelcome = `Hello ${name},

Thank you for taking a few minutes for this. I'm Alex, a senior compliance specialist${welcomeFirm ? ' at ' + welcomeFirm : ''}.

I'm here because ${company} is setting up an account with us, and as a significant shareholder, our compliance process requires a short personal conversation with you about your background and the broad origins of your wealth. This is a standard requirement, every significant shareholder we work with goes through the same process.

A few things worth knowing before we start:

This is about building a clear picture so I can present your case in the strongest way to our compliance team. I'm on your side here.

There are no trick questions. I'll explain why I'm asking anything that might seem sensitive.

Everything you share is strictly confidential and handled in line with our Privacy Policy.

It takes around 10 minutes and you can take as much time as you need on each answer.

Please tick the box below to confirm you're happy with how we handle your information, then we can get started.`;
          setMessages([{ role: 'assistant', content: sowWelcome }]);
          await fetch("/api/save-message", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ sessionId: sowSessionId, role: "assistant", content: sowWelcome, token: tokenRef.current }) });
          setTokenChecked(true);
          return;
        }

        // The invite row IS the session — use invite.id directly as the session ID
        const sessionId = invite.id;
        let existingMessages = [];

        // Load any previous messages for returning clients. Read via /api/client-read,
        // which validates the invite token server-side, rather than querying Supabase
        // directly with the public anon key: onboarding_messages no longer carries an
        // anonymous read policy, so a direct query here would silently return nothing
        // and a returning client would lose their conversation history.
        try {
          const mRes = await fetch('/api/client-read', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ sessionId, token, resource: 'messages' })
          });
          const mJson = await mRes.json().catch(() => null);
          existingMessages = (mJson && Array.isArray(mJson.data)) ? mJson.data : [];
          if (!mRes.ok) console.error('[client-read] messages failed', mRes.status, mJson && mJson.error);
        } catch (e) {
          console.error('[client-read] messages threw', e?.message);
          existingMessages = [];
        }

        sessionIdRef.current = sessionId;
        tokenRef.current = token;

        // Mark session as in_progress (via server-side API to bypass RLS) —
        // but ONLY for a genuinely pre-submission session. Unconditionally
        // flipping the status here regressed completed/pending files back to
        // in_progress on every client re-visit: it hid the status tracker,
        // resurrected the stage sidebar, put decided-family cases back into
        // the reminder chase pool, and corrupted the analytics funnel.
        const SUBMITTED_FAMILY = ['completed', 'pending_info', 'approved', 'declined', 'pending_director_review', 'director_returned', 'pending_external', 'jaybee_approved', 'jaybee_declined'];
        if (!SUBMITTED_FAMILY.includes(invite.status)) {
          await fetch("/api/update-session", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ sessionId, updates: { status: "in_progress" } , token })
          });
        }

        setCollectedData({ contactName: name, contactEmail: email, prefilled_companyName: company, inviteToken: token });

        // Load pre-screen intelligence if it was run at invite time
        // Also load website_url from invite so Alex can trigger research if needed
        if (sessionId) {
          const { data: sessionData } = await clientRead('session', sessionId, tokenRef.current, 'collected_data, status, pre_screen_data, firm_id, assessed');
          // Self-heal prior corruption: earlier builds flipped submitted files
          // back to in_progress on every visit, and stale-base collected_data
          // overwrites could wipe the submittedAt stamp. So the "was this file
          // submitted" test keys off UNWIPEABLE evidence too: risk_tier is a
          // real column written at submission and untouched by jsonb writes.
          // v1_803: read as `assessed` (yes or no); the tier itself is never sent.
          const SUB_FAMILY = ['completed', 'pending_info', 'approved', 'declined', 'pending_director_review', 'director_returned', 'pending_external'];
          let sessStatus = sessionData?.status;
          const cd0 = sessionData?.collected_data || {};
          const looksSubmitted = SUB_FAMILY.includes(sessStatus) || !!cd0.submittedAt || !!cd0.mlroFollowupSubmittedAt || !!sessionData?.assessed;
          console.log('[STATUS] session:', sessStatus, '| submittedAt:', cd0.submittedAt || null, '| followupDoneAt:', cd0.mlroFollowupSubmittedAt || null, '| assessed:', !!sessionData?.assessed, '| looksSubmitted:', looksSubmitted);
          if (looksSubmitted && !SUB_FAMILY.includes(sessStatus)) {
            const healed = (cd0.pendingMlroQuestions?.length) ? 'pending_info' : 'completed';
            console.log('[STATUS] healing status', sessStatus, '->', healed);
            try { await fetch("/api/update-session", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ sessionId, updates: { status: healed }, token }) }); } catch (_) {}
            sessStatus = healed;
          }
          if (sessionData?.collected_data?.consentConfirmed) {
            setConsentGiven(true);
          }
          // Load pending MLRO questions if client is returning after info request
          if (sessionData?.collected_data?.pendingMlroQuestions?.length > 0) {
            setCollectedData(prev => ({ ...prev, pendingMlroQuestions: sessionData.collected_data.pendingMlroQuestions }));
          }
          // Load firm system prompt override if configured
          if (sessionData?.firm_id) {
            try {
              const _fr = await clientRead('firm', sessionId, tokenRef.current);
              const firmData = _fr?.data;
              // Item d: the firm's prompt texts and features are read by the server's prompt
              // builder (api/_client-alex.js), never carried in the page's collected data.
              if (Number(firmData?.ownershipThreshold) > 0) setOwnershipThreshold(Number(firmData.ownershipThreshold));
              setFirmQuestionList(Array.isArray(firmData?.firmQuestions) ? firmData.firmQuestions : []);
              // The firm's features reach Alex through the server's prompt builder (the VQF
              // 902.5 customer profile fields are asked in the interview from them).
            } catch(e) { console.error('Firm prompt load error:', e); }
          }
          // Merge pre-screen intelligence into collected data so Alex has it from message one
          // pre_screen is stored inside collected_data.preScreen (jsonb) — no separate column needed
          const preScreenData = sessionData?.pre_screen_data || sessionData?.collected_data?.preScreen || invite.pre_screen_data;
          if (preScreenData) {
            setCollectedData(prev => ({ ...prev, preScreen: preScreenData }));
          }
          // Cut off fix (b): a pre-screen that did not complete is kept on the case
          // (the officer sees "Not checked") but never counts as research done.
          const preScreenUsable = preScreenData && !preScreenNotChecked(preScreenData) ? preScreenData : null;
          // Store website URL for Alex — but only if no pre-screen already (pre-screen means Alex already knows the site)
          if (invite.website_url && !preScreenUsable) {
            setCollectedData(prev => ({ ...prev, prefilled_websiteUrl: invite.website_url }));
          }
          // If pre-screen ran, store website URL as confirmed — Alex should not ask for it
          if (invite.website_url && preScreenUsable) {
            setCollectedData(prev => ({ ...prev, prefilled_websiteUrl: invite.website_url, websiteConfirmed: true }));
          }
          // Only fully disable chat for approved/declined — and even then, not when
          // the MLRO has sent follow-up questions the client must return to answer.
          const hasPendingMlroQuestions = (sessionData?.collected_data?.pendingMlroQuestions?.length > 0);
          if (!hasPendingMlroQuestions && (sessStatus === 'approved' || sessStatus === 'declined')) {
            setSubmitted(true);
          } else if (hasPendingMlroQuestions || sessStatus === 'pending_info') {
            // An outstanding request round REOPENS the conversation, whatever
            // any earlier path decided: several branches set submitted=true
            // (SOW completion, healing, the original SUBMIT) and none of them
            // knew a round had since gone out. A client who cannot type cannot
            // answer, and the round dies (seen on staging 02/08/26: input
            // locked on 'finalised' with 19 requested items on screen).
            setSubmitted(false);
          }
          // Returning client on a submitted file: load the status panel so they
          // can see review progress and chase outstanding third parties.
          if (looksSubmitted) {
            console.log('[STATUS] loading tracker for', sessionId);
            fetchClientStatus(sessionId);
          }
        }

        // Returning client. Consent is NO LONGER inferred from the existence of messages: that
        // reasoning held only for sessions created after the consent checkbox shipped, so any
        // older session, or any session where the checkbox failed to render, was silently treated
        // as consented with nothing in the database to evidence it. Consent now comes only from a
        // recorded consentConfirmed (set above at the collected_data check). If a returning client
        // has no record, the gate below asks them and records it properly.
        if (invite.session_id || existingMessages.length > 0) {
          setIsReturningClient(true);
        }

        if (existingMessages.length > 0) {
          // Returning client — restore conversation and collected data
          const restored = existingMessages.map(m => ({ role: m.role, content: m.content }));

          // Restore collected data from database.
          // NOTE: request only real columns. There is no 'stage' column on onboarding_sessions;
          // asking for it makes PostgREST reject the whole query and return an empty row, which is
          // what silently broke the MLRO follow-up. Stage is read from collected_data below.
          const _fullResp = await clientRead('session', sessionId, tokenRef.current, 'collected_data');
          const fullSession = _fullResp?.data;
          console.log('[FOLLOWUP] server read, _debug:', JSON.stringify(_fullResp?._debug || null), '| _error:', JSON.stringify(_fullResp?._error || null));
          if (fullSession?.collected_data) {
            setCollectedData(prev => ({ ...prev, ...fullSession.collected_data }));
          }

          // Restore stage from collected_data (no 'stage' column exists; falls back to 'company')
          const savedStage = fullSession?.collected_data?.stage;
          const validStages = individualClient
            ? ["person","summary","report","mlro_followup"]
            : ["welcome","company","ownership","regulatory","classification","aml","sow","sof","documents","summary","report","mlro_followup"];
          const restoredStage = validStages.includes(savedStage) ? savedStage : (individualClient ? "person" : "company");
          setStage(restoredStage);

          // Restore stage progress based on current stage
          const stageIdx = validStages.indexOf(restoredStage);
          const completedProgress = {};
          validStages.slice(0, stageIdx).forEach(s => { completedProgress[s] = true; });
          setStageProgress(completedProgress);

          try {
            // Drive the follow-up off whichever question list is populated. pendingMlroQuestions is
            // the working list; mlroRequestedQuestions is the durable record the dashboard also shows.
            // Falling back to the latter keeps the follow-up working even if an earlier build cleared
            // the working list.
            const cd = fullSession?.collected_data || {};
            const pendingQuestions = (cd.pendingMlroQuestions && cd.pendingMlroQuestions.length) ? cd.pendingMlroQuestions : (cd.mlroRequestedQuestions || []);
            const _sentAt = cd.mlroRequestSentAt ? new Date(cd.mlroRequestSentAt).getTime() : 0;
            const _doneAt = cd.mlroFollowupSubmittedAt ? new Date(cd.mlroFollowupSubmittedAt).getTime() : 0;
            // Active when there are questions and the latest request has not been completed yet.
            const hasPendingQuestions = pendingQuestions.length > 0 && _sentAt >= _doneAt;
            console.log('[FOLLOWUP] re-entry, session:', sessionId, '| pending:', (cd.pendingMlroQuestions || []).length, '| requested:', (cd.mlroRequestedQuestions || []).length, '| sentAt:', cd.mlroRequestSentAt || null, '| deliveredAt:', cd.mlroQuestionsDeliveredAt || null, '| doneAt:', cd.mlroFollowupSubmittedAt || null, '| active:', hasPendingQuestions);

            // If there are pending MLRO questions, start a fresh focused conversation
            // Don't append to the old submitted transcript — client needs a clean context
            if (hasPendingQuestions) {
              // Override stage to mlro_followup — prevents SUBMIT prompt firing on every message,
              // and keeps the chat interactive even if the session was previously submitted.
              setStage('mlro_followup');
              setSubmitted(false);
              // Make sure the working list is in memory so the message handler keeps the follow-up
              // context on every subsequent turn (the chat handler reads collectedData.pendingMlroQuestions).
              // This is what lets Alex answer an item-specific question from the ACTUAL open items
              // even though the opening greeting no longer walks the list.
              setCollectedData(prev => ({ ...prev, pendingMlroQuestions: pendingQuestions }));

              // v1_725: one shared guard + one shared line for both onboarding
              // modes — see postReturnGreetingOnce. Greets at most once per
              // request round, else silently restores; keeps pendingMlroQuestions
              // in context either way, and returns so a follow-up round never
              // falls through to the mid-interview "continue onboarding" greeting.
              const deliveredStamp = await postReturnGreetingOnce({ sessionId, restored, cd, name });
              if (deliveredStamp) {
                try { await fetch("/api/update-session", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ sessionId, updates: { collected_data: { ...(fullSession.collected_data || {}), mlroQuestionsDeliveredAt: deliveredStamp, returnGreetedAt: deliveredStamp } }, token: tokenRef.current }) }); } catch (_) {}
                setCollectedData(prev => ({ ...prev, mlroQuestionsDeliveredAt: deliveredStamp, returnGreetedAt: deliveredStamp, mlroRequestSentAt: cd.mlroRequestSentAt || prev.mlroRequestSentAt }));
              }
              return;
            }

            // A SUBMITTED file's return visit gets NO interview greeting: the
            // tracker communicates status, the transcript is restored as-is,
            // and the chat sits in follow-up mode so anything typed is handled
            // as an update to the file — never a resumed interview. This is
            // what previously produced "we're currently on the Company
            // section" on a completed case (stale stage) and one saved
            // greeting per visit stacking up in the transcript.
            // Derived HERE from fullSession: the looksSubmitted at line ~1903
            // lives in a different function and was never in scope, so this
            // branch has thrown (and been swallowed) since it shipped — every
            // submitted file's return visit fell through to the greeting path
            // (scope-check find, 11/08/26).
            const _cdz = (fullSession && fullSession.collected_data) || {};
            const _looksSubmitted = ['completed', 'pending_info', 'approved', 'declined', 'pending_director_review', 'director_returned', 'pending_external'].includes(fullSession && fullSession.status) || !!_cdz.submittedAt;
            if (_looksSubmitted) {
              console.log('[RESUME] submitted file, restoring transcript with no greeting');
              setStage('mlro_followup');
              visitStartRef.current = restored.length; setMessages(restored);
              return;
            }
            // Mid-interview return where Alex's last question is already the
            // last message on screen: restore silently, do not re-greet — a
            // second greeting under an unanswered question is pure noise.
            if (restored.length && restored[restored.length - 1].role === 'assistant') {
              console.log('[RESUME] last turn is Alex\u2019s question, restoring without a new greeting');
              visitStartRef.current = restored.length; setMessages(restored);
              return;
            }

            // Normal return — genuinely mid-interview and Alex owes a reply.
            // Alternate-merge the recent history: the API rejects consecutive
            // same-role messages, which real transcripts always contain.
            let lastFewMessages = alternateRoles(restored.slice(-8).map(m => ({ role: m.role, content: (m.content || '').slice(0, 800) })));
            if (lastFewMessages.length === 0) lastFewMessages = [{ role: 'user', content: "I'm back." }];
            // The model rejects conversations ending with an assistant message
            // ("does not support assistant message prefill") — end with a user turn.
            if (lastFewMessages[lastFewMessages.length - 1].role !== 'user') lastFewMessages.push({ role: 'user', content: "(I have just reopened the portal.)" });
            const response = await fetch("/api/chat", {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              // Item d: the greeting's prompt is built on the server from the stage and the
              // last thing each side said (the page sends those, never the prompt).
              body: JSON.stringify({
                purpose: "interview_resume",
                token: tokenRef.current,
                sessionId: sessionIdRef.current || null,
                turn: {
                  name, company, restoredStage,
                  restored: [[...restored].reverse().find(m => m.role === 'assistant'), [...restored].reverse().find(m => m.role === 'user')].filter(Boolean)
                },
                messages: lastFewMessages
              })
            });
            const resumeData = await response.json();
            if (!response.ok) console.error('[RESUME] greeting API', response.status, ':', JSON.stringify(resumeData?.error || resumeData).slice(0, 300));
            const resumeMsg = modelText(resumeData) || `Welcome back, ${name}. Let's pick up from where we left off.`;
            restored.push({ role: "assistant", content: resumeMsg });
            await fetch("/api/save-message", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ sessionId: sessionId, role: "assistant", content: resumeMsg, token: tokenRef.current }) });
            visitStartRef.current = restored.length; setMessages(restored);
          } catch(e) {
            const fallback = `Welcome back, ${name}. We were working through your ${restoredStage} information, let's continue from there.`;
            restored.push({ role: "assistant", content: fallback });
            visitStartRef.current = restored.length; setMessages(restored);
          }
        } else {
          // New client — fresh welcome
          // If we have pre-screen intelligence, use it — don't ask for website
          const hasPreScreen = (invite.pre_screen_data || invite.collected_data?.preScreen)?.companyName && !preScreenNotChecked(invite.pre_screen_data || invite.collected_data?.preScreen);
          const preScreen = invite.pre_screen_data || invite.collected_data?.preScreen;

          const personalMsg = hasPreScreen ? `${welcomeLine(welcomeFirm, name)}

I'm Alex, your dedicated onboarding specialist. I've already had a chance to review ${preScreen.companyName || company}'s public profile before you arrived.

My job is to work with you to understand your business properly and help present it in the best possible light for our compliance team. Think of this as a conversation with a senior compliance professional who is on your side.

You can return to this conversation at any time and I'll pick up exactly where we left off. If you'd prefer to discuss anything directly, our team is available at compliance@compliantid.com

Let's get started. Could you confirm the full legal name of the entity we'll be onboarding, is it ${preScreen.companyName || company}?` :

`${welcomeLine(welcomeFirm, name)}

I'm Alex, your dedicated onboarding specialist. I already have your details on file so we can get straight into it.

My job is to work with you, to understand your business properly and help present it in the best possible light for our compliance team. Think of this as a conversation with a senior compliance professional who is on your side.

You can return to this conversation at any time and I'll pick up exactly where we left off. If you'd prefer to discuss anything directly, our team is available at compliance@compliantid.com

To get started, could you share your company website? This helps me understand your business before we begin.`;

          // Individual onboarding (21/10/2026): a person is asked about themselves, never for a company website.
          const individualMsg = `${welcomeLine(welcomeFirm, name)}

I'm Alex, your onboarding specialist. Before your account can be opened, the law requires us to verify who you are and to understand where the money you will use comes from. I will ask you a few questions about yourself, one at a time.

You can return to this conversation at any time and I'll pick up exactly where we left off. If you'd prefer to discuss anything directly, our team is available at ${(firmBranding && firmBranding.supportEmail) || 'compliance@compliantid.com'}

To get started, could you confirm your full name exactly as it appears on your passport or identity card, and your date of birth?`;
          if (individualClient) { setMessages([{ role: "assistant", content: individualMsg }]); setStage("person"); }
          else {
          setMessages([{ role: "assistant", content: personalMsg }]);
          setStage("company");
          }
        }

        window.history.replaceState({}, "", "/");
      } catch(e) {
        console.error("[CompliantID] Token load error:", e);
        console.error("[CompliantID] Full error details:", e.message, e.stack);
        setTokenValid(false);
      } finally {
        setTokenChecked(true);
      }
    };
    loadInvite();
  }, [otpRetry]);

  const apiCall = async (payload, retries = 2, onData = null) => {
    // sessionId rides along for server-side usage logging; /api/chat strips it before Anthropic.
    // v1_806: /api/chat serves a client link only with its session, invite token
    // and code session; each payload names its purpose, and the server sets the
    // model and budget from it (the model and max_tokens below are ignored).
    payload = { ...payload, sessionId: payload.sessionId || sessionIdRef.current || null, token: tokenRef.current };
    for (let attempt = 1; attempt <= retries; attempt++) {
      try {
        const response = await fetch("/api/chat", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload)
        });
        if (!response.ok) {
          const err = await response.json().catch(() => ({}));
          throw new Error(err.error || "HTTP " + response.status);
        }
        let data = await response.json();
        if (typeof onData === 'function') { try { onData(data); } catch (_) {} }
        let text = modelText(data) || "";
        // Truncation continuation: if the reply hit the output ceiling, ask the
        // model to carry on from the exact cut (up to twice) and stitch the
        // parts into one message. Without this a long reply stalls mid-word
        // and the client has to type something just to pump out the rest.
        let continuations = 0;
        while (data.stop_reason === 'max_tokens' && continuations < 2 && text) {
          continuations++;
          const contRes = await fetch("/api/chat", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              ...payload,
              messages: [
                ...payload.messages,
                { role: "assistant", content: text },
                { role: "user", content: "(Your previous message was cut off mid-sentence. Continue EXACTLY from where it stopped, do not repeat anything, do not apologise, do not add a preamble. Just continue.)" }
              ]
            })
          });
          if (!contRes.ok) break;
          data = await contRes.json();
          const more = modelText(data) || "";
          if (!more) break;
          text += more;
        }
        return text;
      } catch (e) {
        const isLastAttempt = attempt === retries;
        const isRetryable = e.message === "Failed to fetch" || e.message.startsWith("HTTP 5");
        if (isLastAttempt || !isRetryable) throw e;
        // Brief pause before retry (cold start usually resolves immediately)
        await new Promise(resolve => setTimeout(resolve, 1500));
      }
    }
  };

  const fetchWebsiteData = async (url) => {
    try {
      const fullUrl = url.startsWith("http") ? url : "https://" + url;
      const text = await apiCall({
        purpose: "website_research",
        // Item d: the research prompt is the server's (WEBSITE_RESEARCH_SYSTEM).
        messages: [{ role: "user", content: "Research this company website thoroughly and extract all compliance-relevant information: " + fullUrl }],
        tools: [{ type: "web_search_20250305", name: "web_search" }]
      });
      return repairModelJson(text);
    } catch(e) {
      console.error("Website fetch error:", e);
      return null;
    }
  };

  // LAYER 1 — External verification: fires when client provides FRN, registration number or jurisdiction
  // Checks FCA Register, Companies House, GLEIF, OpenSanctions in real time
  const runExternalVerify = (verifyType, value, companyName) => {
    const currentSessionId = sessionIdRef.current;
    if (!currentSessionId || !value) return;
    setTimeout(async () => {
      try {
        const res = await fetch("/api/external-verify", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          // #119: the case and its token, so the server can check the caller.
          body: JSON.stringify({ verifyType, value, companyName, sessionId: currentSessionId, token: tokenRef.current })
        });
        const data = await res.json();
        if (data.success && data.result) {
          setCollectedData(prev => ({
            ...prev,
            externalVerifications: {
              ...(prev.externalVerifications || {}),
              [verifyType]: data.result
            }
          }));
        }
      } catch(e) { console.error("External verify error:", e.message); }
    }, 500);
  };
  const runPreScreen = (websiteUrl, companyName, emailDomain) => {
    const currentSessionId = sessionIdRef.current;
    if (!currentSessionId) return;
    setTimeout(async () => {
      try {
        const res = await fetch("/api/pre-screen", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            sessionId: currentSessionId,
            websiteUrl: websiteUrl || null,
            companyName: companyName || null,
            emailDomain: emailDomain || null
          })
        });
        const data = await res.json();
        if (data.success && data.intelligence) {
          // Store pre-screen results in collected data so Alex can use them
          setCollectedData(prev => ({ ...prev, preScreen: data.intelligence }));
          await updateSession({ collected_data: { preScreen: data.intelligence } });
        }
      } catch(e) { console.error("Pre-screen error:", e); }
    }, 500);
  };

  // ============================================================================
  // DOCUMENTS-FIRST ONBOARDING (additive, flag-gated). Only runs for invites
  // created with invite_type === 'documents_first'. The normal Alex interview
  // flow is completely untouched. Documents are analysed, pre-screened and
  // assembled into the decision board server-side in /api/df-process so the work
  // survives the client closing the browser tab. White-label: all firm-specific
  // rules come from the firm's prompt override, appended at merge time (on the server, item d).
  // ============================================================================
  // Item d: the documents first board merge (its guidance, prompt and people carry) runs
  // on the server: src/shared/df-board-merge.js through api/client-fold.js.

  const dfChat = async (payload) => {
    // sessionId rides along for server-side usage logging; /api/chat strips it before Anthropic.
    const { onStop, ...sendable } = payload || {};
    const resp = await fetch('/api/chat', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ ...sendable, token: tokenRef.current, sessionId: sessionIdRef.current || null }) });
    if (!resp.ok) { const e = await resp.json().catch(() => ({})); throw new Error(e.error || ('HTTP ' + resp.status)); }
    const data = await resp.json();
    // v1_783: a caller that must not accept a cut off answer reads the stop reason.
    if (payload && typeof payload.onStop === 'function') payload.onStop(data && data.stop_reason);
    const blocks = data.content || [];
    let lastTool = -1;
    for (let i = 0; i < blocks.length; i++) if (blocks[i] && blocks[i].type !== 'text') lastTool = i;
    const after = blocks.filter((b, i) => b && b.type === 'text' && i > lastTool).map(b => b.text);
    const all = blocks.filter(b => b && b.type === 'text').map(b => b.text);
    const text = (after.length ? after : all).join('\n').trim();
    return text || (blocks[0] && blocks[0].text) || '';
  };

  const DF_QUESTIONS = [
    { key: 'businessType', q: 'Which best describes your business?', options: [
      { label: 'Investment fund', value: 'REGULATED_FUND' },
      { label: 'Asset or investment manager', value: 'ASSET_MANAGER' },
      { label: 'Operating company / corporate treasury', value: 'CORPORATE_TREASURY' },
      { label: 'Broker or trading firm', value: 'BROKER' },
      { label: 'Crypto or digital asset business', value: 'VASP' },
      { label: 'Payments or money services', value: 'MSB' },
      { label: 'Online gaming', value: 'GAMING' },
      { label: 'Family office', value: 'FAMILY_OFFICE' },
      { label: 'Holding company or SPV', value: 'HOLDING_SPV' },
      { label: 'Something else', value: 'OTHER' },
    ] },
    { key: 'regulated', q: 'Is your entity licensed or regulated by a financial regulator?', options: [
      { label: 'Yes, we hold a licence', value: 'yes' },
      { label: 'No', value: 'no' },
      { label: 'Application in progress', value: 'in_progress' },
    ] },
    { key: 'ownership', q: 'How is your company owned?', options: [
      { label: 'Directly by individuals', value: 'direct' },
      { label: 'Through one or more companies or trusts', value: 'layered' },
      { label: 'Publicly listed on a stock exchange', value: 'listed' },
    ] },
    { key: 'crypto', q: 'Will this relationship involve crypto or digital assets?', options: [
      { label: 'Yes', value: 'yes' },
      { label: 'No', value: 'no' },
    ] },
  ];

  const dfBuildChecklist = (a, dc) => {
    dc = dc || {};
    const items = [];
    const add = (x) => { if (x && !items.includes(x)) items.push(x); };
    // Core "all clients" documents — firm override (dc.common) or default
    if (Array.isArray(dc.common) && dc.common.length) {
      dc.common.forEach(add);
    } else {
      add('Certificate of Incorporation');
      add('Register of Directors');
      add('Register of Shareholders / Members');
    }
    // Conditional items (always applied — driven by the client's own answers)
    if (a.ownership === 'layered') add('Group structure chart tracing ownership through to the ultimate individual owners');
    if (a.ownership === 'listed') add('Evidence of your stock exchange listing (e.g. ticker or regulator page)');
    if (a.ownership && a.ownership !== 'listed') add(`Names and details of anyone who ultimately owns or controls ${ownershipThreshold}% or more`);
    if (a.regulated === 'yes') add('Your regulatory licence or authorisation');
    if (a.regulated === 'in_progress') add('Evidence of your licence application and its current status');
    // Per entity type — firm override (dc.byType[type]) or default
    const firmType = (dc.byType && Array.isArray(dc.byType[a.businessType]) && dc.byType[a.businessType].length) ? dc.byType[a.businessType] : null;
    if (firmType) {
      firmType.forEach(add);
    } else {
      switch (a.businessType) {
        case 'REGULATED_FUND': add('Fund prospectus or PPM'); add('Latest audited accounts'); add('Administrator or depositary confirmation'); break;
        case 'ASSET_MANAGER': add('Regulatory licence'); add('AML / compliance policy'); break;
        case 'CORPORATE_TREASURY': add('Board resolution authorising this relationship'); add('Most recent financial statements'); break;
        case 'BROKER': add('Regulatory licence covering your products'); add('AML / compliance policy'); break;
        case 'VASP': add('VASP / crypto registration certificate'); add('AML / CTF policy'); break;
        case 'MSB': add('MSB / PI / EMI registration certificate'); add('AML / CTF policy'); break;
        case 'GAMING': add('Gaming licence'); add('AML / CTF policy'); add('Confirmation of player-fund segregation'); break;
        case 'FAMILY_OFFICE': add('Structure chart for the family office'); break;
        case 'HOLDING_SPV': add('A short note explaining the purpose of the holding / SPV structure'); break;
        default: break;
      }
    }
    add('A recent bank statement for your operating account');
    add('A short summary of the source of wealth and funds of the main owner(s)');
    return items;
  };

  const DF_ACTIVITY_QUESTIONS = [
    "What is the intended purpose of your relationship with us, what will the account or service be used for?",
    "What types of transactions do you expect, and roughly what annual volume and typical transaction size?",
    "Which currencies will you transact in?",
    "Which countries will funds typically come from (inbound), and which countries will they go to (outbound)?",
    "Who are your main customers or counterparties, what is the split between corporate and individual, and in which countries are they based?",
    "Source of wealth, how has the business built up its overall financial position over time: its main sources of revenue, and how the company's wealth and capital have been accumulated (for example trading profits built up over the years, investor or shareholder capital, a parent company, an asset sale, or retained earnings)?",
    "Source of funds, for the money that will actually flow through this relationship, where will those specific funds originate: which bank account(s) or sources will be used to fund the activity, and are those accounts held in the company's own name?",
  ];

  // Item d: what Alex is told it already knows (the board brief) is built on the server
  // (dfBoardBrief in src/shared/client-alex-prompt.js, from the stored board).

  // Upload one file to storage and record a pending document row via the
  // multi-tenant save-document API (which stamps firm_id server-side). The
  // file_path is written straight onto the row so /api/df-process matches it.
  // No analysis here — that runs server-side so it survives the tab closing.
  // Put a file in storage without depending on anonymous storage RLS.
  //
  // The direct browser upload is evaluated by a bucket policy that calls get_user_firm_id(), which
  // an anonymous onboarding client cannot satisfy, so it fails with "permission denied for function
  // get_user_firm_id" and no document reaches the file at all. The server mints a one-shot signed
  // upload URL instead, scoped to a path it chooses inside this session's folder, and the browser
  // uploads straight to storage with it. The bytes still never pass through a serverless function,
  // so large documents remain possible.
  //
  // Phase 2: there is no direct upload fallback any more. The storage policy that let a browser
  // write to the bucket is dropped, so a failure to mint or use the signed URL is the failure,
  // reported to Sentry and shown to the client by the caller.
  //
  // Owner decision 3 (02/11/2026): the file is read once in the browser before it is sent
  // (src/shared/upload-batch.js storeFile), so Sentry records whether the file could not be
  // opened from the computer or the network failed, with its size, and the error carries
  // that kind for the client's plain message.
  const uploadToStorage = useCallback(async (file) => {
    const sid = sessionIdRef.current;
    if (!sid) throw uploadFailure(UPLOAD_FAILURE.SIGN, 'No session');
    return storeFile(file, {
      sign: async (f) => {
        const r = await fetch('/api/save-document', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ action: 'signUpload', sessionId: sid, token: tokenRef.current, fileName: f.name })
        });
        const j = await r.json().catch(() => ({}));
        return (r.ok && j.success && j.path && j.token) ? { path: j.path, token: j.token } : { error: j.error || r.status };
      },
      put: (path, token, f) => supabase.storage
        .from('onboarding-documents')
        .uploadToSignedUrl(path, token, f, { contentType: f.type || 'application/octet-stream' }),
      // Reported explicitly: the fetch wrapper only watches /api/ routes, so a storage upload
      // failing never reached Sentry. That is exactly why a total upload outage produced no events.
      report: (e, failure, sizes) => captureError(new Error('Document storage upload failed: ' + e.message), {
        extra: { kind: 'storage', failure, ...sizes }
      }),
    });
  }, []);

  const dfUploadRaw = async (file) => {
    const sid = sessionIdRef.current;
    // Throws an upload failure carrying its kind (src/shared/upload-batch.js).
    // Review fix S1: when only the record step failed before, the stored path is
    // recorded again (save-document reuses the row for the same path): one
    // object and one row for one file, never a second upload.
    const prior = dfStateOf(file);
    const fp = (prior && prior.state === 'failed' && prior.kind === UPLOAD_FAILURE.RECORD && prior.path) ? prior.path : await uploadToStorage(file);
    // v1_871: the document engine reads only files the case has recorded, so a
    // failed record is a failed upload, shown to the client, never swallowed.
    // Owner decision 2: an orphaned upload (stored, not recorded) is a failed file
    // in the list and is reported to Sentry, never passed over.
    let saved = null, body = {};
    try {
      saved = await fetch('/api/save-document', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ sessionId: sid, token: tokenRef.current, document: { document_name: file.name, document_type: 'Pending review', file_size: file.size, file_url: null, file_path: fp, mime_type: file.type || null } }) });
      body = await saved.json().catch(() => ({}));
    } catch (e) {
      throw uploadFailure(UPLOAD_FAILURE.RECORD, 'Could not record ' + file.name + ': ' + e.message, { path: fp });
    }
    // A file the server refused from its content says why, in the shared wording.
    const recFail = recordFailure(file.name, { ok: saved.ok, status: saved.status, body }, fp);
    if (recFail) {
      if (recFail.uploadFailure === UPLOAD_FAILURE.RECORD) { try { captureError(new Error('Documents first: an uploaded file was not recorded (status ' + saved.status + (body && body.orphan ? ', orphaned upload' : '') + ')'), { extra: { kind: 'documents_first_upload', failure: UPLOAD_FAILURE.RECORD, status: saved.status, orphan: !!(body && body.orphan), fileSizeKb: Math.round((file.size || 0) / 1024), ext: fileExt(file) } }); } catch (_) {} }
      throw recFail;
    }
    // Upload fix batch, fault d: never labelled a PDF when the browser gave no type; the
    // server detects the type from the content and stores that.
    return { name: file.name, path: fp, mime: file.type || null, size: file.size };
  };

  const enterDocumentsFirstChatPending = async (activityQuestions, baseCollected) => {
    const cName = dfContact.company || (baseCollected && baseCollected.companyName) || 'your company';
    const firstName = ((dfContact.name || (baseCollected && baseCollected.contactName) || '').split(' ')[0]) || 'there';
    const qs = (activityQuestions && activityQuestions.length) ? activityQuestions : DF_ACTIVITY_QUESTIONS;

    // A returning client must see their conversation, never a restart. The
    // board-ready path gained this in v1_146; this is the same fix for the
    // still-processing phase. Only a genuinely first entry greets fresh.
    let restored = [];
    try {
      const { data: msgs } = await clientRead('messages', sessionIdRef.current, tokenRef.current);
      restored = (msgs || []).filter(m => m.role === 'user' || m.role === 'assistant').map(m => ({ role: m.role, content: m.content }));
    } catch (_) {}
    if (restored.length) {
      const mergedR = { ...(baseCollected || {}), onboardingMode: 'documents_first', documentsFirstChat: true, boardReady: false, documentsFirstQuestions: qs, contactName: dfContact.name || (baseCollected && baseCollected.contactName), contactEmail: dfContact.email || (baseCollected && baseCollected.contactEmail), pendingMlroQuestions: [] };
      setCollectedData(mergedR);
      setStage('mlro_followup');
      setIsReturningClient(true);
      setConsentGiven(true);
      setIsDocumentsFirst(false);
      visitStartRef.current = restored.length; setMessages(restored);
      try { await updateSession({ collected_data: mergedR }); } catch (_) {}
      return;
    }

    let greeting = `Hi ${firstName}, I'm ${assistantName}an AI compliance specialist, and I'll be preparing your file today. Thank you for the documents; I'm reading every one of them in full right now. While I do, I'd like to get a few things directly from you, everything you tell me here is something nobody has to chase you for later, and complete files get decided faster. Ask me anything along the way, in any language. To start: ${qs[0]}`;
    try {
      // Item d: the opening's prompt is built on the server; the page sends the names and the points.
      const resp = await fetch('/api/chat', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ purpose: 'greeting', token: tokenRef.current, sessionId: sessionIdRef.current || null, turn: { kind: 'df_pending', firstName, cName, questions: qs }, messages: [{ role: 'user', content: "I've just uploaded my documents." }] }) });
      const gd = await resp.json();
      const t = modelText(gd);
      if (t && t.trim()) greeting = t.trim();
    } catch (_) {}
    const merged = { ...(baseCollected || {}), onboardingMode: 'documents_first', documentsFirstChat: true, boardReady: false, documentsFirstQuestions: qs, contactName: dfContact.name || (baseCollected && baseCollected.contactName), contactEmail: dfContact.email || (baseCollected && baseCollected.contactEmail), pendingMlroQuestions: [] };
    setCollectedData(merged);
    setStage('mlro_followup');
    setIsReturningClient(true);
    setConsentGiven(true);
    setIsDocumentsFirst(false);
    setMessages([{ role: 'assistant', content: greeting }]);
    try { await saveMessage('assistant', greeting); } catch (_) {}
    try { await updateSession({ collected_data: merged }); } catch (_) {}
  };

  const enterDocumentsFirstChat = async (board, questions, baseCollected, mode = 'resume') => {
    // Item d: the board brief and the source of wealth reconcile (v1_641) are made on
    // the server from the stored board; the page holds only the board's company name.

    // A returning client must see their conversation, never a restart. Load
    // the saved transcript first; only a genuinely first entry greets fresh.
    let restored = [];
    try {
      const { data: msgs } = await clientRead('messages', sessionIdRef.current, tokenRef.current);
      restored = (msgs || []).filter(m => m.role === 'user' || m.role === 'assistant').map(m => ({ role: m.role, content: m.content }));
    } catch (_) {}

    const applyState = (merged) => {
      setCollectedData(merged);
      setStage('mlro_followup');
      setIsReturningClient(true);
      setConsentGiven(true);
      setIsDocumentsFirst(false);
    };

    // Plain resume with history: restore silently. If Alex's question is the
    // last message, it is simply waiting for its answer; if the client spoke
    // last, their next message gets Alex's reply with full board knowledge.
    if (mode === 'resume' && restored.length) {
      const mergedR = { ...(baseCollected || {}), onboardingMode: 'documents_first', documentsFirstChat: true, boardReady: true, documentsFirstQuestions: questions || [], contactName: dfContact.name || (baseCollected && baseCollected.contactName), contactEmail: dfContact.email || (baseCollected && baseCollected.contactEmail), pendingMlroQuestions: [] };
      applyState(mergedR);
      visitStartRef.current = restored.length; setMessages(restored);
      try { await updateSession({ collected_data: mergedR }); } catch (_) {}
      return;
    }
    const cName = (board && board.clientName) || dfContact.company || (baseCollected && baseCollected.companyName) || 'your company';
    const firstName = ((dfContact.name || (baseCollected && baseCollected.contactName) || '').split(' ')[0]) || 'there';
    let greeting = `Hi ${firstName}, thank you for sending those documents through, I've had a good look. There are just a few things I'd love to go through with you to complete the picture. To start: ${(questions && questions[0]) || 'could you tell me a little about how you intend to use the relationship with us?'}`;
    try {
      // Item d: the opening's prompt (with what the documents show) is built on the server.
      const resp = await fetch('/api/chat', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ purpose: 'greeting', token: tokenRef.current, sessionId: sessionIdRef.current || null, turn: { kind: 'df_board', firstName, cName, questions: questions || [] }, messages: [{ role: 'user', content: "I've just uploaded my documents." }] }) });
      const gd = await resp.json();
      const t = modelText(gd);
      if (t && t.trim()) greeting = t.trim();
    } catch (_) {}
    const merged = { ...(baseCollected || {}), onboardingMode: 'documents_first', documentsFirstChat: true, boardReady: true, documentsFirstQuestions: questions || [], contactName: dfContact.name || (baseCollected && baseCollected.contactName), contactEmail: dfContact.email || (baseCollected && baseCollected.contactEmail), pendingMlroQuestions: [] };
    applyState(merged);
    setMessages([...restored, { role: 'assistant', content: greeting }]);
    try { await saveMessage('assistant', greeting); } catch (_) {}
    try { await updateSession({ collected_data: merged }); } catch (_) {}
  };

  const finishDocumentsFirstChat = async (data) => {
    // Run-once latch is finishStartedRef, NOT submittedRef (v1_668). The SUBMIT
    // handler sets submittedRef true at acceptance to hard-stop the pre-submit
    // loop before this deferred finish runs, so guarding on it here made this
    // function early-return and never complete the case (Sage Capital AG, session
    // 65c8a17c, staging 18/09/26). Guard on the dedicated latch (and the durable
    // already-submitted signals) so the completion chain runs exactly once.
    if (!shouldRunFinish({ submitted, finishStarted: finishStartedRef.current, submittedAt: collectedData && collectedData.submittedAt })) return;
    finishStartedRef.current = true;
    submittedRef.current = true; // hard stop for the async pre-submit review loop (v1_653)
    setSubmitted(true);
    // COMPLETION IS A LATCH, WRITTEN BEFORE THE SLOW MERGE (v1_644). The board
    // merge below is a full Opus-5 call of tens of seconds. Alex's "your file
    // has been submitted" line is shown to the client the instant they type
    // SUBMIT, so a client who then closes the tab — entirely natural, they were
    // just told it is done — leaves this function mid-merge and the write that
    // marks the case completed, stamps submittedAt and fires the whole
    // post-submission chain never runs. The case then sits at in_progress with
    // submittedAt unset though the client was told it submitted, and the officer
    // never gets the automated run (Sage Capital AG, staging 15/09/26). So the
    // completion is persisted FIRST, awaited and independent of the merge; the
    // enrichment and the chain follow as best effort.
    const submittedAtStamp = (collectedData && collectedData.submittedAt) || new Date().toISOString();
    let completionSaved = false;
    try {
      // v1_861: analysisRequested asks the server to stamp analysisRequestedAt,
      // so the officer's case reads analysis in progress from this moment, not
      // from when the rebuild first writes its progress.
      completionSaved = (await updateSession({
        status: 'completed',
        collected_data: { ...collectedData, submittedAt: submittedAtStamp, analysisRequested: true },
        has_updates: true, last_update_type: 'documents_first_submitted', last_update_at: new Date().toISOString()
      })) === true;
      if (completionSaved) setCollectedData(prev => ({ ...prev, submittedAt: submittedAtStamp }));
    } catch (_) {}
    // 11/10/2026 (walk w73bbfaa0): the client is told the file has gone only now,
    // once the write that sends it has succeeded. If it failed, they are told it
    // has not gone, and SUBMIT works again.
    if (!completionSaved) {
      finishStartedRef.current = false;
      submittedRef.current = false;
      setSubmitted(false);
      setMessages(prev => [...prev, { role: "assistant", content: SUBMIT_NOT_SAVED_TEXT }]);
      saveMessage("assistant", SUBMIT_NOT_SAVED_TEXT);
      return;
    }
    {
      const firstName = String((data && data.contactName) || dfContact.name || (collectedData && collectedData.contactName) || '').trim().split(/\s+/)[0] || '';
      const done = submittedText({ firstName, firmName: brandName });
      setMessages(prev => [...prev, { role: "assistant", content: done }]);
      saveMessage("assistant", done);
    }
    // Item d: the page holds only the board's company name (dfBoard).
    const board = dfBoard;
    const cName = (data && data.companyName) || dfContact.company || (collectedData && collectedData.companyName);
    const cEmail = (data && data.contactEmail) || dfContact.email || (collectedData && collectedData.contactEmail);
    const cContact = (data && data.contactName) || dfContact.name || (collectedData && collectedData.contactName);
    // FIRE THE DURABLE SERVER-SIDE AUTOMATION BEFORE THE SLOW MERGE (v1_644).
    // df-process(force) is the authoritative rebuild (and dispatches auto-actions
    // from its tail); adversarial-review, auto-actions and notify complete the
    // post-submission automation. They ran only AFTER the tens-of-seconds
    // client-side merge below, so a tab closed on the confirmation lost them and
    // the officer never got the automated run. Dispatch them here, right after
    // the completion latch, so they survive the tab. notify reads the pre-merge
    // board (clientName/riskTier are already on it); the full merge lands via
    // df-process regardless.
    startDfRun(dfRunRef, { sessionId: sessionIdRef.current, force: true, token: tokenRef.current });
    // Cost change (12/10/2026): no direct adversarial review here. The forced run's
    // finishB runs it on the rebuilt board; walk w8971ad99 showed it running twice
    // per SUBMIT, the first time on the board from before the rebuild.
    try { setTimeout(() => { fetch('/api/auto-actions', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ sessionId: sessionIdRef.current, token: tokenRef.current }) }).then(() => fetchClientStatus()).catch(() => {}); }, 4000); } catch (_) {}
    // Item d: the tier and the recommendation are read from the stored report by notify
    // (the page no longer holds the board).
    try { fetch('/api/notify', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ clientName: cContact, companyName: (board && board.clientName) || cName, contactEmail: cEmail, sessionId: sessionIdRef.current, token: tokenRef.current }) }); } catch (_) {}
    try {
      let transcript = '';
      try {
        const { data: msgs } = await clientRead('messages', sessionIdRef.current, tokenRef.current);
        transcript = (msgs || []).filter(m => m.role === 'user' || m.role === 'assistant').map(m => `${m.role === 'user' ? 'Client' : 'Alex'}: ${m.content}`).join('\n\n');
      } catch (_) {}
      // Cost change (12/10/2026): no fold here. The forced df-process run started
      // above folds this same transcript into the board it rebuilds (and makes the
      // board provisional, with a case note, if that fold is cut off twice); a fold
      // from the page was always overwritten by it minutes later.
      // Completion (status, submittedAt) is already persisted above; this write
      // only ENRICHES. Never re-open a completed case.
      const upd = { status: 'completed', has_updates: true, last_update_type: 'documents_first_conversation', last_update_at: new Date().toISOString() };
      // Safety net: extract any wallet addresses from the conversation and persist them into
      // collected_data so the dashboard's Chainalysis screening box can surface them. The
      // rolling merge only writes the report, so without this a follow-up wallet may never
      // reach collected_data.walletAddresses.
      try {
        const walletRegex = /\b(bc1[a-zA-HJ-NP-Z0-9]{25,90}|[13][a-zA-HJ-NP-Z0-9]{25,34}|0x[a-fA-F0-9]{40}|T[a-zA-HJ-NP-Z0-9]{33})\b/g;
        const foundWallets = [...new Set((transcript || '').match(walletRegex) || [])];
        const existingWallets = (collectedData && collectedData.walletAddresses) || [];
        const mergedWallets = [...new Set([...existingWallets, ...foundWallets])];
        if (mergedWallets.length > existingWallets.length) {
          upd.collected_data = { ...collectedData, ...(upd.collected_data || {}), walletAddresses: mergedWallets };
          setCollectedData(prev => ({ ...prev, walletAddresses: mergedWallets }));
        }
      } catch (_) {}
      // The AI recommendation is NEVER written into the officer's decision column (v1_442).
      // Enrichment only: the completion latch and the server-side automation
      // (df-process force rebuild, auto-actions, notify) already fired above and
      // do not depend on this write, so a tab closed during the merge loses only
      // a stopgap board that df-process then supersedes.
      await updateSession(upd);
    } catch (e) { console.error('documents-first finish error', e); }
  };

  // COST CHANGE F (approved 12/10/2026): no fold from the page. The conversation
  // is folded on the server once per visit, ten minutes after the client's last
  // message (api/_visit-fold.js, run by the engine sweep), and at SUBMIT by the
  // forced engine run. Before, the page folded after every Alex reply (an Opus
  // call each time, overwritten by the engine's fold at SUBMIT).

  // PRE-SUBMIT READINESS LOOP — the first time Alex invites SUBMIT, run one
  // bounded MLRO-readiness pass and, if it raises material points, have Alex
  // ask them in THIS conversation instead of them coming back days later as a
  // post-submission request. Capped at 4 questions, one pass per case, never
  // blocking (submitting mid-check is fine; findings land on the file).
  const preSubmitInFlightRef = useRef(false);
  useEffect(() => {
    // SUBMISSION IS A HARD STOP (v1_653). Never start, and never inject, once the
    // client has submitted — checked against the live ref and the durable
    // submittedAt, not the stale `submitted` captured in this closure. submittedRef
    // is the acceptance-time hard-stop flag the SUBMIT handler sets synchronously.
    if (preSubmitLoopHalted({ submitted, submitStopFlag: submittedRef.current, submittedAt: collectedData.submittedAt }) || loading || isSowInterview) return;
    if (!sessionIdRef.current || !tokenRef.current) return;
    if (collectedData.preSubmitReviewDone || preSubmitInFlightRef.current) return;
    const last = messages[messages.length - 1];
    if (!last || last.role !== 'assistant' || !invitesSubmit(last.content)) return;

    preSubmitInFlightRef.current = true;
    (async () => {
      try {
        const res = await fetch('/api/pre-submit-review', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ sessionId: sessionIdRef.current, token: tokenRef.current })
        });
        const body = await res.json().catch(() => ({}));
        const questions = body?.review?.questions || [];
        // The client may have typed SUBMIT while this review was in flight. The
        // review and its request items are already persisted server-side by
        // /api/pre-submit-review (from a fresh read), so on a hard stop we defer
        // them to the officer request round and touch neither the chat nor
        // collected_data — writing the stale local copy here could drop the
        // submittedAt the completion latch just stamped. (defect 1 fix)
        const action = preSubmitReviewAction({ questionsCount: questions.length, submitted: submittedRef.current || !!collectedData.submittedAt });
        if (action === 'defer_to_officer') { preSubmitInFlightRef.current = false; return; }

        // v1_759: the review stored the interview's request items server side; the
        // soft close card reads them from local state, which never received them.
        // Read them back so the card appears with its four options.
        let storedItems = null;
        try {
          const { data: row, _error } = await clientRead('session', sessionIdRef.current, tokenRef.current, 'collected_data');
          storedItems = storedRequestItems(row);
          if (!storedItems) console.error('[pre-submit] stored request items not read back; the soft close card cannot show', _error || 'no requestItems on the session');
        } catch (e) { console.error('[pre-submit] request items read back failed; the soft close card cannot show', e && e.message); }
        const _itemsPatch = storedItems ? { requestItems: storedItems } : {};
        const merged = { ...collectedData, preSubmitReviewDone: true, preSubmitReview: body?.review || null, ..._itemsPatch };
        setCollectedData(prev => ({ ...prev, preSubmitReviewDone: true, preSubmitReview: body?.review || null, ..._itemsPatch }));
        await updateSession({ collected_data: merged });

        if (action === 'ask_client') {
          setLoading(true);
          try {
            // v1_636: the server now groups the items by theme (documents included) and
            // supplies the instruction; the numbered list is kept as a fallback for a
            // firm still on the old review shape.
            const trigger = (body?.review?.instruction && String(body.review.instruction).trim())
              || ("[SYSTEM: A final pre-submission readiness review has identified the following points that would otherwise come back as a follow-up request AFTER submission, delaying the client's approval. Before the client submits, work through them ONE AT A TIME, warmly and naturally, never as a list, explaining briefly that you are just closing out a couple of final points so their file goes to the compliance team complete:\n" + questions.map((q, i) => (i + 1) + ". " + q).join("\n") + "\nDo not repeat anything already answered. When all points are covered, briefly confirm the additions and re-invite the client to type SUBMIT with the standard bold SUBMIT line as the very last line.]");
            const reply = await callClaude(trigger, stage, merged, uploadedDocs);
            // Submission may have raced during the model call: if the client has
            // now submitted, drop the reply rather than posting into a closed chat.
            if (submittedRef.current || collectedData.submittedAt) { setLoading(false); preSubmitInFlightRef.current = false; return; }
            const t = (reply || '').trim();
            const looksJson = t.startsWith('{') || t.startsWith('[') || t.startsWith('```json');
            if (t && !looksJson) {
              setMessages(prev => [...prev, { role: "assistant", content: reply }]);
              await saveMessage("assistant", reply);
            }
          } catch (_) {}
          setLoading(false);
        }
      } catch (_) {}
      preSubmitInFlightRef.current = false;
    })();
  }, [messages, submitted, loading, isSowInterview]);

  // When the document review finishes mid-conversation the board lands silently in
  // the background. Have Alex proactively pick the conversation back up the moment
  // the file is ready, rather than waiting for the client to send a message. Sends a
  // hidden continuation instruction (never shown or stored as a client turn) so Alex
  // produces one natural "I've finished reviewing your documents" message.
  const autoContinueAfterBoard = async (data) => {
    if (submitted || loading) return;
    setLoading(true);
    try {
      const trigger = "[SYSTEM: Your background review of this client's uploaded documents has just finished and you now have their full compliance file, given to you as the file summary above. The client has just WATCHED this happen: a live progress panel on their screen showed the documents being read, registers checked, parties screened and the case assembled, ending with a completion summary. So speak as a colleague who just finished the work they saw in progress, not as if nothing happened. In ONE warm, natural message: acknowledge that the analysis they were watching has completed, briefly reference what you can now see from the file (for example the entity name and that you have their key incorporation and ownership documents), and continue the conversation from where it left off. Do not describe or restate the progress panel itself, do not ask them to re-state anything the file already contains, and do not present a final summary or invite SUBMIT yet.]";
      const reply = await callClaude(trigger, 'mlro_followup', data, uploadedDocs);
      const t = (reply || '').trim();
      const looksJson = t.startsWith('{') || t.startsWith('[') || t.startsWith('```json');
      if (t && !looksJson) {
        setMessages(prev => [...prev, { role: "assistant", content: reply }]);
        await saveMessage("assistant", reply);
        dfSpokenWithFileRef.current = true;
      }
    } catch (_) {
    } finally {
      setLoading(false);
    }
  };

  // While documents are analysed server-side, poll the session. When the board
  // lands (collected_data.boardReady), swap Alex into full-knowledge mode and merge
  // the document-gap questions alongside the activity questions already in progress.
  useEffect(() => {
    if (stage !== 'mlro_followup') return;
    if (!collectedData || !collectedData.documentsFirstChat || collectedData.boardReady) return;
    if (!sessionIdRef.current) return;
    let active = true;
    const iv = setInterval(async () => {
      try {
        // analysis_progress feeds the live strip. Schema-drift guard: an
        // environment without the 2026-08-01 migration rejects the whole
        // select, so retry without it rather than losing the boardReady swap.
        let data = null;
        try {
          ({ data } = await clientRead('session', sessionIdRef.current, tokenRef.current, 'report, collected_data, analysis_progress'));
        } catch (_) {}
        if (!data) ({ data } = await clientRead('session', sessionIdRef.current, tokenRef.current, 'report, collected_data'));
        if (!active) return;
        if (data && Array.isArray(data.analysis_progress)) { setAnalysisProgress(data.analysis_progress); dfDrive(data.analysis_progress); }
        const cd = data && data.collected_data;
        // Ready when the engine's flag is set OR the assembled report is on
        // file. A wholesale client write can clobber collected_data.boardReady
        // back to false after the checkpoint, but the report column survives,
        // so an assembled report (one with a clientName) is the durable
        // completion signal and the proactive continuation still fires
        // (staging 1748f85f). v1_642.
        const boardIsReady = !!(cd && cd.boardReady) || !!(data && data.report && data.report.clientName);
        if (data && data.report && boardIsReady) {
          const board = data.report;
          setDfBoard(board);
          const gapQs = cd.documentsFirstQuestions || [];
          // Item d: the generic source of wealth question is reconciled against the
          // stored board on the server, when Alex's prompt is built (v1_641); the page
          // holds only the board's company name.
          const mergedQs = [...DF_ACTIVITY_QUESTIONS, ...gapQs.filter(q => !DF_ACTIVITY_QUESTIONS.some(a => (a || '').trim() === (q || '').trim()))];
          setCollectedData(prev => ({ ...prev, boardReady: true, documentsFirstQuestions: mergedQs }));
          dfAutoPendingRef.current = true;
        }
      } catch (_) {}
    }, 5000);
    return () => { active = false; clearInterval(iv); };
  }, [stage, collectedData && collectedData.documentsFirstChat, collectedData && collectedData.boardReady]);

  // AFTER THE CHECKPOINT, THE FINAL WRITE STILL MATTERS (v1_617). The engine now
  // saves the assembled board and opens this gate minutes before its final write,
  // which can add points to cover (the VQF declaration questions among them),
  // refresh the brief, and record completion or failure. The poll above stops the
  // moment boardReady lands, so without this the client would never see any of
  // that. Lighter cadence, append-only on questions so nothing Alex has already
  // been given is taken away mid-conversation, and it stops on completion,
  // failure, or once the engine can no longer be running.
  useEffect(() => {
    if (stage !== 'mlro_followup') return;
    if (!collectedData || !collectedData.documentsFirstChat || !collectedData.boardReady) return;
    if (!sessionIdRef.current) return;
    if (dfFinalSeenRef.current) return;
    const startedAt = Date.now();
    let active = true;
    const iv = setInterval(async () => {
      try {
        // v1_802: the strip itself says when the run has stopped (analysisRunState);
        // this is only a backstop against a poll left running for hours.
        if (Date.now() - startedAt > 2 * 60 * 60 * 1000) { clearInterval(iv); return; }
        let data = null;
        try {
          ({ data } = await clientRead('session', sessionIdRef.current, tokenRef.current, 'collected_data, analysis_progress'));
        } catch (_) {}
        // Same schema-drift guard as the poll above: without the progress column the
        // whole select is rejected, so fall back to collected_data alone and read
        // completion from consistencyCheck, which only the final write ever sets.
        if (!data) { try { ({ data } = await clientRead('session', sessionIdRef.current, tokenRef.current, 'collected_data')); } catch (_) {} }
        if (!active || !data) return;
        const ap = Array.isArray(data.analysis_progress) ? data.analysis_progress : [];
        if (ap.length) { setAnalysisProgress(ap); dfDrive(ap); }
        const cd = data.collected_data;
        // v1_802: completion is the strip's (analysisRunState). An earlier run's
        // consistencyCheck is not this run finishing; it is read only where the
        // progress column is missing altogether. v1_804: and the strip is read
        // for the run this page started, by its runId (dfRunStateFor).
        const _run = dfRunStateFor(dfRunRef, ap);
        const finished = _run.complete || _run.stopped
          || (!Object.prototype.hasOwnProperty.call(data, 'analysis_progress') && !!(cd && Object.prototype.hasOwnProperty.call(cd, 'consistencyCheck')));
        if (finished && cd && cd.boardReady) {
          dfFinalSeenRef.current = true;
          setCollectedData(prev => {
            const have = Array.isArray(prev.documentsFirstQuestions) ? prev.documentsFirstQuestions : [];
            const incoming = Array.isArray(cd.documentsFirstQuestions) ? cd.documentsFirstQuestions : [];
            const added = incoming.filter(q => !have.some(h => (h || '').trim() === (q || '').trim()));
            // Item d: the combined ask set is reconciled against the stored board on the
            // server when Alex's prompt is built (v1_641), and the board and its brief
            // stay there: the page keeps only the points.
            return {
              ...prev,
              documentsFirstQuestions: [...have, ...added]
            };
          });
          clearInterval(iv);
        }
      } catch (_) {}
    }, 15000);
    return () => { active = false; clearInterval(iv); };
  }, [stage, collectedData && collectedData.documentsFirstChat, collectedData && collectedData.boardReady]);

  // v1_805: FOLLOW AND DRIVE THE RUN THIS PAGE STARTED, whatever screen the
  // client is on (the submission, the return visit, the status page), until it
  // reads complete or stopped. The polls above also drive while they run; this
  // covers the screens they do not. With the page closed, the engine sweep cron
  // drives it instead.
  useEffect(() => {
    let busy = false;
    const iv = setInterval(async () => {
      const r = dfRunRef.current;
      if (busy || !r || r.finished || !sessionIdRef.current) return;
      busy = true;
      try {
        const { data } = await clientRead('session', sessionIdRef.current, tokenRef.current, 'analysis_progress');
        const ap = data && Array.isArray(data.analysis_progress) ? data.analysis_progress : null;
        if (ap) {
          dfDrive(ap);
          const st = dfRunStateFor(dfRunRef, ap);
          if ((st.complete || st.stopped) && dfRunRef.current === r) dfRunRef.current = { ...r, finished: true };
        }
      } catch (_) {} finally { busy = false; }
    }, 5000);
    return () => clearInterval(iv);
  }, []);

  // Fire Alex's proactive continuation once the board has landed (flagged by the poll
  // above). Waits until the client is idle so it never collides with a turn in flight,
  // and fires at most once per session. Never fires on resume (the poll does not run
  // when the board is already ready on entry, so the pending flag stays false).
  useEffect(() => {
    if (!dfAutoPendingRef.current || dfAutoDoneRef.current) return;
    if (!collectedData || !collectedData.documentsFirstChat || !collectedData.boardReady) return;
    if (submitted || loading) return;
    dfAutoDoneRef.current = true;
    dfAutoPendingRef.current = false;
    // If Alex's last message is an unanswered QUESTION, never barge in with a
    // second message on top of it — the board has already merged silently into
    // Alex's knowledge, and the acknowledgement folds into the next natural
    // reply. But if the last message is a waiting statement ("I'll be back with
    // you shortly"), this is precisely the moment to speak: deferring there
    // creates a three-way deadlock — Alex told the client to wait, the client
    // obediently waits, and the auto-continue politely waits for the client —
    // and the interview stalls until someone types "hello" (seen 01/08/26).
    // v1_858: decided from the last sentence alone, and a closing promise to
    // come back counts as waiting (src/shared/alex-waiting.js): a holding
    // message with an earlier question read as a question, and Alex never came
    // back when the review finished (walk w4f1b992e).
    const lastMsg = messages[messages.length - 1];
    const lastText = lastMsg && typeof lastMsg.content === 'string' ? lastMsg.content : '';
    const asksQuestion = awaitsClientAnswer(lastText);
    if (lastMsg && lastMsg.role === "assistant" && asksQuestion) { dfAckPendingRef.current = true; return; }
    autoContinueAfterBoard(collectedData);
  }, [collectedData, loading, submitted]);

  // fix-client-email-recipient (19/10/2026): the list goes to the email address
  // on the invitation, which the server reads from the case; the page sends no
  // address and no link.
  const emailDocList = async (checklist) => {
    setDfListStatus("sending");
    try {
      const r = await fetch("/api/email-doc-list", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          checklist: checklist || [],
          sessionId: sessionIdRef.current || null,
          token: tokenRef.current || null
        })
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok || !d.to) throw new Error();
      setDfListStatus("sent:" + d.to);
    } catch (_) {
      setDfListStatus("error");
    }
  };

  // Invite with documents: the client confirms the documents the firm supplied,
  // or sends a current copy of one (recorded against it on the server).
  const prefiledFirmDocs = (docsOnFile || []).filter(d => d && d.suppliedVia === 'officer_upload');
  // Owner 15/10/2026: while a firm supplied document is still being read, the
  // list is fetched again now and then, so the checklist marks what it covers
  // once the reading has worked (for up to fifteen minutes on this page).
  const coverPollsRef = useRef(0);
  useEffect(() => {
    if (!dfPrefiled || coverPollsRef.current >= 45) return undefined;
    const reading = prefiledFirmDocs.some(d => !d.providedAs && (d.state === 'awaiting' || d.state === 'confirmed'));
    if (!reading) return undefined;
    const t = setTimeout(() => { coverPollsRef.current += 1; loadDocsOnFile(); }, 20000);
    return () => clearTimeout(t);
  }, [dfPrefiled, docsOnFile]);
  const prefiledSay = (m) => { setDfStatus(m); setPrefiledNotice(m); };
  const confirmPrefiled = async (ids) => {
    setPrefiledNotice('');
    if (!ids || !ids.length || prefiledBusy) return;
    setPrefiledBusy(true);
    try {
      const r = await fetch('/api/save-document', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ action: 'confirm-supplied', sessionId: sessionIdRef.current, token: tokenRef.current, documentIds: ids }) });
      if (!r.ok) { const j = await r.json().catch(() => ({})); throw new Error(j.error || ('status ' + r.status)); }
      await loadDocsOnFile();
    } catch (e) { prefiledSay('We could not record your confirmation just now. Please try again. (' + String(e.message || e).slice(0, 80) + ')'); }
    finally { setPrefiledBusy(false); }
  };
  const replacePrefiled = async (doc, file) => {
    setPrefiledNotice('');
    if (!doc || !file || prefiledBusy) return;
    // Upload fix batch, fault f: the shared type list, the refusal named.
    { const c = splitUploads([file]); if (c.refused.length) { prefiledSay(c.refused[0]); return; } }
    setPrefiledBusy(true);
    try {
      const fp = await uploadToStorage(file);
      const r = await fetch('/api/save-document', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ sessionId: sessionIdRef.current, token: tokenRef.current, replacesDocumentId: doc.id, document: { document_name: file.name, document_type: 'Pending review', file_size: file.size, file_url: null, file_path: fp, mime_type: file.type || null } }) });
      if (!r.ok) { const j = await r.json().catch(() => ({})); throw new Error(j.error || ('status ' + r.status)); }
      await loadDocsOnFile();
    } catch (e) { prefiledSay(/cannot be uploaded:/.test(String(e && e.message)) ? String(e.message) : 'We could not upload ' + file.name + ' just now. Please try again. (' + String(e.message || e).slice(0, 80) + ')'); }
    finally { setPrefiledBusy(false); }
  };

  // opts.files and opts.auto: reading continuing by itself once the last failed
  // file was uploaded or removed (owner decision 2), with the list as it is now.
  const runDocumentsFirst = async (opts) => {
    const auto = !!(opts && opts.auto);
    const dfFiles = (opts && Array.isArray(opts.files)) ? opts.files : dfFilesRef.current;
    // Invite with documents: with documents already on file the client may go on
    // without adding more (the firm's documents are read with theirs).
    if ((!dfFiles.length && !(dfPrefiled && prefiledFirmDocs.length)) || (dfBusy && !auto)) return;
    setDfBusy(true);
    if (auto) setDfStatus(DF_CONTINUE_LINE);
    try {
      const sid = sessionIdRef.current;
      const context = { companyName: dfContact.company, contactName: dfContact.name, contactEmail: dfContact.email, statedBusinessType: dfAnswers.businessType || null, statedRegulated: dfAnswers.regulated || null, statedOwnership: dfAnswers.ownership || null, involvesCrypto: dfAnswers.crypto || null };
      // Owner decision 1 (02/11/2026): every file is uploaded and reported on its own;
      // one failure no longer stops the rest. A file already uploaded is not sent again.
      const states = new Map(dfUploadsRef.current);
      const toSend = dfFiles.filter(f => (states.get(f) || {}).state !== 'uploaded');
      const results = await uploadEach(toSend, dfUploadRaw, { onProgress: (i, n, file) => setDfStatus(`Uploading document ${i + 1} of ${n}: ${file.name} ...`) });
      for (const r of results) states.set(r.file, dfStateFromResult(r));
      putDfUploads(states);
      // Owner decision 2: reading does not start while a failed file is in the list.
      // The client sees which one, and can try it again or remove it.
      if (!readingMayStart(dfFiles, f => states.get(f))) { setDfStatus(dfFailureStatus(dfFiles, states)); return; }
      const manifest = dfFiles.map(f => states.get(f).entry);
      setDfStatus(auto ? DF_CONTINUE_LINE : '');
      const baseCollected = { contactName: dfContact.name, contactEmail: dfContact.email, companyName: dfContact.company, intake: dfAnswers, docManifest: manifest, dfContext: context, dfProcessingStartedAt: new Date().toISOString(), preScreen: null, consentConfirmed: true, consentTimestamp: dfConsentAt || new Date().toISOString(),
        ...((financialRevenueBand && financialAssetsBand) ? { financialRanges: { annualRevenue: financialRevenueBand, totalAssets: financialAssetsBand, indicated: true, indicatedAt: dfConsentAt || new Date().toISOString() } } : {}),
        // v1_836b: answers already given on the checklist to the firm's questions
        // stay in view in the chat (walk wb2c6b466: the card read 0 of 2 answered).
        ...(Array.isArray(collectedData.firmQuestions) ? { firmQuestions: collectedData.firmQuestions } : {}) };
      await updateSession({
        status: 'in_progress',
        company_name: dfContact.company,
        collected_data: { ...baseCollected, onboardingMode: 'documents_first', documentsFirstChat: true, boardReady: false, documentsFirstQuestions: DF_ACTIVITY_QUESTIONS }
      });
      // Invite with documents: the firm's documents were read before the invitation,
      // so this run reads every document on the case together (a rebuild, as the
      // submission's run is: stored readings are reused, the client's are read).
      startDfRun(dfRunRef, { sessionId: sid, docs: manifest, context, token: tokenRef.current, ...(dfPrefiled ? { force: true } : {}) });
      await enterDocumentsFirstChatPending(DF_ACTIVITY_QUESTIONS, baseCollected);
    } catch (e) {
      // This path had no reporting at all, which is why a complete upload outage produced nothing
      // in Sentry: the browser talks to storage directly, and the fetch wrapper only watches /api/.
      // Owner decision 1: the technical detail goes to Sentry only, never to the client.
      try { captureError(e instanceof Error ? e : new Error(String(e)), { extra: { kind: 'documents_first_upload', files: dfFiles.length } }); } catch (_) {}
      setDfStatus('We could not continue just now. Nothing you selected has been lost, so please try again in a moment. If it happens again, contact ' + supportEmail + '.');
    } finally {
      setDfBusy(false);
    }
  };
  // Owner decision 2: one failed file tried again on its own. Once no failed
  // file remains, reading continues by itself (no second Submit), said on screen.
  const retryDfFile = async (file) => {
    if (dfBusy || !file) return;
    setDfBusy(true);
    setDfRetrying(true);
    setDfDropNote('');
    const files = dfFilesRef.current;
    let carryOn = false;
    try {
      const states = new Map(dfUploadsRef.current);
      const [r] = await uploadEach([file], dfUploadRaw, { onProgress: () => setDfStatus(`Uploading ${file.name} ...`) });
      states.set(file, dfStateFromResult(r));
      putDfUploads(states);
      setDfStatus(dfFailureStatus(files, states));
      if (r.ok) setDfDropNote(`${file.name} has uploaded.`);
      carryOn = r.ok && dfConsent && readingMayStart(files, f => states.get(f));
    } finally {
      setDfBusy(false);
      setDfRetrying(false);
    }
    if (carryOn) await runDocumentsFirst({ files, auto: true });
  };
  // Owner decision 2: the client may remove a failed file and go on without it;
  // with none left, reading continues by itself.
  const removeDfFile = (file) => {
    if (dfBusy) return;
    const wasFailed = (dfStateOf(file) || {}).state === 'failed';
    const next = dfFilesRef.current.filter(f => f !== file);
    const states = new Map(dfUploadsRef.current); states.delete(file);
    putDfUploads(states);
    putDfFiles(next);
    setDfStatus(dfFailureStatus(next, states));
    // Staging test 03/11/2026: the note beside the picker said "Added 9 files." after a Remove.
    setDfDropNote(`${file.name} was removed from the list.`);
    if (wasFailed && dfConsent && (next.length || (dfPrefiled && prefiledFirmDocs.length)) && readingMayStart(next, f => states.get(f))) runDocumentsFirst({ files: next, auto: true });
  };

  // Item d: ALEX'S PROMPT IS BUILT ON THE SERVER. The page used to assemble the
  // system prompt here from the whole report and collected_data it was sent (the
  // risk tier, the engine's assessment, the board and its brief, the live
  // screening flags, the escalation detector's reading of the board). It now
  // sends only the conversation and its turn context; /api/chat loads the case
  // and builds the same prompt (src/shared/client-alex-prompt.js), including the
  // review state check (v1_642) and the escalation detector. The server tells
  // the page what it may heal: the review finished, the interview went full.
  const callClaude = async (userMessage, currentStage, data, docs, submitDecision = 'none') => {
    if (currentStage === "report") throw new Error('The report is prepared on the server (/api/client-report).');
    const history = messages.map(m => ({ role: m.role, content: m.content }));
    const fullHistory = [...history, { role: "user", content: userMessage }];
    const turn = {
      userMessage, currentStage, data,
      docs: (docs || []).map(d => ({ name: d.name, analysis: d.analysis, uploadedAt: d.uploadedAt })),
      history, docsOnFile, isSowInterview,
      interviewEverFull: interviewEverFullRef.current,
      dfAckPending: dfAckPendingRef.current,
      visitStart: visitStartRef.current,
      submitDecision
    };
    if (dfAckPendingRef.current) dfAckPendingRef.current = false;
    submitStateRef.current = null;
    const onData = (d) => {
      const cs = d && d.clientState;
      if (!cs) return;
      if (cs.submitGo) submitStateRef.current = { go: true };
      else if (cs.submitHold) submitStateRef.current = { hold: cs.submitHold };
      if (cs.interviewEverFull) interviewEverFullRef.current = true;
      if (cs.boardReady) {
        if (cs.clientName) setDfBoard(prev => ({ ...(prev || {}), clientName: cs.clientName }));
        setCollectedData(prev => ({ ...prev, boardReady: true, ...(Array.isArray(cs.documentsFirstQuestions) && cs.documentsFirstQuestions.length ? { documentsFirstQuestions: cs.documentsFirstQuestions } : {}) }));
      }
    };
    // v1_740: the interview turn runs with thinking off (the server's purpose sets it).
    const reply = await apiCall({ purpose: "interview", turn, messages: fullHistory }, 2, onData);
    // v1_923: never "complete" while an officer sent item is still owed.
    return guardCompletionClaim(reply, data);
  };

  const analyzeDocument = async (docName, fileBase64, fileType, collectedContext, filePath) => {
    try {
      let messageContent = [];
      if (fileBase64 && (fileType === "application/pdf" || fileType.startsWith("image/"))) {
        if (fileType === "application/pdf") {
          messageContent.push({ type: "document", source: { type: "base64", media_type: "application/pdf", data: fileBase64 } });
        } else {
          messageContent.push({ type: "image", source: { type: "base64", media_type: fileType, data: fileBase64 } });
        }
      }
      const _userText = "Document: " + docName + "\n\nClient data (context only, use ONLY to identify what this document is, never to judge the case):\n" + collectedContext + "\n\nExtract the intelligence from THIS document and assess only the document itself. You are reading one document. You cannot see the other documents in the file, the full transcript, or the final risk picture, so do not draw case-level conclusions. Do NOT decide whether the client is regulated, supervised for AML, or eligible. Do NOT flag other documents as missing. Do NOT flag relationships to other companies or group entities. Those are decided later by the case-level review that sees everything. Return ONLY JSON using this exact schema:\n{ \"docType\": \"...\", \"documentLanguage\": \"...\", \"assessment\": \"Satisfactory\", \"keyFindings\": [\"...\"], \"materialFlags\": [], \"notedFlags\": [], \"informationals\": [], \"registerExtract\": { \"isEnumeratingDocument\": false, \"subjectEntity\": \"...\", \"peopleNamed\": [{ \"name\": \"...\", \"role\": \"...\", \"date\": \"...\" }], \"holdersNamed\": [{ \"holder\": \"...\", \"held\": \"...\", \"percentage\": \"...\", \"basis\": \"direct|indirect\" }] } }\nENUMERATING DOCUMENTS, registerExtract OVERRIDES any limit on keyFindings. A register of directors, register of members or shareholders, shareholder/UBO register extract, certificate of incumbency, articles naming officers, board resolution appointing officers, ownership or structure chart, or an official register extract exists in order to LIST people or holdings, and for those the list IS the content: a name that does not reach the report cannot be identified, verified or screened. Set isEnumeratingDocument true and name EVERY person and EVERY holding the document contains, never truncate, never sample. subjectEntity is REQUIRED on every enumerating document, never blank: the entity THIS document enumerates the owners of, exactly as the document names it, read from its own header/title. It is the applicant for the applicant's own register, but a named PARENT or intermediate company for that company's own PSC or register extract. For each holder, held is the entity they hold (leave it empty to mean subjectEntity). WORKED EXAMPLE: a Companies House PSC page headed 'Creative Agentics Limited - People with significant control' naming Danielle Sage is the PSC register OF Creative Agentics Limited, so subjectEntity is 'Creative Agentics Limited' and Danielle's held is 'Creative Agentics Limited' (she holds the PARENT), NEVER the applicant; recording her as holding the applicant directly would flatten a two-tier structure. Give each person the role the DOCUMENT gives them, and include anyone recorded as resigned or ceased with that stated in role. For any other document set isEnumeratingDocument false and leave both arrays empty. Never invent a person the document does not name.\nAssessment must be exactly one of: Satisfactory, Requires Clarification, or Unsatisfactory, judged ONLY on whether this document is valid, legible and internally consistent. documentLanguage is the language the document is written in (for example \"English\", \"Spanish\"; the predominant language if mixed) and must always be set."
      ;
      messageContent.push({ type: "text", text: _userText });

      // Where the file is already in storage, analyse it FROM storage. The bytes then never pass
      // through a serverless request body, so the 3.2MB ceiling that limited this disappears and
      // the real limit becomes the model's own, which one KYC document rarely approaches.
      if (filePath) {
        const r = await fetch('/api/analyze-document', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            sessionId: sessionIdRef.current, filePath, token: tokenRef.current,
            // Item d: a client's document is read under the server's own prompt (DOC_READ_SYSTEM).
            mimeType: fileType, userText: _userText,
            // Opus 5: thinking shares the budget with the response — 2000 risked truncated JSON.
            model: 'claude-opus-5-5', maxTokens: 8000, effort: 'medium'
          })
        });
        const j = await r.json().catch(() => ({}));
        if (j && j.success && j.text) return repairModelJson(j.text);
        const reason = (j && j.reason) || 'analysis_error';
        const detail =
          reason === 'file_too_large_for_model' ? `The document is ${j.sizeMb}MB, above the ${j.limitMb}MB the analysis service accepts.`
          : reason === 'too_many_pages_for_model' ? `The document is ${j.pageCount} pages, above the ${j.limitPages}-page limit for automated reading.`
          : reason === 'unsupported_format' ? `The file format (${j.mimeType || 'unknown'}) cannot be read automatically.`
          : 'Automated analysis did not complete.';
        console.error('analyze-document declined:', reason, detail);
        return {
          docType: docName, documentLanguage: 'Unknown', assessment: 'Requires Clarification',
          keyFindings: [`Document received but its contents were NOT read by the platform. ${detail} It has not been cross-referenced against the rest of the file and must be read directly.`],
          materialFlags: [], notedFlags: [], informationals: [detail],
          analysisSkipped: true, analysisSkippedReason: reason, analysisSkippedDetail: detail,
          pageCount: (j && j.pageCount) || null, sizeMb: (j && j.sizeMb) || null
        };
      }

      // Item d: the document reading prompt is the server's (DOC_READ_SYSTEM); the
      // purpose sets the model and its budget.
      const text = await apiCall({
        purpose: "document_read",
        messages: [{ role: "user", content: messageContent }]
      });
      return repairModelJson(text);
    } catch(e) {
      // A failure here USED to return this fallback silently, indistinguishable from a genuine
      // "needs a human look" verdict. That was the worst outcome available: cross-referencing works
      // from each document's extracted findings, so a document with no findings is invisible to
      // contradiction detection while the engine still returns a clean result. The file looked
      // complete and was not. Failures are now marked explicitly so the case can say so out loud.
      console.error('analyzeDocument failed:', e.message);
      return {
        docType: docName, documentLanguage: 'Unknown', assessment: "Requires Clarification",
        keyFindings: ["Document received but its contents were NOT read by the platform. It has not been cross-referenced against the rest of the file and must be read directly."],
        materialFlags: [], notedFlags: [],
        informationals: ["Automated analysis did not complete for this document."],
        analysisSkipped: true, analysisSkippedReason: 'analysis_error'
      };
    }
  };

  const handleSend = async (overrideText) => {
    const sourceText = (typeof overrideText === 'string' && overrideText) || input;
    if (!sourceText.trim() || loading) return;
    const userMsg = sourceText.trim();
    if (typeof overrideText !== 'string') setInput("");
    setMessages(prev => [...prev, { role: "user", content: userMsg }]);
    setLoading(true);
    try {
      let currentStage = stage;
      let updatedData = { ...collectedData, [currentStage + "_response"]: userMsg };

      // Store name and email during welcome
      if (stage === "welcome") {
        if (userMsg.includes("@")) {
          updatedData = { ...updatedData, contactEmail: userMsg.trim() };
          // Create session once we have both name and email
          if (updatedData.contactName && !sessionIdRef.current) {
            await createSession(updatedData.contactName, userMsg.trim());
          }
        } else if (!isSowInterview && !collectedData.contactEmail && !collectedData.contactName) {
          // v1_898: never in a source of wealth interview, whose person is named by the invitation.
          updatedData = { ...updatedData, contactName: userMsg.trim() };
        }
      }

      // Detect and fetch website
      const looksLikeUrl = !isSowInterview && !isIndividualClient && !userMsg.includes("@") && (
        userMsg.includes("www.") || userMsg.includes("http") ||
        /[a-zA-Z0-9-]+\.(com|co|io|net|org|uk|gg|sg|gi|vc)/.test(userMsg)
      );
      if (looksLikeUrl && !updatedData.websiteData) {
        setMessages(prev => [...prev, { role: "assistant", content: "Give me a moment while I take a look at your website..." }]);
        const websiteData = await fetchWebsiteData(userMsg);
        if (websiteData && !websiteData.error) {
          updatedData = {
            ...updatedData, websiteData,
            prefilled_companyName: websiteData.companyName || null,
            prefilled_legalName: websiteData.legalName || null,
            prefilled_registrationNumber: websiteData.registrationNumber || null,
            prefilled_registeredAddress: websiteData.registeredAddress || null,
            prefilled_jurisdiction: websiteData.jurisdiction || null,
            prefilled_businessDescription: websiteData.businessDescription || null,
            prefilled_servicesOffered: websiteData.servicesOffered || null,
            prefilled_targetClients: websiteData.targetClients || null,
            prefilled_regulatoryStatus: websiteData.regulatoryStatus || null,
            prefilled_regulatoryBody: websiteData.regulatoryBody || null,
            prefilled_licenceNumber: websiteData.licenceNumber || null,
            prefilled_directors: websiteData.directors || null,
          };
          // LAYER 1 — Trigger deep pre-screen intelligence in background
          // Checks regulatory registers, adverse news, public databases independently
          runPreScreen(
            userMsg,
            websiteData.companyName || websiteData.legalName || null,
            updatedData.contactEmail ? updatedData.contactEmail.split('@')[1] : null
          );
        }
      }

      // Stage transitions
      if (stage === "welcome" && (looksLikeUrl || updatedData.websiteData)) {
        currentStage = "company"; setStage("company");
      }

      // Detect wallet addresses in user message (Bitcoin, Ethereum, Tron, generic crypto)
      const walletRegex = /\b(bc1[a-zA-HJ-NP-Z0-9]{25,90}|[13][a-zA-HJ-NP-Z0-9]{25,34}|0x[a-fA-F0-9]{40}|T[a-zA-HJ-NP-Z0-9]{33}|[a-zA-Z0-9]{32,64}(?=\s|$))\b/g;
      const detectedWallets = userMsg.match(walletRegex) || [];
      // Filter to likely wallet addresses (exclude short words, emails, URLs)
      const likelyWallets = detectedWallets.filter(w => w.length >= 26 && !w.includes('@') && !w.includes('.'));
      if (likelyWallets.length > 0) {
        const existingWallets = updatedData.walletAddresses || [];
        const newWallets = likelyWallets.filter(w => !existingWallets.includes(w));
        if (newWallets.length > 0) {
          updatedData = { ...updatedData, walletAddresses: [...existingWallets, ...newWallets] };
          // Documents-first persists collected_data here (the rolling merge only writes
          // the report), so the dashboard sees the wallet for Chainalysis / Travel Rule.
          if (updatedData.documentsFirstChat) {
            try { await updateSession({ collected_data: updatedData }); } catch (_) {}
          }
        }
      }

      setCollectedData(updatedData);
      // Save user message to DB
      await saveMessage("user", userMsg);

      // Flag post-submission activity for MLRO dashboard
      const sessionUpdateFields = { collected_data: updatedData };
      if (isReturningClient) {
        sessionUpdateFields.has_updates = true;
        sessionUpdateFields.last_update_type = "message";
        sessionUpdateFields.last_update_at = new Date().toISOString();
      }
      await updateSession(sessionUpdateFields);

      // v1_894: decide what a typed SUBMIT does BEFORE Alex replies, from whether
      // Alex has yet spoken with the documents file.
      const _isDfTurn = !!(updatedData.documentsFirstChat || updatedData.onboardingMode === 'documents_first' || updatedData.documentsBoard || updatedData.docManifest);
      const dfDecision = dfSubmitDecision({ typedSubmit: isTypedSubmit(userMsg), isDf: _isDfTurn, boardReady: !!updatedData.boardReady, spokenWithFile: dfSpokenWithFileRef.current, submittedAt: updatedData.submittedAt });
      // 11/10/2026 (walk w73bbfaa0): while the reading is still running Alex is not
      // asked at all. It had answered "Your file has been sent" before the page
      // held the submission. The fixed line says the file has not gone, and is kept.
      if (dfDecision === 'hold_analysis') {
        setMessages(prev => [...prev, { role: "assistant", content: SUBMIT_HOLD_TEXT }]);
        await saveMessage("assistant", SUBMIT_HOLD_TEXT);
        setLoading(false);
        return;
      }
      const reply = await callClaude(dfDecision === 'raise_points_first' ? userMsg + '\n\n' + RAISE_POINTS_FIRST : userMsg, currentStage, updatedData, uploadedDocs, dfDecision);
      // A cleared SUBMIT comes back with no reply: the page sends the file and
      // confirms it once the write has succeeded. A held one carries Alex's one ask.
      const submitGo = !!(submitStateRef.current && submitStateRef.current.go);
      const submitHeld = !!(submitStateRef.current && submitStateRef.current.hold);
      // Don't show raw JSON to client - if reply looks like JSON it's a report generation error
      // Block JSON appearing in chat — check if reply contains JSON anywhere
      const backtickJson = '```json';
      const replyIsJson = reply.trim().startsWith('{') || reply.trim().startsWith('[') 
        || reply.trim().startsWith(backtickJson)
        || (reply.includes(backtickJson) && (reply.includes('"uboName"') || reply.includes('"clientName"') || reply.includes('"interviewComplete"')))
        || (reply.includes('"reportTitle"') && reply.includes('"riskTier"'))
        || (reply.includes('"uboName"') && reply.includes('"interviewComplete"'))
        || (reply.includes('"wealthOrigins"') && reply.includes('"sowAssessment"'));
      if (submitGo && !reply.trim()) {
        // nothing to show yet: finishDocumentsFirstChat confirms after the write
      } else if (!replyIsJson) {
        setMessages(prev => [...prev, { role: "assistant", content: reply }]);
        // Save assistant reply to DB
        await saveMessage("assistant", reply);
        if (updatedData.documentsFirstChat && updatedData.boardReady) dfSpokenWithFileRef.current = true;
        // v1_694: reconcile conversational answers back to the shared checklist
        // store. When the client answers one of Alex's questions in chat, the
        // matching requestItem is marked answered server-side, so the checklist,
        // the status panel and the officer's outstanding list all reflect it —
        // no checklist click needed. Fire-and-forget, non-blocking, and guarded
        // so only one runs at a time. Only fires when there are open items Alex
        // asked, and never during a SOW interview (its items are separate).
        try {
          const _ri = (updatedData.requestItems || collectedData.requestItems || []);
          const _openAsked = _ri.filter(i => i && ['asked_in_interview', 'requested', 'promised', 'partial'].includes(i.state));
          if (!isSowInterview && _openAsked.length && sessionIdRef.current && tokenRef.current && !reconcileInFlightRef.current) {
            reconcileInFlightRef.current = true;
            fetch('/api/reconcile-answers', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ sessionId: sessionIdRef.current, token: tokenRef.current }) })
              .then(r => r.json())
              .then(rb => {
                if (rb && rb.changed && Array.isArray(rb.requestItems)) {
                  setCollectedData(prev => ({ ...prev, requestItems: rb.requestItems }));
                  try { fetchClientStatus(); } catch (_) {}
                }
              })
              .catch(() => {})
              .finally(() => { reconcileInFlightRef.current = false; });
          }
        } catch (_) {}
      } else if (isSowInterview) {
        // SOW JSON leaked into chat — trigger report generation silently
        setTimeout(() => generateSowReport(), 500);
      }
      const rl = reply.toLowerCase();

      // SOW interview completion trigger — only fire if not already submitted
      const sowCompleteTriggers = [
        "everything we need", "pass this back", "don't need to do anything else", "compliance team now",
        "that's everything", "that is everything", "nothing else you need to do", "nothing more you need",
        "thank you for completing", "profile is now complete", "all done", "we're all done", "we are all done",
        "passed to our compliance", "shared with our compliance", "recorded and passed", "information we need",
        "have what we need", "got everything", "completed your profile", "profile complete"
      ];
      const hasSowComplete = sowCompleteTriggers.some(t => rl.includes(t));
      if (isSowInterview && hasSowComplete && !submitted) {
        setSubmitted(true); // Set immediately to block any further messages
        setTimeout(() => generateSowReport(), 1500);
        return;
      }
      // If SOW interview already submitted or complete, block all further processing
      if (isSowInterview && submitted) {
        setLoading(false);
        return;
      }

      // Report generation — only fires when client explicitly types SUBMIT
      // This is intentional: "SUBMIT" is unlikely to appear in normal conversation
      // and gives the client clear, explicit control over when their file is sent
      const clientTypedSubmit = /^submit[.!]?$/i.test(userMsg.trim()) || /^yes,?\s+submit/i.test(userMsg.trim()) || userMsg.trim().toLowerCase().includes("**submit**");
      // Documents-first is decided by DURABLE evidence, never by in-memory
      // flags alone: a resumed session (or one whose collected_data was
      // rewritten by a healing path) can lose the boolean flags, and a df
      // SUBMIT that falls through to the classic-flow branch dies silently
      // against data shaped for the other machine — the client is thanked,
      // nothing persists, no email, no merge (seen live on staging 02/08/26).
      const isDfCase = !!(updatedData.documentsFirstChat || updatedData.onboardingMode === 'documents_first' || updatedData.documentsBoard || updatedData.docManifest);
      if (clientTypedSubmit && isDfCase && updatedData.boardReady && (dfDecision === 'raise_points_first' || submitHeld)) {
        // v1_894: the reading landed but Alex had not yet spoken with it. Alex's
        // reply above raised the findings (RAISE_POINTS_FIRST); the next SUBMIT goes.
        // 11/10/2026: or the server held this SUBMIT for one unanswered point,
        // asked once; the next SUBMIT goes.
      }
      else if (clientTypedSubmit && isDfCase && updatedData.boardReady) {
        submittedRef.current = true; // hard stop the instant SUBMIT is accepted, before the deferred finish (v1_653)
        setTimeout(() => finishDocumentsFirstChat(updatedData), 800);
      }
      else if (clientTypedSubmit && isDfCase && !updatedData.boardReady) {
        // Analysis genuinely still running: hold the submission honestly
        // rather than misrouting it. The strip on screen shows the progress.
        // (Reached only if the decision above did not catch it first.)
        setMessages(prev => [...prev, { role: "assistant", content: SUBMIT_HOLD_TEXT }]);
        saveMessage("assistant", SUBMIT_HOLD_TEXT);
      }
      else if (clientTypedSubmit && !isDfCase && !["company","welcome"].includes(currentStage)) {
        setStage("summary"); setStageProgress(p => ({...p, documents: true}));
        setTimeout(() => generateReport(), 1000);
      }
      // Stage progression
      // Individual onboarding: the person interview is one stage; Alex's summary
      // (it invites SUBMIT) moves it to the summary stage, so the Submit button shows.
      else if (currentStage === "person" && invitesSubmit(reply)) { setStage("summary"); setStageProgress(p => ({...p, person: true})); }
      else if (currentStage === "company" && (rl.includes("director") || rl.includes("ownership") || rl.includes("shareholder") || rl.includes("ubo") || rl.includes("beneficial owner") || rl.includes("who own") || rl.includes("controlling"))) { setStage("ownership"); setStageProgress(p => ({...p, company: true})); }
      else if (currentStage === "ownership" && (rl.includes("regulat") || rl.includes("licen") || rl.includes("authoris") || rl.includes("supervis") || rl.includes("fca") || rl.includes("finma") || rl.includes("sec ") || rl.includes("registered with"))) {
        setStage("regulatory"); setStageProgress(p => ({...p, ownership: true}));
        // LIVE SCREENING — ownership is complete, so every director and UBO name
        // is now on the table while the conversation is still open. Fire the
        // rapid open-source PEP/sanctions/adverse sweep once (fire-and-forget);
        // the result lands in collected_data.liveScreening and folds into
        // Alex's prompt below, so any follow-up question gets asked in THIS
        // conversation instead of a post-submission MLRO round trip.
        if (!collectedData.liveScreeningRequested && !isSowInterview && sessionIdRef.current && tokenRef.current) {
          setCollectedData(prev => ({ ...prev, liveScreeningRequested: true }));
          fetch('/api/live-screen', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ sessionId: sessionIdRef.current, token: tokenRef.current }) })
            // Item d: the findings stay on the case; Alex's prompt (built on the server) reads them.
            .catch(() => {});
        }
        // Trigger Companies House verification if UK entity
        const regNum = updatedData.registrationNumber || updatedData.prefilled_registrationNumber;
        const jurisdiction = (updatedData.jurisdiction || updatedData.prefilled_jurisdiction || '').toLowerCase();
        if (regNum && (jurisdiction.includes('uk') || jurisdiction.includes('england') || jurisdiction.includes('wales') || jurisdiction.includes('scotland'))) {
          runExternalVerify('companies_house', regNum, updatedData.companyName || updatedData.prefilled_companyName);
        }
        // GLEIF lookup for any entity — by company name
        const cName = updatedData.companyName || updatedData.prefilled_companyName;
        if (cName) runExternalVerify('gleif', cName, cName);
        // #119: no sanctions screen from the browser. The company is screened on
        // the server (/api/live-screen, with the people) and its result stays there.
      }
      else if (currentStage === "regulatory" && (rl.includes("own account") || rl.includes("external clients") || rl.includes("client type") || rl.includes("third parties") || rl.includes("formally classify"))) {
        setStage("classification"); setStageProgress(p => ({...p, regulatory: true}));
        // Trigger FCA Register verification if FRN mentioned
        const frn = updatedData.licenceNumber || updatedData.prefilled_licenceNumber;
        const regulator = (updatedData.regulatoryBody || updatedData.prefilled_regulatoryBody || '').toLowerCase();
        if (frn && regulator.includes('fca')) {
          runExternalVerify('fca_register', frn, updatedData.companyName || updatedData.prefilled_companyName);
        }
        // FATF jurisdiction check
        const juris = updatedData.jurisdiction || updatedData.prefilled_jurisdiction;
        if (juris) runExternalVerify('fatf', juris, null);
      }
      else if (currentStage === "classification" && (rl.includes("external client") || rl.includes("own account") || rl.includes("aml") || rl.includes("proprietary") || rl.includes("anti-money") || rl.includes("compliance framework") || rl.includes("money laundering") || rl.includes("domiciliary") || rl.includes("tax") || rl.includes("politically exposed"))) { setStage("aml"); setStageProgress(p => ({...p, classification: true})); }
      else if (currentStage === "aml" && (rl.includes("source of wealth") || rl.includes("wealth") || rl.includes("accumulated") || rl.includes("net worth") || rl.includes("personal assets"))) { setStage("sow"); setStageProgress(p => ({...p, aml: true})); }
      else if (currentStage === "sow" && (rl.includes("source of funds") || rl.includes("funding") || rl.includes("bank account") || rl.includes("funds come from") || rl.includes("deposited") || rl.includes("transfer") || rl.includes("which account") || rl.includes("named account"))) { setStage("sof"); setStageProgress(p => ({...p, sow: true})); }
      else if (currentStage === "sof" && (rl.includes("document") || rl.includes("last step") || rl.includes("few documents") || rl.includes("upload") || rl.includes("certificate") || rl.includes("incorporation") || rl.includes("register") || rl.includes("evidence") || rl.includes("provide") || rl.includes("attach"))) { setStage("documents"); setStageProgress(p => ({...p, sof: true})); }
      else if (currentStage === "documents" && (rl.includes("summarise") || rl.includes("summary") || rl.includes("before i pass") || rl.includes("compliance team") || rl.includes("run through") || rl.includes("recap") || rl.includes("review everything") || rl.includes("check everything") || rl.includes("confirm the following"))) {
        // Only advance to summary if this was triggered by Alex (not a user upload auto-trigger)
        // and the conversation shows documents stage is reasonably complete
        setStage("summary"); setStageProgress(p => ({...p, documents: true}));
      }

    } catch(e) { setMessages(prev => [...prev, { role: "assistant", content: "Error: " + e.message }]); }
    setLoading(false);
  };

  const handleFileUpload = async (e) => {
    // Upload fix batch (15/10/2026), fault f: the one shared type list and 30 MB limit.
    // A refused file is named with its reason; the others still upload.
    const { ok: files, refused: refusedLines } = splitUploads(e.target.files);
    // Reset the input so the same file can be uploaded again if needed
    e.target.value = '';
    if (refusedLines.length) {
      const refusalMsg = (refusedLines.length === 1 ? refusedLines[0] : 'I could not take these files:\n' + refusedLines.join('\n'))
        + (files.length ? '\n\nThe others are on their way.' : '');
      setMessages(prev => [...prev, { role: "assistant", content: refusalMsg }]);
      try { await saveMessage("assistant", refusalMsg); } catch (_) {}
    }
    if (!files.length) { uploadForItemRef.current = null; return; }

    // Uploads to an already-submitted file are UPDATES the compliance team
    // must see: badge the case and send one throttled notification per hour.
    const isPostSubmission = submitted || stage === 'mlro_followup' || !!collectedData?.submittedAt;
    if (isPostSubmission && files.length && sessionIdRef.current) {
      try {
        const lastNotify = collectedData?.updateNotifyAt ? new Date(collectedData.updateNotifyAt).getTime() : 0;
        const throttleOk = (Date.now() - lastNotify) > 60 * 60 * 1000;
        const upd = { has_updates: true, last_update_type: 'documents_added', last_update_at: new Date().toISOString() };
        if (throttleOk) {
          // updateSession replaces collected_data wholesale, so writing it from local state risks
          // overwriting whatever the compliance team has since put on the case: requested
          // questions, response verdicts, acknowledged risk factors. Re-read the server copy and
          // merge onto that, and if the read fails do not write the blob at all — the
          // notification throttle stamp is not worth the risk of losing case data.
          const _fresh = await clientRead('session', sessionIdRef.current, tokenRef.current, 'collected_data');
          const _serverCd = _fresh?.data?.collected_data;
          if (_serverCd && typeof _serverCd === 'object') {
            const _at = new Date().toISOString();
            upd.collected_data = { ..._serverCd, updateNotifyAt: _at };
            setCollectedData(prev => ({ ...prev, updateNotifyAt: _at }));
          } else {
            console.error('Skipping collected_data write on upload: could not read the current server copy.');
          }
          fetch('/api/notify', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ updateType: 'documents_added', clientName: collectedData?.contactName || '', companyName: collectedData?.companyName || collectedData?.prefilled_companyName || '', contactEmail: collectedData?.contactEmail || '', sessionId: sessionIdRef.current, token: tokenRef.current }) }).catch(() => {});
        }
        updateSession(upd).catch(() => {});
      } catch (_) {}
    }

    // A file over the shared limit is refused above (splitUploads) with its own advice, so
    // nothing reaching here is over it; the old advice block is kept for a limit change.
    const MODEL_READABLE_MAX = 30 * 1024 * 1024;
    const oversized = files.filter(f => f.size > MODEL_READABLE_MAX);
    const preWarned = new Set(oversized.map(f => f.name));
    if (oversized.length) {
      const lines = oversized.map(f => `${f.name} (${(f.size / (1024 * 1024)).toFixed(0)}MB)`).join(', ');
      const advice = `Just so you know before I start: ${oversized.length === 1 ? lines + ' is' : lines + ' are'} larger than I can read in one go, so I will not be able to check ${oversized.length === 1 ? 'it' : 'them'} against the rest of your file automatically.\n\nThe easiest fixes, whichever suits you:\nSplit it into two or three smaller files and send those\nSend only the part we need, for example the most recent six months of a statement\nIf it is a scan, re-save or re-scan it at a lower resolution, which usually shrinks it a great deal\n\nI will still keep what you send now, so nothing is wasted.`;
      setMessages(prev => [...prev, { role: "assistant", content: advice }]);
      try { await saveMessage("assistant", advice); } catch (_) {}
    }

    // Process files SEQUENTIALLY — one at a time, waiting for each auto-trigger to complete
    // before starting the next. This prevents multiple simultaneous Alex responses.
    let fileIndex = 0;
    // v1_923: the items as this batch leaves them, so Alex's next turn reads an
    // item the upload answered as answered (collectedData here is the render's copy).
    let _itemsAfterUpload = Array.isArray(collectedData.requestItems) ? collectedData.requestItems : [];
    for (const file of files) {
      fileIndex++;
      setLoading(true);
      setDocProgress({ name: file.name, sizeMb: file.size / (1024 * 1024), phase: 'uploading', index: fileIndex, total: files.length, startedAt: Date.now() });
      try {

        // Storage FIRST, then analyse from storage. Analysis used to send the whole file through a
        // serverless function, which caps inbound request bodies at 4.5MB with no way to raise it;
        // base64 inflates a file by a third, so anything over roughly 3.2MB could not be read. That
        // ceiling was self-imposed rather than a limit on reading documents. Uploading first and
        // analysing by path removes it entirely and the real limit becomes the model's own.
        // The ordering matters for a second reason: if the upload fails we still want the analysis
        // attempt, and if the analysis fails we still want the file.
        let storedPath = null;
        try {
          storedPath = await uploadToStorage(file);
        } catch (upEx) {
          // Not fatal here: analysis can still run inline for a small file, so the client is not
          // blocked. It does mean a large document cannot be read, which the analysis step reports.
          console.error('Storage upload failed, falling back to inline analysis:', upEx.message);
        }

        // Only read the file into memory for the inline fallback, which needs base64. A large file
        // does not need to be held in browser memory at all when it is analysed from storage.
        let base64 = null;
        if (!storedPath) {
          base64 = await new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = ev => resolve(ev.target.result.split(",")[1]);
            reader.onerror = () => reject(new Error("File read failed"));
            reader.readAsDataURL(file);
          });
        }

        setDocProgress(prev => prev ? { ...prev, phase: 'reading' } : prev);
        const analysis = await analyzeDocument(file.name, base64, file.type, JSON.stringify(collectedData, null, 2), storedPath);
        setDocProgress(prev => prev ? { ...prev, phase: 'saving' } : prev);

        // Update uploaded docs state and save to DB
        const newDoc = { name: file.name, analysis, uploadedAt: new Date().toISOString() };
        setUploadedDocs(prev => [...prev, newDoc]);
        const saved = await saveDocument(file.name, analysis.docType, analysis.assessment, analysis, file.size, storedPath ? null : file, file.type, storedPath);
        // v1_900: no record, no receipt and no item marked. The catch below tells
        // the client the file may not have reached their file and to try again.
        // Refused by the server from the file's content (a program renamed .pdf, an old .doc):
        // the client is told why, in the shared wording, not that something went wrong.
        if (saved && !saved.ok && saved.refused && saved.error) { uploadForItemRef.current = null; const rx = new Error(saved.error); rx.refusal = true; throw rx; }
        if (!saved || !saved.ok) { uploadForItemRef.current = null; throw new Error('the document record did not save (' + ((saved && saved.error) || 'unknown') + ')'); }
        if (uploadForItemRef.current) {
          const _itemId = uploadForItemRef.current; uploadForItemRef.current = null;
          try {
            const r = await fetch('/api/request-items', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ sessionId: sessionIdRef.current, token: tokenRef.current, action: 'answered', itemId: _itemId, evidence: file.name, documentId: saved.id }) });
            const b = await r.json().catch(() => ({}));
            if (r.ok && b.item) {
              _itemsAfterUpload = (_itemsAfterUpload || []).map(x => x.id === b.item.id ? { ...x, state: b.item.state } : x);
              setCollectedData(prev => ({ ...prev, requestItems: (prev.requestItems || []).map(x => x.id === b.item.id ? { ...x, state: b.item.state } : x) }));
              if (typeof fetchClientStatus === 'function') fetchClientStatus();
            }
          } catch (_) {}
        }

        // Flag post-submission document activity for MLRO dashboard
        if (isReturningClient) {
          await updateSession({
            has_updates: true,
            last_update_type: "document",
            last_update_at: new Date().toISOString()
          });
        }

        // Show receipt confirmation. Where the automated read was skipped, say so plainly: the
        // document IS on file, so "please try again" would be wrong and would send the client
        // round a loop that cannot succeed.
        const responseMsg = !analysis.analysisSkipped
          ? "Thank you, I have received " + file.name + "."
          : preWarned.has(file.name)
          ? "Thank you, " + file.name + " is now safely on your file. As mentioned, I have not been able to read this one, so a smaller version whenever convenient would let me check it properly."
          : analysis.analysisSkippedReason === 'too_many_pages_for_model'
            ? "Thank you, I have received " + file.name + " and it is safely on your file. At " + (analysis.pageCount || 'this many') + " pages it is too long for me to read in one go, so that I can check it properly against the rest of your file could you send it as two or three smaller files, or just the sections we need? If it is a bank statement, the most recent six months is usually enough."
            : analysis.analysisSkippedReason === 'file_too_large_for_model'
              ? "Thank you, I have received " + file.name + " and it is safely on your file. It is larger than I can read in one go, so that I can check it properly against the rest of your file could you send it split into smaller files, or as a lower-resolution scan? Either works."
              : analysis.analysisSkippedReason === 'unsupported_format'
                ? "Thank you, I have received " + file.name + " and it is safely on your file. To read it cleanly could you send it as a PDF or a photo if you have one? That way I can check it against the rest of your file."
                : "Thank you, I have received " + file.name + " and it is safely on your file. It did not come through clearly enough for me to read this time, if you have it in another format, sending that would let me read it properly.";
        setMessages(prev => [...prev, { role: "assistant", content: responseMsg }]);
        await saveMessage("assistant", responseMsg);

        // Only trigger Alex's next question for the LAST file in the batch
        // For intermediate files, just acknowledge receipt and wait
        const isLastFile = file === files[files.length - 1];
        if (isLastFile) {
          // Build context of what was last requested by Alex
          const allMsgs = await new Promise(resolve => {
            setMessages(prev => { resolve(prev); return prev; });
          });
          const lastAlexMsg = allMsgs.filter(m => m.role === 'assistant').slice(-1)[0]?.content || '';
          const uploadedNames = files.map(f => f.name).join(", ");
          const batchNote = files.length > 1 ? `The client uploaded ${files.length} files in one batch: ${uploadedNames}.` : `The client uploaded "${file.name}".`;

          await new Promise(resolve => setTimeout(resolve, 800));

          // v1_923: receipt only. The old instruction told Alex to treat a point as
          // answered from the document and move to the summary if everything was in,
          // and it judged a bank statement as addressing the officer's concern (Fig
          // Markets, 04/10/26). The next question comes from what is still owed.
          const nextMsg = await callClaude(
            afterUploadTrigger({ batchNote, lastAlexMsg, followup: Array.isArray(collectedData.pendingMlroQuestions) && collectedData.pendingMlroQuestions.length > 0 }),
            stage, { ...collectedData, requestItems: _itemsAfterUpload }, uploadedDocs
          );
          if (nextMsg && nextMsg.trim()) {
            setMessages(p => [...p, { role: "assistant", content: nextMsg }]);
            await saveMessage("assistant", nextMsg);
          }
        }
      } catch(err) {
        console.error("File upload error for " + file.name + ":", err);
        setMessages(prev => [...prev, { role: "assistant", content: (err && err.refusal) ? err.message : "Something went wrong while handling " + file.name + " and it may not have reached your file. Please try that one again, and if it happens a second time let us know rather than keep retrying." }]);
      }
      setDocProgress(null);
      setLoading(false);
    }
  };

  const generateSowReport = async () => {
    setLoading(true);
    try {
      // 23/10/2026 (client screening exposure): THE REPORT IS WRITTEN ON THE SERVER
      // (api/client-sow-report.js) from the stored conversation: the summary, its
      // completion status, the parent's beneficial owner record, the case note and
      // the parent's adversarial review. The page is told only that it is done; the
      // assessment never reaches this browser and is never written from it.
      const doneRes = await fetch("/api/client-sow-report", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ sessionId: sessionIdRef.current, token: tokenRef.current })
      });
      const doneJson = await doneRes.json().catch(() => ({}));
      if (!doneRes.ok || !doneJson.ok) {
        alert("Your answers are safe, but the final confirmation could not be recorded. Please keep this page open and try the button again in a moment; if it keeps failing, simply close the page, the firm can see your responses and will follow up.");
        return;
      }

      setSubmitted(true);
      setMessages(prev => [...prev, {
        role: "assistant",
        content: `Thank you, we have everything we need. Your responses have been recorded and passed to our compliance team.

You don't need to do anything else. If you have any questions please contact us at ${supportEmail}`
      }]);
    } catch(e) {
      console.error("SOW report error:", e);
    }
    setLoading(false);
  };

  const generateReport = async () => {
    setLoading(true); setStage("report");
    // Owner (30/10/2026): the server writes the report, records the
    // submission, then reassesses the risk before it answers (for at most 90
    // seconds after the report), so the client is told plainly to wait,
    // without a promised time (review, 30/10/2026).
    setMessages(prev => [...prev, { role: "assistant", content: "Preparing your report and finishing your submission. This can take several minutes, so please keep this page open until it is done." }]);
    try {
      // Item d: THE REPORT IS PREPARED AND SAVED ON THE SERVER (api/client-report.js),
      // from the stored case, with the same prompt and the same finishing steps
      // (this visit's uploads, the website findings, the people carried, the
      // permission gaps, the wallets named). The page is sent back only the names,
      // the documents listed for the confirmation email and the wallets to screen,
      // never the tier, the risk factors, the red flags or the MLRO notes.
      const rr = await fetch('/api/client-report', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sessionId: sessionIdRef.current, token: tokenRef.current, turn: {
          data: collectedData,
          docs: uploadedDocs.map(d => ({ name: d.name, analysis: d.analysis, uploadedAt: d.uploadedAt })),
          history: messages.map(m => ({ role: m.role, content: m.content })),
          docsOnFile, isSowInterview, interviewEverFull: interviewEverFullRef.current
        } })
      });
      const rj = await rr.json().catch(() => ({}));
      if (!rr.ok || !rj.ok || !rj.summary) throw new Error(rj.error || ('the report could not be prepared (status ' + rr.status + ')'));
      const parsed = rj.summary;
      const mergedWallets = Array.isArray(parsed.walletAddresses) ? parsed.walletAddresses : (collectedData.walletAddresses || []);
      if (mergedWallets.length > 0) setCollectedData(prev => ({ ...prev, walletAddresses: mergedWallets }));
      setReport(parsed);
      // Save completed report to DB
      const submittedAtStamp = collectedData.submittedAt || new Date().toISOString();
      // A submission that closes an MLRO follow-up is an UPDATE to a reviewed
      // file, not a new onboarding — badge it and notify it as such.
      const wasFollowup = !!(collectedData?.pendingMlroQuestions?.length || collectedData?.mlroQuestionsDeliveredAt);
      await updateSession({
        status: 'completed',
        company_name: parsed.clientName,
        collected_data: { ...collectedData, ...(mergedWallets.length > 0 ? { walletAddresses: mergedWallets } : {}), submittedAt: submittedAtStamp },
        ...(wasFollowup ? { has_updates: true, last_update_type: 'mlro_followup_completed', last_update_at: new Date().toISOString() } : {})
      });
      setCollectedData(prev => ({ ...prev, submittedAt: submittedAtStamp }));
      // Load the status panel against the now-saved completed session.
      fetchClientStatus();
      // If this submission completed an MLRO follow-up, clear the follow-up flags so a later
      // visit does not re-open it. The answers are captured in the messages and the new report.
      if (wasFollowup) {
        await updateSession({ collected_data: { ...collectedData, ...(mergedWallets.length > 0 ? { walletAddresses: mergedWallets } : {}), submittedAt: submittedAtStamp, pendingMlroQuestions: [], mlroQuestionsDeliveredAt: null, mlroFollowupSubmittedAt: new Date().toISOString() } });
      }
      // Send email notification to MLRO team
      try {
        await fetch("/api/notify", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            updateType: wasFollowup ? 'followup_completed' : undefined,
            clientName: parsed.contactName,
            companyName: parsed.clientName,
            contactEmail: parsed.contactEmail,
            // Item d: the tier and recommendation are read from the stored report by notify.
            sessionId: sessionIdRef.current,
            token: tokenRef.current
          })
        });
      } catch(e) { console.error("Notification error:", e); }
      // v1_691: PROPORTIONALITY PASS, server side, before the officer sees the
      // request. It tightens the saved report's gap list and logs one case note.
      // Row 6 (20/10/2026): the page is told only whether it ran, never the list
      // (the analysis's gaps never reach the client, v1_921). Never blocks.
      try {
        await fetch("/api/proportionality", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ sessionId: sessionIdRef.current, token: tokenRef.current })
        });
      } catch(e) { console.error("Proportionality pass error:", e); }

      // Send confirmation email to client
      try {
        await fetch("/api/confirm-submission", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          // fix-client-email-recipient (19/10/2026): the server sends it to the
          // address on the invitation, with the case's name and company. Row 6
          // (20/10/2026): the outstanding list is the case's own officer sent items.
          body: JSON.stringify({
            documentsReceived: parsed.documentsReceived,
            inviteToken: collectedData.inviteToken || null
          })
        });
      } catch(e) { console.error("Confirmation email error:", e); }

      // LAYER 2 — Trigger adversarial review and cross-document analysis in background
      // These run asynchronously — do not await, do not block the client experience
      const currentSessionId = sessionIdRef.current;
      const currentMessages = messages;
      setTimeout(async () => {
        try {
          // Fetch documents and screenings for the adversarial review
          const [docsRes, screenRes0] = await Promise.all([
            clientRead('documents', currentSessionId, tokenRef.current),
            clientRead('screenings', currentSessionId, tokenRef.current)
          ]);
          const docs = docsRes.data || [];
          const existingScreenings = screenRes0?.data?.wallets || [];
          const sumsub = screenRes0?.data?.sumsub || [];

          // LAYER 3 — Auto-screen all wallet addresses Alex collected during the interview
          // (the report's Travel Rule wallets and any address in its source of funds text).
          // Item d: the server lists them from the report it saved (walletCandidates).
          const walletAddresses = Array.isArray(parsed.walletCandidates) ? parsed.walletCandidates : [];

          const alreadyScreened = existingScreenings.map(s => (s.address || '').toLowerCase());

          // v1_782: screened SERVER SIDE. The portal sends the addresses it
          // collected and gets back only which were screened; it never calls
          // Chainalysis, never holds a risk level, score or exposure, and never
          // writes the screening or its case note itself. A failure is logged here
          // and recorded on the case by the server (never silent).
          const _toScreen = walletAddresses
            .map(w => ({ address: (w.address || '').trim(), asset: w.asset || null, label: w.exchangeName || null }))
            .filter(w => w.address && !alreadyScreened.includes(w.address.toLowerCase()));
          if (_toScreen.length) {
            try {
              const _wsr = await fetch('/api/client-wallet-screen', {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ sessionId: currentSessionId, token: tokenRef.current, wallets: _toScreen })
              });
              const _wsd = await _wsr.json().catch(() => ({}));
              if (!_wsr.ok || (_wsd.failed || []).length) {
                try { captureError(new Error('Wallet screening incomplete: ' + (_wsd.error || ((_wsd.failed || []).length + ' not screened'))), { extra: { kind: 'wallet_screening_server', status: _wsr.status } }); } catch (_) {}
              }
            } catch (e) { console.error('Wallet screening request failed:', e); }
          }

          // v1_756: contradictions Alex raised and the client explained reach the
          // officer's Risk Factors as Raised in interview (fire and forget).
          fetch("/api/interview-inconsistencies", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ sessionId: currentSessionId, token: tokenRef.current, source: 'submit' }) }).catch(() => {});

          // Run adversarial review — fetches its own data server-side
          fetch("/api/adversarial-review", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ sessionId: currentSessionId, token: tokenRef.current })
          }).catch(e => console.error("Adversarial review error:", e));

          // Run cross-document reference if 2+ documents
          if (docs.length >= 2) {
            fetch("/api/cross-reference", {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              // Item d: the cross reference reads the stored report (the page no longer holds it).
              body: JSON.stringify({
                sessionId: currentSessionId,
                token: tokenRef.current
              })
            }).catch(e => console.error("Cross-reference error:", e));
          }
        } catch(e) { console.error("Background analysis error:", e); }
      }, 2000); // 2 second delay to ensure session is saved first

      // AUTO ACTIONS — send ID verification links and any required SOW
      // interview invites the moment the file is submitted (feature-gated per
      // firm, idempotent, fully logged to case notes). Slightly longer delay so
      // the completed report is committed first.
      setTimeout(() => {
        fetch('/api/auto-actions', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ sessionId: sessionIdRef.current, token: tokenRef.current }) })
          .then(() => fetchClientStatus())
          .catch(() => {});
      }, 4000);

      // Show thank you message to client - never show MLRO report
      setSubmitted(true);
      setMessages(prev => [...prev, { 
        role: "assistant", 
        content: "Your file has been submitted to our compliance team for review. You will receive a confirmation email shortly with a summary of your submission and our expected review timeline.\n\nEverything we discussed is saved to your secure checklist on this page. If our team needs anything further it will appear there, where you can upload documents, tell us the status of anything you're chasing, and, if an item is held by someone else, like your accountant or lawyer, pass it (or several at once) to them for us to collect. Your invitation link always brings you back here.\n\nIt has been a pleasure working through this with you. If you have any questions in the meantime, our team is available at " + supportEmail + "\n\n" + (brandName ? "Thank you for choosing " + brandName + ". " : "Thank you for your time. ") + "We look forward to working with you."
      }]);
    } catch(e) { setMessages(prev => [...prev, { role: "assistant", content: "Report error: " + e.message }]); }
    setLoading(false);
  };

  const resetSession = () => {
    if (window.confirm("Start a new client onboarding? This will clear the current session.")) {
      setStage("welcome"); setMessages([{ role: "assistant", content: welcomeMsg(brandName) }]);
      setInput(""); setCollectedData({}); setUploadedDocs([]);
      setReport(null); setStageProgress({});  // setView removed: never declared here, and this function is unreachable (scope-check, 11/08/26)
      sessionIdRef.current = null;
    }
  };

  const rc = tier => !tier ? "#888888" : tier.toLowerCase().includes("low") ? "#10b981" : tier.toLowerCase().includes("medium") ? "#f59e0b" : "#ef4444";
  // #246b: the client's document dot. Received is green; a document we may need
  // another copy of is one neutral colour, never amber or red; not yet read is grey.
  const docDot = assessment => { const v = clientDocVerdict(assessment); return v === CLIENT_DOC_RECEIVED ? "#10b981" : (!v || /^(pending|superseded)$/i.test(v)) ? "#888888" : "#7fa7c9"; };
  const activeStages = isIndividualClient ? INDIVIDUAL_STAGES : STAGES;
  const stageIndex = isIndividualClient ? Math.max(0, activeStages.indexOf(stage)) : STAGES.indexOf(stage);
  const progressPct = Math.round((stageIndex / (activeStages.length - 1)) * 100);
  const estMinutes = Math.max(1, Math.round((activeStages.length - 1 - stageIndex) * (isIndividualClient ? 10 : 5)));
  // Documents-first clients do not walk the staged interview, so the stage
  // progress bar and time estimate are meaningless for them and are hidden.
  const isDocsFirstChat = !!(collectedData && collectedData.documentsFirstChat);

  // Return-visit layout (v1_724): a submitted file that still has OPEN request
  // items to action gets a two-column layout — the checklist panel becomes the
  // primary, wider LEFT column and Alex sits in a narrower RIGHT column, so the
  // client acts on the panel directly and never has to leave it. First-visit
  // interviews and decided files are unchanged (they keep the floating panel).
  // v1_730: hold the two-column layout for the ENTIRE return visit on any
  // submitted, undecided file — not keyed on open item count. Answering the last
  // open item used to drop the count to zero and flip back to the chat-dominant
  // layout with the floating panel obscuring Alex's messages, mid-visit. The
  // panel is a submitted-family return (statusData is only loaded then), so the
  // presence of statusData plus an undecided status is the correct condition;
  // the checklist simply shows everything received once all items are answered.
  const twoColReturn = !!(statusData && !isSowInterview && statusData.status !== 'approved' && statusData.status !== 'declined');
  // One source of props for the status panel so the floating (right) and the
  // return-visit (left column) renders never drift. onToggle/collapsed are set
  // per placement: the floating panel can be hidden to a pill; the left-column
  // panel is the primary surface and is always open with no Hide control.
  const statusPanelProps = statusData ? {
    data: statusData,
    onResend: handleClientResend,
    resending: resendingItem,
    onRefresh: () => fetchClientStatus(),
    refreshing: statusRefreshing,
    supportEmail,
    payment: (statusData.paymentStatus === 'unpaid' && statusData.feeAmount) ? { amount: statusData.feeAmount, currency: statusData.feeCurrency || 'EUR', reference: (sessionIdRef.current || '').slice(0, 8).toUpperCase(), billing: (feeInfo && feeInfo.billing) || null, payeeName: (feeInfo && feeInfo.billing && feeInfo.billing.payeeName) || brandName || 'CompliantID' } : null,
    activity: caseActivity,
    feedback: (!statusData.experienceRating && !feedbackDismissed) ? { onRate: async (rating, comment) => { try { await updateSession({ experience_rating: rating, experience_comment: (comment || '').slice(0, 500) || null, experience_rated_at: new Date().toISOString() }); } catch (_) {} setStatusData(prev => prev ? { ...prev, experienceRating: rating } : prev); }, onDismiss: () => setFeedbackDismissed(true) } : null,
    requestItems: statusData.requestItems || [],
    sessionId: sessionIdRef.current,
    token: tokenRef.current,
    onItemsChanged: () => fetchClientStatus(),
    onUploadFor: (i) => { uploadForItemRef.current = i.id; fileInputRef.current?.click(); },
    firmQuestions: firmQuestionList.length ? { questions: firmQuestionList, collected: collectedData, onSaved: (answers) => setCollectedData(prev => ({ ...prev, firmQuestions: answers })) } : null
  } : null;

  // ── OTP-1 verification screen. Renders BEFORE the invitation-only lock:
  // this browser holds a genuine link and only needs to prove the inbox. ──
  if (tokenChecked && otpGate) {
    const gBrandName = (branding && branding.displayName) || null;
    const gBrandLogo = (branding && branding.logoUrl) || null;
    return (
      <div style={{ minHeight:"100vh", background:"#071727", display:"flex", alignItems:"center", justifyContent:"center", fontFamily:"'Inter',sans-serif", padding:24 }}>
        <style>{`@import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600&family=Playfair+Display:wght@700&display=swap');@keyframes spin{to{transform:rotate(360deg)}}`}</style>
        <div style={{ background:"#0a1f33", border:"1px solid rgba(132,241,237,0.15)", borderRadius:16, padding:"40px 44px", width:440, maxWidth:"100%", textAlign:"center" }}>
          <div style={{ display:"flex", alignItems:"center", justifyContent:"center", gap:14, marginBottom:24 }}>
            {gBrandLogo && <img src={gBrandLogo} alt={gBrandName || "logo"} style={{ maxHeight:44, maxWidth:160, objectFit:"contain", display:"block" }} />}
            <div style={{ fontFamily:"'Playfair Display',serif", fontSize:26, fontWeight:700, color:"#f5f5f0", letterSpacing:"0.02em" }}>{gBrandName || (!gBrandLogo && <>Compliant<span style={{ color:"#13a4a4" }}>ID</span></>)}</div>
          </div>
          <div style={{ fontSize:18, fontWeight:600, color:"#f5f5f0", marginBottom:8 }}>Confirm it's you</div>
          <div style={{ fontSize:13.5, color:"rgba(255,255,255,0.45)", lineHeight:1.7, marginBottom:24 }}>
            For your security this file is protected. We have emailed a 6-digit code to {otpGate.maskedEmail ? <span style={{ color:"#84f1ed" }}>{otpGate.maskedEmail}</span> : "the address this invitation was sent to"}. Enter it below to open your file.
          </div>
          <input
            value={otpCode}
            onChange={e => { setOtpCode(e.target.value.replace(/\D/g, "").slice(0, 6)); setOtpError(null); }}
            onKeyDown={e => { if (e.key === "Enter" && otpCode.length === 6) otpVerifyCode(); }}
            inputMode="numeric"
            autoComplete="one-time-code"
            autoFocus
            placeholder="••••••"
            style={{ width:"100%", background:"#071727", border:"1px solid rgba(132,241,237,0.25)", borderRadius:10, color:"#f5f5f0", fontSize:28, fontWeight:600, letterSpacing:"12px", textAlign:"center", padding:"14px 0 14px 12px", outline:"none", marginBottom:14 }}
          />
          {otpError && <div style={{ fontSize:12.5, color:"#f0a3a3", lineHeight:1.6, marginBottom:12 }}>{otpError}</div>}
          <button
            onClick={otpVerifyCode}
            disabled={otpBusy || otpCode.length !== 6}
            style={{ width:"100%", background: otpBusy || otpCode.length !== 6 ? "rgba(19,164,164,0.35)" : "#13a4a4", color:"#071727", border:"none", padding:"13px 0", borderRadius:8, fontWeight:600, fontSize:14.5, cursor: otpBusy || otpCode.length !== 6 ? "default" : "pointer", marginBottom:14 }}
          >{otpBusy ? "Verifying..." : "Open my file"}</button>
          <button
            onClick={otpRequestCode}
            disabled={otpResendIn > 0}
            style={{ background:"none", border:"none", color: otpResendIn > 0 ? "rgba(255,255,255,0.25)" : "#84f1ed", fontSize:13, cursor: otpResendIn > 0 ? "default" : "pointer", textDecoration:"underline" }}
          >{otpResendIn > 0 ? `Send a new code (${otpResendIn}s)` : "Send a new code"}</button>
          <div style={{ marginTop:18, fontSize:11.5, color:"rgba(255,255,255,0.25)", lineHeight:1.6 }}>The code expires after 10 minutes. Once verified, this browser stays verified for 30 days.</div>
        </div>
      </div>
    );
  }

  // Gate — show locked screen if no valid token
  if (tokenChecked && !tokenValid) {
    return (
      <div style={{ minHeight:"100vh", background:"#071727", display:"flex", alignItems:"center", justifyContent:"center", fontFamily:"'Inter',sans-serif" }}>
        <div style={{ background:"#071727", border:"1px solid rgba(132,241,237,0.15)", borderRadius:16, padding:"40px 48px", width:420, textAlign:"center" }}>
          <div style={{ fontFamily:"Playfair Display,serif", fontSize:28, fontWeight:700, color:"#f5f5f0", marginBottom:24, letterSpacing:"0.02em" }}>Compliant<span style={{ color:"#13a4a4" }}>ID</span></div>
          <div style={{ fontSize:18, fontWeight:600, color:"#f5f5f0", marginBottom:8 }}>{inviteExpired ? "This invitation link has expired" : "Onboarding by Invitation Only"}</div>
          <div style={{ fontSize:14, color:"rgba(255,255,255,0.4)", lineHeight:1.7, marginBottom:28 }}>{inviteExpired ? "For security, unused invitation links expire after a period of time. Please contact the team that invited you and they will send a fresh link." : "Access to this onboarding portal is by invitation only. Please contact us to request access."}</div>
          <a href="mailto:compliance@compliantid.com" target="_blank" rel="noreferrer" style={{ display:"inline-block", background:"#13a4a4", color:"#071727", padding:"12px 28px", borderRadius:8, textDecoration:"none", fontWeight:600, fontSize:14 }}>Contact Us</a>
          <div style={{ marginTop:20, fontSize:12, color:"rgba(255,255,255,0.2)" }}>compliance@compliantid.com</div>
        </div>
      </div>
    );
  }

  if (!tokenChecked) {
    return (
      <div style={{ minHeight:"100vh", background:"#071727", display:"flex", alignItems:"center", justifyContent:"center" }}>
        <div style={{ width:32, height:32, border:"2px solid #84f1ed", borderTopColor:"transparent", borderRadius:"50%", animation:"spin 0.8s linear infinite" }} />
        <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
      </div>
    );
  }

  // Documents-first client screen — intake routing, tailored checklist, then upload.
  // Rendered only for documents_first invites; the normal onboarding UI is untouched.
  if (isDocumentsFirst) {
    const dfNextQuestion = DF_QUESTIONS.find(q => dfAnswers[q.key] === undefined);
    const dfAnsweredCount = DF_QUESTIONS.filter(q => dfAnswers[q.key] !== undefined).length;
    const dfChecklist = dfBuildChecklist(dfAnswers, dfFirmChecklist);
    const dfCard = (children) => (
      <div style={{ minHeight:"100vh", background:"#071727", color:"#e8e8e0", fontFamily:"'Inter','Helvetica Neue',sans-serif", display:"flex", alignItems:"center", justifyContent:"center", padding:"24px" }}>
        <style>{`@import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600&family=Playfair+Display:wght@700&display=swap');*{box-sizing:border-box;margin:0;padding:0}@keyframes spin{to{transform:rotate(360deg)}}`}</style>
        <div style={{ background:"#0a1f33", border:"1px solid rgba(132,241,237,0.15)", borderRadius:16, padding:"40px 44px", width:580, maxWidth:"100%" }}>
          <div style={{ marginBottom:24, display:"flex", alignItems:"center", gap:14 }}>{brandLogo && <img src={brandLogo} alt={brandName || "logo"} style={{ maxHeight:44, maxWidth:160, objectFit:"contain", display:"block" }} />}<div style={{ fontFamily:"'Playfair Display',serif", fontSize:24, fontWeight:700, color:"#f5f5f0", letterSpacing:"0.02em" }}>{brandName || (!brandLogo && <>Compliant<span style={{ color:"#13a4a4" }}>ID</span></>)}</div></div>
          {children}
        </div>
      </div>
    );

    if (dfDone) {
      // v1_813: after the decision the card says the outcome (clientOutcome),
      // before it the documents are with the team, as before.
      const dfOutcome = clientOutcome(statusData && statusData.status, statusData && statusData.welcomeSent, dfContact.email);
      if (dfOutcome) {
        return dfCard(
          <div data-client-outcome={dfOutcome.tone}>
            <div style={{ fontSize:19, fontWeight:600, color: dfOutcome.tone === 'approved' ? "#84f1ed" : "#f5f5f0", marginBottom:10 }}>{dfOutcome.title}</div>
            <div style={{ fontSize:14, color:"rgba(232,232,224,0.7)", lineHeight:1.7 }}>
              {dfOutcome.body}
            </div>
            <div style={{ fontSize:12, color:"rgba(232,232,224,0.45)", lineHeight:1.6, marginTop:16 }}>
              {dfBoard?.clientName || dfContact.company}
            </div>
          </div>
        );
      }
      return dfCard(
        <div>
          <div style={{ fontSize:19, fontWeight:600, color:"#f5f5f0", marginBottom:10 }}>Thank you, your documents are with the compliance team</div>
          <div style={{ fontSize:14, color:"rgba(232,232,224,0.55)", lineHeight:1.7, marginBottom: (feeInfo && feeInfo.paymentStatus === 'unpaid') ? 18 : 0 }}>
            We have received and begun reviewing the documents for <strong style={{ color:"#e8e8e0" }}>{dfBoard?.clientName || dfContact.company}</strong>. The team will review everything and come back to you at <strong style={{ color:"#e8e8e0" }}>{dfContact.email || "the email you provided"}</strong>, including any further information or clarification that may be needed.
          </div>
          {feeInfo && feeInfo.paymentStatus === 'unpaid' && (
            <FeePaymentCard
              amount={feeInfo.amount}
              currency={feeInfo.currency}
              reference={(sessionIdRef.current || '').slice(0, 8).toUpperCase()}
              billing={feeInfo.billing}
              payeeName={(feeInfo.billing && feeInfo.billing.payeeName) || brandName || 'CompliantID'}
            />
          )}
        </div>
      );
    }

    if (dfStep === 'fee' && feeInfo && feeInfo.mode === 'schedule') {
      const cur = (feeInfo.currency || 'GBP').toUpperCase();
      const sym = cur === 'GBP' ? '\u00a3' : cur === 'EUR' ? '\u20ac' : cur + ' ';
      const sch = feeInfo.schedule || {};
      const fmt = (n) => Number(n) > 0 ? sym + Number(n).toLocaleString('en-GB') : 'No fee';
      return dfCard(
        <div>
          <div style={{ fontSize:12, color:"#84f1ed", fontWeight:600, letterSpacing:".04em", textTransform:"uppercase", marginBottom:8 }}>Before you begin</div>
          <div style={{ fontSize:19, fontWeight:600, color:"#f5f5f0", marginBottom:12 }}>Your compliance file, and how the fee works</div>
          <div style={{ fontSize:13.5, color:"rgba(232,232,224,0.65)", lineHeight:1.7, marginBottom:14 }}>
            Onboarding here produces more than an approval. By the end of this process, your company's ownership, activity and source of funds will be documented in one structured, verified compliance file: complete, consistent, and organised to the standard regulated firms are expected to evidence. Most firms have never had this in one place, and assembling it is the bulk of the work we do.
          </div>
          <div style={{ fontSize:13.5, color:"rgba(232,232,224,0.65)", lineHeight:1.7, marginBottom:14 }}>
            A case preparation fee applies, based on your review category. Your category is not selected, by you or by us. It is determined by our <a href={'/api/methodology?sessionId=' + encodeURIComponent(sessionIdRef.current || '') + '&token=' + encodeURIComponent(tokenRef.current || '')} target="_blank" rel="noopener noreferrer" style={{ color:"#84f1ed", textDecoration:"underline" }}>published methodology</a> from your completed submission, because it reflects the verification work your company's profile genuinely requires. Simpler structures are never asked to subsidise complex ones. The methodology is open for you to read, so the basis of your category is checkable, not asserted.
          </div>
          <div style={{ margin:"0 0 16px", border:"1px solid rgba(132,241,237,0.2)", borderRadius:9, overflow:"hidden" }}>
            {[['Standard review', sch.standard], ['Extended review', sch.extended], ['Enhanced review', sch.enhanced]].map(([label, amt], i) => (
              <div key={i} style={{ display:"flex", justifyContent:"space-between", padding:"11px 16px", borderTop: i ? "1px solid rgba(132,241,237,0.12)" : "none", background:"rgba(19,164,164,0.05)" }}>
                <span style={{ fontSize:13.5, color:"rgba(232,232,224,0.8)" }}>{label}</span>
                <span style={{ fontSize:13.5, fontWeight:600, color:"#f5f5f0" }}>{fmt(amt)}</span>
              </div>
            ))}
          </div>
          <div style={{ fontSize:12.5, color:"rgba(232,232,224,0.5)", lineHeight:1.7, marginBottom:16 }}>
            You will be told your category and any fee the moment your submission is complete, before any review work begins. The fee covers the compliance review itself and is payable whether or not a business relationship is ultimately established.
          </div>
          <button onClick={async () => {
            const at = new Date().toISOString();
            setFeeInfo(prev => prev ? { ...prev, acceptedAt: at } : prev);
            try { await updateSession({ fee_terms_accepted_at: at }); } catch (_) {}
            setDfStep('consent');
          }} style={{ width:"100%", padding:"13px", background:"#13a4a4", border:"none", borderRadius:9, color:"#071727", fontSize:14.5, fontWeight:700, cursor:"pointer" }}>
            Accept and continue
          </button>
        </div>
      );
    }
    if (dfStep === 'fee' && feeInfo) {
      const svc = (feeInfo.billing && feeInfo.billing.serviceText) ||
        `Your compliance case will be prepared professionally: every document you provide is read in full, in any language, checked for authenticity and cross-referenced against official registers and public records. Named individuals are screened against global sanctions and PEP lists with adverse media checks, identities are verified, and where digital assets are involved, wallets are screened and Travel Rule requirements handled. The whole file is then assembled, checked for contradictions, and presented to the standard regulated counterparties expect. Most onboarding failures are failures of presentation, not substance. This process fixes the presentation.`;
      const payee = (feeInfo.billing && feeInfo.billing.payeeName) || brandName || 'CompliantID';
      return dfCard(
        <div>
          <div style={{ fontSize:12, color:"#84f1ed", fontWeight:600, letterSpacing:".04em", textTransform:"uppercase", marginBottom:8 }}>About this onboarding</div>
          <div style={{ fontSize:19, fontWeight:600, color:"#f5f5f0", marginBottom:10 }}>Your compliance case, prepared for you</div>
          <div style={{ fontSize:13.5, color:"rgba(232,232,224,0.6)", lineHeight:1.7, marginBottom:14, whiteSpace:"pre-wrap" }}>{svc}</div>
          <div style={{ padding:"14px 16px", background:"rgba(19,164,164,0.07)", border:"1px solid rgba(19,164,164,0.3)", borderRadius:10, marginBottom:14 }}>
            <div style={{ fontSize:12, color:"#84f1ed", fontWeight:600, marginBottom:4 }}>Case preparation fee</div>
            <div style={{ fontSize:22, fontWeight:700, color:"#f5f5f0" }}>{feeInfo.amount} {feeInfo.currency}</div>
            <div style={{ fontSize:12, color:"rgba(232,232,224,0.5)", lineHeight:1.6, marginTop:6 }}>
              Payable to {payee} once you have completed and submitted your file. Your case enters review when payment is received. Payment details will be shown at submission and sent to you by email.
            </div>
          </div>
          <div style={{ fontSize:12, color:"rgba(232,232,224,0.45)", lineHeight:1.7, marginBottom:18 }}>
            This fee covers the preparation of your compliance case. It has no bearing on the onboarding decision, which rests solely with the compliance officer reviewing your file.
          </div>
          <button onClick={async () => {
            const at = new Date().toISOString();
            setFeeInfo(prev => prev ? { ...prev, acceptedAt: at } : prev);
            // Recorded server-side immediately: the acceptance is the contractual
            // moment and must survive an abandoned browser session.
            try { await updateSession({ fee_terms_accepted_at: at }); } catch (_) {}
            setDfStep('consent');
          }} style={{ width:"100%", padding:"13px", background:"#13a4a4", border:"none", borderRadius:9, color:"#071727", fontSize:15, fontWeight:600, cursor:"pointer", fontFamily:"inherit" }}>
            Accept and continue
          </button>
          <div style={{ marginTop:14, fontSize:11.5, color:"rgba(232,232,224,0.3)", lineHeight:1.6, textAlign:"center" }}>By continuing you agree to the case preparation fee shown above.</div>
          {/* Counterparty transparency: when the fee is payable to CompliantID
              rather than the inviting firm, the client sees whose product they
              are buying, without the journey leaving the firm's brand. */}
          {/compliant\s*id/i.test(payee) && brandName && (
            <div style={{ marginTop:12, fontSize:11, color:"rgba(232,232,224,0.35)", textAlign:"center" }}>
              Payments are collected by <span style={{ fontWeight:700, color:"rgba(245,245,240,0.7)" }}>Compliant<span style={{ color:"#13a4a4" }}>ID</span></span>
            </div>
          )}
        </div>
      );
    }

    if (dfStep === 'consent') {
      return dfCard(
        <div>
          <div style={{ fontSize:12, color:"#84f1ed", fontWeight:600, letterSpacing:".04em", textTransform:"uppercase", marginBottom:8 }}>Before we begin</div>
          <div style={{ fontSize:19, fontWeight:600, color:"#f5f5f0", marginBottom:10 }}>How we use your information</div>
          <div style={{ fontSize:13.5, color:"rgba(232,232,224,0.6)", lineHeight:1.7, marginBottom:14 }}>
            {brandName || 'We'} {brandName ? 'is' : 'are'} required by anti money laundering law to verify the companies and people {brandName ? 'it does' : 'we do'} business with, and to understand the purpose of the relationship, before an account can be opened{dfContact.company ? <> for <strong style={{ color:"#e8e8e0" }}>{dfContact.company}</strong></> : null}. That obligation is not something either of us can waive.
          </div>
          <div style={{ fontSize:13.5, color:"rgba(232,232,224,0.6)", lineHeight:1.7, marginBottom:14 }}>
            The information and documents you provide are used to carry out those checks and to build the compliance record the law requires us to keep. We rely on our legal obligations, and our legitimate interest in preventing financial crime, as the basis for this, so we are not asking you to consent and you do not need to.
          </div>
          <div style={{ fontSize:13.5, color:"rgba(232,232,224,0.6)", lineHeight:1.7, marginBottom:14 }}>
            Identity checks are completed electronically. Where that applies, a photograph or short video of your face is compared against the photograph in your identity document to confirm you are the person it describes. That comparison is used for nothing else. If you would rather not complete an electronic check, tell us and we will agree another way of verifying your identity.
          </div>
          <div style={{ fontSize:13.5, color:"rgba(232,232,224,0.6)", lineHeight:1.7, marginBottom:18 }}>
            Records are kept for the period the law requires after the relationship ends. We may have to disclose information to regulators, law enforcement or a court, and in some circumstances the law prevents us from telling you that we have. Your rights, our retention periods and how to contact us about them are set out in the Privacy Policy.
          </div>
          <label style={{ display:"flex", alignItems:"flex-start", gap:10, marginBottom:18, cursor:"pointer", padding:"14px 16px", background:"rgba(255,255,255,0.03)", border:"1px solid rgba(132,241,237,0.15)", borderRadius:10 }}>
            <input type="checkbox" checked={dfConsent} onChange={e => setDfConsent(e.target.checked)} style={{ marginTop:3, accentColor:"#84f1ed", flexShrink:0, width:15, height:15, cursor:"pointer" }} />
            <span style={{ fontSize:12.5, color:"rgba(232,232,224,0.7)", lineHeight:1.7 }}>
              I confirm I have read and understood the{" "}
              <a href={privacyPolicyUrl} target="_blank" rel="noreferrer" style={{ color:"#84f1ed" }}>Privacy Policy</a>
              {" "}and that I am authorised to provide this information on the company's behalf.
            </span>
          </label>
          <button onClick={() => { if (!dfConsent) return; setDfConsentAt(new Date().toISOString()); setDfStep('financials'); }} disabled={!dfConsent}
            style={{ width:"100%", padding:"13px", background:"#13a4a4", border:"none", borderRadius:9, color:"#071727", fontSize:15, fontWeight:600, cursor: dfConsent ? "pointer" : "not-allowed", fontFamily:"inherit", opacity: dfConsent ? 1 : 0.4 }}>
            Continue
          </button>
          <div style={{ marginTop:14, fontSize:11.5, color:"rgba(232,232,224,0.3)", lineHeight:1.6, textAlign:"center" }}>Nothing you provide is collected or stored until you continue.</div>
        </div>
      );
    }

    // Financial declaration — its own step, AFTER the client has accepted the
    // terms (v1_647). Both bands are required before Continue is enabled.
    if (dfStep === 'financials') {
      const ready = !!(financialRevenueBand && financialAssetsBand);
      return dfCard(
        <div>
          <div style={{ fontSize:12, color:"#84f1ed", fontWeight:600, letterSpacing:".04em", textTransform:"uppercase", marginBottom:8 }}>Financial declaration</div>
          <div style={{ fontSize:19, fontWeight:600, color:"#f5f5f0", marginBottom:10 }}>The business's financial size</div>
          <FinancialRangeQuestions revenue={financialRevenueBand} assets={financialAssetsBand} onRevenue={setFinancialRevenueBand} onAssets={setFinancialAssetsBand} />
          <button onClick={() => { if (!ready) return; setDfStep('intake'); }} disabled={!ready}
            style={{ width:"100%", padding:"13px", background:"#13a4a4", border:"none", borderRadius:9, color:"#071727", fontSize:15, fontWeight:600, cursor: ready ? "pointer" : "not-allowed", fontFamily:"inherit", opacity: ready ? 1 : 0.4 }}>
            Continue
          </button>
          {!ready && <div style={{ marginTop:12, fontSize:11.5, color:"rgba(232,232,224,0.35)", lineHeight:1.6, textAlign:"center" }}>Please select a band for both figures to continue.</div>}
        </div>
      );
    }

    if (dfStep === 'intake' && dfNextQuestion) {
      return dfCard(
        <div>
          <div style={{ fontSize:12, color:"#84f1ed", fontWeight:600, letterSpacing:".04em", textTransform:"uppercase", marginBottom:8 }}>A few quick questions</div>
          <div style={{ fontSize:19, fontWeight:600, color:"#f5f5f0", marginBottom:6 }}>{dfNextQuestion.q}</div>
          <div style={{ fontSize:13, color:"rgba(232,232,224,0.4)", lineHeight:1.6, marginBottom:20 }}>This lets us tell you exactly which documents are needed. It takes under a minute.</div>
          <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
            {dfNextQuestion.options.map(opt => (
              <button key={opt.value} onClick={() => { const na = { ...dfAnswers, [dfNextQuestion.key]: opt.value }; setDfAnswers(na); if (!DF_QUESTIONS.find(q => na[q.key] === undefined)) setDfStep('upload'); }}
                style={{ textAlign:"left", padding:"13px 16px", background:"rgba(255,255,255,0.04)", border:"1px solid rgba(132,241,237,0.15)", borderRadius:9, color:"#e8e8e0", fontSize:14, cursor:"pointer", fontFamily:"inherit" }}>{opt.label}</button>
            ))}
          </div>
          <div style={{ marginTop:18, fontSize:12, color:"rgba(232,232,224,0.3)" }}>Question {dfAnsweredCount + 1} of {DF_QUESTIONS.length}</div>
        </div>
      );
    }

    return dfCard(
      <div>
        <div style={{ fontSize:19, fontWeight:600, color:"#f5f5f0", marginBottom:8 }}>Documents we need{dfContact.company ? ` from ${dfContact.company}` : ""}</div>
{dfPrefiled && prefiledFirmDocs.length > 0 && <PrefiledDocsCard docs={docsOnFile} onConfirm={confirmPrefiled} onReplace={replacePrefiled} busy={prefiledBusy || dfBusy} />}
                <div style={{ fontSize:13, color:"rgba(232,232,224,0.5)", lineHeight:1.6, marginBottom:16 }}>Based on your answers, please upload the items below. Don't worry if you don't have everything to hand: upload what you can and we will tell you if anything further is needed.</div>
        {dfPrefiled && prefiledFirmDocs.length > 0 && <div data-prefiled-checklist-note style={{ fontSize:12.5, color:"rgba(232,232,224,0.5)", lineHeight:1.6, marginTop:-8, marginBottom:14 }}>Items covered by the documents we already have do not need sending again. If everything is covered, you can continue without adding anything.</div>}
        <ul data-df-checklist style={{ listStyle:"none", margin:"0 0 18px", border:"1px solid rgba(132,241,237,0.12)", borderRadius:10, overflow:"hidden" }}>
          {coverChecklist(dfChecklist, prefiledFirmDocs).map(({ item, providedBy }, i) => (
            <li key={i} data-checklist-provided={providedBy || undefined} style={{ display:"flex", gap:10, padding:"10px 14px", fontSize:13.5, color: providedBy ? "rgba(232,232,224,0.5)" : "rgba(232,232,224,0.75)", lineHeight:1.5, borderBottom: i < dfChecklist.length - 1 ? "1px solid rgba(255,255,255,0.05)" : "none" }}>
              <span style={{ color:"#84f1ed", flexShrink:0 }}>{providedBy ? "✓" : "•"}</span>
              <span style={{ minWidth:0, overflowWrap:"anywhere" }}>{item}{providedBy && <span style={{ display:"block", fontSize:12.5, color:"#84f1ed" }}>Already provided: {providedBy}</span>}</span>
            </li>
          ))}
        </ul>
        <FirmQuestionsCard questions={firmQuestionList} collected={collectedData} sessionId={sessionIdRef.current} token={tokenRef.current} via="checklist"
          intro="We ask these of every client, whatever the business. You can answer them now, or later in the chat."
          onSaved={(answers) => setCollectedData(prev => ({ ...prev, firmQuestions: answers }))} />
        <div style={{ display:"flex", flexWrap:"wrap", alignItems:"center", gap:8, marginBottom:14 }}>
          <span data-df-list-address style={{ flex:"1 1 200px", minWidth:0, padding:"10px 12px", background:"rgba(255,255,255,0.04)", border:"1px solid rgba(132,241,237,0.2)", borderRadius:8, color:"rgba(232,232,224,0.75)", fontSize:13.5, fontFamily:"inherit", overflowWrap:"anywhere" }}>{dfContact.email ? `We will send it to ${dfContact.email}` : "We will send it to the email address on your invitation"}</span>
          <button type="button" onClick={() => emailDocList(dfChecklist)} disabled={dfListStatus === "sending"}
            style={{ flexShrink:0, padding:"10px 16px", background:"rgba(132,241,237,0.12)", border:"1px solid rgba(132,241,237,0.3)", borderRadius:8, color:"#84f1ed", fontSize:13.5, fontWeight:600, cursor: dfListStatus === "sending" ? "wait" : "pointer", fontFamily:"inherit" }}>
            {dfListStatus === "sending" ? "Sending ..." : "Email this list to me"}
          </button>
        </div>
        {dfListStatus && dfListStatus !== "sending" && (
          <div style={{ fontSize:12.5, marginBottom:16, color: dfListStatus.startsWith("sent:") ? "#84f1ed" : "#ff9aa2", lineHeight:1.6 }}>
            {dfListStatus.startsWith("sent:") ? `Sent to ${dfListStatus.slice(5)}. Check your inbox and return whenever you are ready.` : "Could not send just now. Please try again."}
          </div>
        )}
        <div style={{ fontSize:12, color:"rgba(232,232,224,0.4)", lineHeight:1.6, marginBottom:18 }}>Identity checks for directors and owners (passports) and any signatures are handled separately by secure link, so you do not need to upload IDs here. PDFs, photos, Word (.docx), Excel (.xlsx) and text files are accepted, up to {MAX_UPLOAD_LABEL} each.</div>
        <label
          onDragOver={e => { e.preventDefault(); if (!dfBusy) setDfDragOver(true); }}
          onDragEnter={e => { e.preventDefault(); if (!dfBusy) setDfDragOver(true); }}
          onDragLeave={e => { e.preventDefault(); setDfDragOver(false); }}
          onDrop={async e => {
            e.preventDefault();
            setDfDragOver(false);
            if (dfBusy) return;
            try { addDfFiles(await cidFilesFromDrop(e.dataTransfer)); }
            catch (err) { setDfDropNote('Those could not be read. Try the Choose files button instead.'); }
          }}
          style={{ display:"block", border: dfDragOver ? "1.5px solid #84f1ed" : "1.5px dashed rgba(132,241,237,0.35)", background: dfDragOver ? "rgba(132,241,237,0.07)" : "transparent", borderRadius:12, padding:"22px", textAlign:"center", cursor: dfBusy ? "not-allowed" : "pointer", background:"rgba(255,255,255,0.02)", opacity: dfBusy ? 0.5 : 1 }}>
          <input type="file" multiple accept={UPLOAD_ACCEPT} disabled={dfBusy} onChange={e => { addDfFiles(e.target.files); e.target.value = ''; }} style={{ display:"none" }} />
          <div style={{ fontSize:14, color:"#84f1ed", fontWeight:600 }}>{dfFiles.length ? `${dfFiles.length} file${dfFiles.length>1?"s":""} selected` : "Choose documents"}</div>
          <div style={{ fontSize:12, color:"rgba(232,232,224,0.35)", marginTop:4 }}>
            {dfDragOver ? "Drop them here" : "Drag files or a folder here, or click to choose. You can add more at any time before submitting."}
          </div>
        </label>
        <div style={{ display:"flex", alignItems:"center", gap:12, marginTop:10, flexWrap:"wrap" }}>
          <label style={{ fontSize:12.5, color:"#84f1ed", cursor: dfBusy ? "not-allowed" : "pointer", textDecoration:"underline" }}>
            <input type="file" multiple webkitdirectory="" directory="" disabled={dfBusy}
              onChange={e => { addDfFiles(e.target.files); e.target.value = ''; }} style={{ display:"none" }} />
            Or choose a whole folder
          </label>
          {dfFiles.length > 0 && !dfBusy && !dfFiles.some(f => (dfUploads.get(f) || {}).state === 'uploaded') && (
            <button type="button" onClick={() => { setDfFiles([]); setDfDropNote(''); setDfRefused([]); putDfUploads(new Map()); setDfStatus(''); }}
              style={{ background:"transparent", border:"none", color:"rgba(232,232,224,0.4)", fontSize:12.5, cursor:"pointer", fontFamily:"inherit", textDecoration:"underline" }}>
              Clear the list
            </button>
          )}
          {dfDropNote && <span style={{ fontSize:12.5, color:"rgba(232,232,224,0.5)" }}>{dfDropNote}</span>}
        </div>
        {dfRefused.length > 0 && (
          <ul data-upload-refused style={{ listStyle:"none", margin:"10px 0 0", padding:0 }}>
            {dfRefused.map((line, i) => (
              <li key={i} style={{ fontSize:12.5, color:"#ffb4a8", lineHeight:1.55, padding:"6px 10px", background:"rgba(255,120,100,0.07)", border:"1px solid rgba(255,120,100,0.2)", borderRadius:6, marginBottom:6, overflowWrap:"anywhere" }}>{line}</li>
            ))}
          </ul>
        )}
        {dfFiles.length > 0 && (
          <ul style={{ listStyle:"none", margin:"14px 0 0" }}>
            {dfFiles.map((f, i) => {
              // Owner decisions 1 and 2: each file shows its own state; a failed one
              // can be tried again or removed (a refused one only removed).
              const st = dfUploads.get(f) || null;
              const done = !!st && st.state === 'uploaded';
              const failed = !!st && st.state === 'failed';
              return (
              <li key={i} data-df-file-state={st ? st.state : 'waiting'} style={{ fontSize:13, color:"rgba(232,232,224,0.65)", padding:"7px 12px", background: failed ? "rgba(255,120,100,0.07)" : "rgba(255,255,255,0.04)", border: failed ? "1px solid rgba(255,120,100,0.3)" : "1px solid transparent", borderRadius:6, marginBottom:6 }}>
                <div style={{ display:"flex", justifyContent:"space-between" }}>
                  <span style={{ overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap", marginRight:10, minWidth:0 }}>{f.name}</span>
                  <span style={{ flexShrink:0, display:"flex", alignItems:"center", gap:10 }}>
                    {done ? <span style={{ color:"#84f1ed" }}>Uploaded</span> : <span style={{ color:"rgba(232,232,224,0.35)" }}>{(f.size/1024).toFixed(0)} KB</span>}
                    {!dfBusy && !st && (
                      <button type="button" title="Remove this file" onClick={() => setDfFiles(prev => prev.filter((_, idx) => idx !== i))}
                        style={{ background:"transparent", border:"none", color:"rgba(232,232,224,0.35)", cursor:"pointer", fontSize:14, lineHeight:1, padding:0, fontFamily:"inherit" }}>
                        &times;
                      </button>
                    )}
                  </span>
                </div>
                {failed && (
                  <div data-df-file-failed style={{ display:"flex", flexWrap:"wrap", alignItems:"center", gap:"6px 14px", marginTop:6 }}>
                    <span style={{ flex:"1 1 160px", minWidth:0, fontSize:12.5, color:"#ffb4a8" }}>{failedFileLabel(st.kind)}</span>
                    {retryHelps(st.kind) && (
                      <button type="button" data-df-retry disabled={dfBusy} onClick={() => retryDfFile(f)}
                        style={{ padding:"5px 12px", background:"rgba(132,241,237,0.12)", border:"1px solid rgba(132,241,237,0.3)", borderRadius:6, color:"#84f1ed", fontSize:12.5, fontWeight:600, cursor: dfBusy ? "not-allowed" : "pointer", fontFamily:"inherit" }}>
                        Try again
                      </button>
                    )}
                    <button type="button" data-df-remove disabled={dfBusy} onClick={() => removeDfFile(f)}
                      style={{ background:"transparent", border:"none", padding:0, color:"rgba(232,232,224,0.55)", fontSize:12.5, cursor: dfBusy ? "not-allowed" : "pointer", fontFamily:"inherit", textDecoration:"underline" }}>
                      Remove
                    </button>
                  </div>
                )}
              </li>
              );
            })}
          </ul>
        )}
        {dfBusy && (
          <div style={{ display:"flex", alignItems:"center", gap:10, marginTop:18, fontSize:13, color:"rgba(232,232,224,0.6)" }}>
            <div style={{ width:18, height:18, border:"2px solid #84f1ed", borderTopColor:"transparent", borderRadius:"50%", animation:"spin 0.8s linear infinite", flexShrink:0 }} />
            <span>{dfStatus || "Working ..."}</span>
          </div>
        )}
        {!dfBusy && dfStatus && (
          <div data-df-status style={{ marginTop:16, fontSize:13, color:"#ff9aa2", lineHeight:1.6, overflowWrap:"anywhere" }}>{dfStatus}</div>
        )}
        {dfFiles.some(f => (dfUploads.get(f) || {}).state === 'failed') && (
          <div data-df-reading-held style={{ marginTop:10, fontSize:12.5, color:"rgba(232,232,224,0.55)", lineHeight:1.6 }}>{readingHeldLine(dfFiles, f => dfUploads.get(f))}</div>
        )}
        <div style={{ marginTop:20, fontSize:12, color:"rgba(232,232,224,0.4)", lineHeight:1.7 }}>
          You accepted the{" "}
          <a href={privacyPolicyUrl} target="_blank" rel="noreferrer" style={{ color:"#84f1ed" }}>Privacy Policy</a>
          {" "}at the start of this session. The documents and information you provide are used for compliance onboarding purposes and may be shared with regulators where required.
        </div>
        <button onClick={() => runDocumentsFirst()} disabled={dfBusy || (!dfFiles.length && !(dfPrefiled && prefiledFirmDocs.length)) || !dfConsent || dfFiles.some(f => (dfUploads.get(f) || {}).state === 'failed')} style={{ width:"100%", marginTop:16, padding:"13px", background:"#13a4a4", border:"none", borderRadius:9, color:"#071727", fontSize:15, fontWeight:600, cursor: (dfBusy || (!dfFiles.length && !(dfPrefiled && prefiledFirmDocs.length)) || !dfConsent || dfFiles.some(f => (dfUploads.get(f) || {}).state === 'failed')) ? "not-allowed" : "pointer", fontFamily:"inherit", opacity: (dfBusy || (!dfFiles.length && !(dfPrefiled && prefiledFirmDocs.length)) || !dfConsent || dfFiles.some(f => (dfUploads.get(f) || {}).state === 'failed')) ? 0.4 : 1 }}>
          {dfBusy ? (dfRetrying ? "Uploading ..." : "Submitting ...") : (!dfFiles.length && dfPrefiled && prefiledFirmDocs.length) ? "Continue without adding documents" : "Submit documents for review"}
        </button>
        
      </div>
    );
  }

  // Consent gate for the standard onboarding flow. Previously consent was a checkbox rendered as a
  // chat bubble after Alex's opening message: it did block the input and the upload button, but it
  // was easy to scroll past, it sat inside the conversation rather than in front of it, and a
  // returning client whose consent had never been recorded was never asked at all. Documents-first
  // already gated properly; this brings the standard flow into line. Nothing the client provides
  // can be collected before they accept, and the acceptance is written with a timestamp so there
  // is an actual record rather than an inference.
  // The consent gate must render whenever the chat is live and consent is not recorded,
  // regardless of whether the status tracker has loaded. The `!statusData` condition created
  // a dead end: a returning client on a submitted file triggers fetchClientStatus(), which
  // populates statusData, so the gate did not render, while the composer stayed disabled on
  // `!consentGiven` with the placeholder asking them to accept a privacy notice they were
  // never shown. An established client asked for further information could not reply at all
  // and had no route to fix it (25/07/26).
  //
  // Consent is deliberately no longer inferred from the existence of messages, because that
  // treated any pre-consent-checkbox session as consented with nothing in the database to
  // evidence it. So an older session legitimately has no record, and the correct response is
  // to ask and record it properly rather than to lock the client out. Accepting merges
  // consentConfirmed and a timestamp onto the SERVER copy and leaves the conversation intact.
  if (!consentGiven && !submitted) {
    const consentCard = (children) => (
      <div style={{ minHeight:"100vh", background:"#071727", color:"#e8e8e0", fontFamily:"'Inter','Helvetica Neue',sans-serif", display:"flex", alignItems:"center", justifyContent:"center", padding:"24px" }}>
        <style>{`@import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600&family=Playfair+Display:wght@700&display=swap');*{box-sizing:border-box;margin:0;padding:0}`}</style>
        <div style={{ background:"#0a1f33", border:"1px solid rgba(132,241,237,0.15)", borderRadius:16, padding:"40px 44px", width:580, maxWidth:"100%" }}>
          <div style={{ marginBottom:24, display:"flex", alignItems:"center", gap:14 }}>{brandLogo && <img src={brandLogo} alt={brandName || "logo"} style={{ maxHeight:44, maxWidth:160, objectFit:"contain", display:"block" }} />}<div style={{ fontFamily:"'Playfair Display',serif", fontSize:24, fontWeight:700, color:"#f5f5f0", letterSpacing:"0.02em" }}>{brandName || (!brandLogo && <>Compliant<span style={{ color:"#13a4a4" }}>ID</span></>)}</div></div>
          {children}
        </div>
      </div>
    );
    const companyLabel = collectedData?.prefilled_companyName || collectedData?.companyName || '';
    return consentCard(
      <div>
        <div style={{ fontSize:12, color:"#84f1ed", fontWeight:600, letterSpacing:".04em", textTransform:"uppercase", marginBottom:8 }}>Before we begin</div>
        <div style={{ fontSize:19, fontWeight:600, color:"#f5f5f0", marginBottom:10 }}>How we use your information</div>
        <div style={{ fontSize:13.5, color:"rgba(232,232,224,0.6)", lineHeight:1.7, marginBottom:14 }}>
          {isSowInterview
            ? <>Anti-money-laundering law requires us to understand the background of the people who ultimately own or control the companies we do business with. That is why you have been asked to complete this short confidential profile, covering your career and how you came to hold your shareholding.</>
            : isIndividualClient
            ? <>Anti money laundering law requires us to verify the identity of the people we do business with, and to understand the purpose of the relationship and where the money comes from, before an account can be opened for you. That obligation is not something either of us can waive.</>
            : <>Anti-money-laundering law requires us to verify the companies and people we do business with, and to understand the purpose of the relationship, before an account can be opened{companyLabel ? <> for <strong style={{ color:"#e8e8e0" }}>{companyLabel}</strong></> : null}. That obligation is not something either of us can waive.</>}
        </div>
        <div style={{ fontSize:13.5, color:"rgba(232,232,224,0.6)", lineHeight:1.7, marginBottom:14 }}>
          The information and documents you provide are used to carry out those checks and to build the compliance record the law requires us to keep. We rely on our legal obligations, and our legitimate interest in preventing financial crime, as the basis for this, so we are not asking you to consent and you do not need to.
        </div>
        {!isSowInterview && (
          <div style={{ fontSize:13.5, color:"rgba(232,232,224,0.6)", lineHeight:1.7, marginBottom:14 }}>
            Identity checks are completed electronically. Where that applies, a photograph or short video of the person\u2019s face is compared against the photograph in their identity document, to confirm they are the person it describes. That comparison is used for nothing else. Anyone who would rather not complete an electronic check can tell us and we will agree another way of verifying their identity.
          </div>
        )}
        <div style={{ fontSize:13.5, color:"rgba(232,232,224,0.6)", lineHeight:1.7, marginBottom:18 }}>
          Records are kept for the period the law requires after the relationship ends. We may have to disclose information to regulators, law enforcement or a court, and in some circumstances the law prevents us from telling you that we have. Your rights, our retention periods and how to contact us about them are set out in the Privacy Policy.
        </div>
        {isReturningClient && (
          <div style={{ fontSize:12.5, color:"rgba(245,158,11,0.85)", lineHeight:1.6, marginBottom:16, padding:"11px 14px", background:"rgba(245,158,11,0.06)", border:"1px solid rgba(245,158,11,0.22)", borderRadius:8 }}>
            We do not have a record that this notice was shown to you on this file, so we are showing it once before you continue. Your progress has been saved and nothing has been lost.
          </div>
        )}
        <label style={{ display:"flex", alignItems:"flex-start", gap:10, marginBottom:18, cursor:"pointer", padding:"14px 16px", background:"rgba(255,255,255,0.03)", border:"1px solid rgba(132,241,237,0.15)", borderRadius:10 }}>
          <input type="checkbox" checked={standardConsentTicked} onChange={e => setStandardConsentTicked(e.target.checked)} style={{ marginTop:3, accentColor:"#84f1ed", flexShrink:0, width:15, height:15, cursor:"pointer" }} />
          <span style={{ fontSize:12.5, color:"rgba(232,232,224,0.7)", lineHeight:1.7 }}>
            I confirm I have read and understood the{" "}
            <a href={privacyPolicyUrl} target="_blank" rel="noreferrer" style={{ color:"#84f1ed" }}>Privacy Policy</a>
            {(isSowInterview || isIndividualClient) ? '.' : " and that I am authorised to provide this information on the company's behalf."}
          </span>
        </label>
        <button
          onClick={async () => {
            if (!standardConsentTicked || acceptingConsent) return;
            setAcceptingConsent(true);
            try {
              const at = new Date().toISOString();
              // Merge onto the SERVER copy, never onto local state. updateSession replaces
              // collected_data wholesale, and a returning client whose session read had failed
              // would otherwise have their entire onboarding record overwritten by a two-key
              // object at the moment they acknowledged this notice.
              let base = collectedData || {};
              if (sessionIdRef.current) {
                const fresh = await clientRead('session', sessionIdRef.current, tokenRef.current, 'collected_data');
                const serverCd = fresh?.data?.collected_data;
                if (serverCd && typeof serverCd === 'object') base = { ...serverCd, ...base };
                else if (isReturningClient) throw new Error('could not read existing file');
              }
              await updateSession({ collected_data: { ...base, consentConfirmed: true, consentTimestamp: at } });
              setCollectedData(prev => ({ ...(prev || {}), consentConfirmed: true, consentTimestamp: at }));
              setConsentGiven(true);
              // Source-of-wealth interviews open with a warm framing message rather than a cold
              // question. That auto-send used to live inside the old in-chat consent checkbox, so
              // it has to be carried over here or a UBO would land on an empty conversation.
              if (isSowInterview) {
                setLoading(true);
                try {
                  const opening = await callClaude("The client has confirmed consent and is ready to begin. Start warmly — briefly explain what you need to cover and why, mention the LinkedIn shortcut for career background, and ask your first question about how they came to hold their shareholding. Keep this opening message friendly and reassuring — set the tone that you are on their side.", "sow", collectedData, []);
                  const replyIsJson = opening.trim().startsWith('{') || opening.trim().startsWith('[');
                  if (!replyIsJson) {
                    setMessages(prev => [...prev, { role: "assistant", content: opening }]);
                    await saveMessage("assistant", opening);
                  }
                } catch (err) { console.error("SOW auto-prompt error:", err); }
                setLoading(false);
              }
            } catch (e) {
              // Consent must be RECORDED, not just accepted in the browser. If the write fails the
              // client stays on the gate rather than proceeding with nothing on the file.
              alert('We could not save your progress just now. Please check your connection and try again, or contact ' + supportEmail + '.');
            }
            setAcceptingConsent(false);
          }}
          disabled={!standardConsentTicked || acceptingConsent}
          style={{ width:"100%", padding:"13px", background:"#13a4a4", border:"none", borderRadius:9, color:"#071727", fontSize:15, fontWeight:600, cursor: standardConsentTicked && !acceptingConsent ? "pointer" : "not-allowed", fontFamily:"inherit", opacity: standardConsentTicked && !acceptingConsent ? 1 : 0.4 }}>
          {acceptingConsent ? 'One moment...' : 'Continue'}
        </button>
        <div style={{ marginTop:14, fontSize:11.5, color:"rgba(232,232,224,0.3)", lineHeight:1.6, textAlign:"center" }}>Nothing you provide is collected or stored until you continue.</div>
        <div style={{ marginTop:12, fontSize:11.5, color:"rgba(232,232,224,0.3)", lineHeight:1.6, textAlign:"center" }}>Questions before you start? <a href={"mailto:" + supportEmail} style={{ color:"#84f1ed" }}>{supportEmail}</a></div>
      </div>
    );
  }

  // FINANCIAL DECLARATION — its own step for the classic interview flow, shown
  // AFTER the client has accepted the terms and before the interview (v1_647).
  // Separate from consent; both bands required. Skipped for a source-of-wealth
  // interview, for documents-first (which has its own financials step), and once
  // the ranges are already on file (a returning client).
  if (consentGiven && !submitted && !isSowInterview && !isIndividualClient
      && !collectedData?.documentsFirstChat && collectedData?.onboardingMode !== 'documents_first'
      && !collectedData?.financialRanges && !financialsGiven) {
    const ready = !!(financialRevenueBand && financialAssetsBand);
    return (
      <div style={{ minHeight:"100vh", background:"#071727", color:"#e8e8e0", fontFamily:"'Inter','Helvetica Neue',sans-serif", display:"flex", alignItems:"center", justifyContent:"center", padding:"24px" }}>
        <style>{`@import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600&family=Playfair+Display:wght@700&display=swap');*{box-sizing:border-box;margin:0;padding:0}`}</style>
        <div style={{ background:"#0a1f33", border:"1px solid rgba(132,241,237,0.15)", borderRadius:16, padding:"40px 44px", width:580, maxWidth:"100%" }}>
          <div style={{ marginBottom:24, display:"flex", alignItems:"center", gap:14 }}>{brandLogo && <img src={brandLogo} alt={brandName || "logo"} style={{ maxHeight:44, maxWidth:160, objectFit:"contain", display:"block" }} />}<div style={{ fontFamily:"'Playfair Display',serif", fontSize:24, fontWeight:700, color:"#f5f5f0", letterSpacing:"0.02em" }}>{brandName || (!brandLogo && <>Compliant<span style={{ color:"#13a4a4" }}>ID</span></>)}</div></div>
          <div style={{ fontSize:12, color:"#84f1ed", fontWeight:600, letterSpacing:".04em", textTransform:"uppercase", marginBottom:8 }}>Financial declaration</div>
          <div style={{ fontSize:19, fontWeight:600, color:"#f5f5f0", marginBottom:10 }}>The business's financial size</div>
          <FinancialRangeQuestions revenue={financialRevenueBand} assets={financialAssetsBand} onRevenue={setFinancialRevenueBand} onAssets={setFinancialAssetsBand} />
          <button
            onClick={async () => {
              if (!ready || acceptingConsent) return;
              setAcceptingConsent(true);
              try {
                const at = new Date().toISOString();
                let base = collectedData || {};
                if (sessionIdRef.current) {
                  const fresh = await clientRead('session', sessionIdRef.current, tokenRef.current, 'collected_data');
                  const serverCd = fresh?.data?.collected_data;
                  if (serverCd && typeof serverCd === 'object') base = { ...serverCd, ...base };
                }
                const financialRanges = { annualRevenue: financialRevenueBand, totalAssets: financialAssetsBand, indicated: true, indicatedAt: at };
                await updateSession({ collected_data: { ...base, financialRanges } });
                setCollectedData(prev => ({ ...(prev || {}), financialRanges }));
                setFinancialsGiven(true);
              } catch (e) {
                alert('We could not save your progress just now. Please check your connection and try again, or contact ' + supportEmail + '.');
              }
              setAcceptingConsent(false);
            }}
            disabled={!ready || acceptingConsent}
            style={{ width:"100%", padding:"13px", background:"#13a4a4", border:"none", borderRadius:9, color:"#071727", fontSize:15, fontWeight:600, cursor: ready && !acceptingConsent ? "pointer" : "not-allowed", fontFamily:"inherit", opacity: ready && !acceptingConsent ? 1 : 0.4 }}>
            {acceptingConsent ? 'One moment...' : 'Continue'}
          </button>
          {!ready && <div style={{ marginTop:12, fontSize:11.5, color:"rgba(232,232,224,0.35)", lineHeight:1.6, textAlign:"center" }}>Please select a band for both figures to continue.</div>}
        </div>
      </div>
    );
  }

  return (
    <div style={{ height:"100vh", overflow:"hidden", background:"#071727", color:"#e8e8e0", fontFamily:"'Inter','Helvetica Neue',sans-serif", display:"flex", flexDirection:"column" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600&display=swap');
        *{box-sizing:border-box;margin:0;padding:0}
        ::-webkit-scrollbar{width:4px}::-webkit-scrollbar-track{background:#071727}::-webkit-scrollbar-thumb{background:#13a4a4;border-radius:2px;opacity:0.4}
        .msg{animation:fadeIn 0.3s ease forwards}@keyframes fadeIn{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)}}
        .dot{animation:pulse 2s infinite}@keyframes pulse{0%,100%{opacity:1}50%{opacity:0.4}}
        .progress-sweep{animation:sweep 1.4s ease-in-out infinite}@keyframes sweep{0%{transform:translateX(-100%)}100%{transform:translateX(300%)}}
        .as-line{animation:asRise 0.45s ease both}@keyframes asRise{from{opacity:0;transform:translateY(6px)}to{opacity:1;transform:translateY(0)}}
        .as-spin{animation:spin 0.9s linear infinite}
        .as-tick{stroke-dasharray:20;stroke-dashoffset:20;animation:asDraw 0.5s ease forwards}@keyframes asDraw{to{stroke-dashoffset:0}}
        .as-shimmer{background:linear-gradient(90deg,rgba(232,232,224,0.85) 40%,#84f1ed 50%,rgba(232,232,224,0.85) 60%);background-size:200% 100%;-webkit-background-clip:text;background-clip:text;-webkit-text-fill-color:transparent;animation:asShimmer 2.2s linear infinite}@keyframes asShimmer{0%{background-position:180% 0}100%{background-position:-20% 0}}
        .as-edge{animation:asGlow 2.4s ease-in-out infinite}@keyframes asGlow{0%,100%{opacity:0.45}50%{opacity:0.95}}
        .as-summary{animation:asLand 0.7s cubic-bezier(0.2,0.9,0.3,1.2) both}@keyframes asLand{from{opacity:0;transform:translateY(8px) scale(0.985)}to{opacity:1;transform:translateY(0) scale(1)}}
        @media (prefers-reduced-motion: reduce){.progress-sweep{animation-duration:3s}}
        textarea:focus,input:focus{outline:none}
        .send-btn:hover{background:#13a4a4!important;color:#071727!important}.send-btn:disabled{opacity:0.3;cursor:not-allowed}
        .tab:hover{border-color:#84f1ed!important;color:#84f1ed!important}
        .hex-bg{
          background-color:#071727;
          background-image:
            radial-gradient(ellipse 60% 50% at 80% 40%, rgba(19,164,164,0.07) 0%, transparent 70%),
            radial-gradient(ellipse 40% 40% at 20% 80%, rgba(19,164,164,0.05) 0%, transparent 70%),
            url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='52' height='60'%3E%3Cpolygon points='26,1 51,14 51,46 26,59 1,46 1,14' fill='none' stroke='%23c9a84c' stroke-width='0.3' opacity='0.08'/%3E%3C/svg%3E"),
            url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='52' height='60'%3E%3Ccircle cx='26' cy='30' r='1.5' fill='%23c9a84c' opacity='0.12'/%3E%3C/svg%3E");
          background-size:auto, auto, 52px 60px, 52px 60px;
          background-position:0 0, 0 0, 0 0, 26px 30px;
        }
        .mobile-progress{display:none}
        @media(max-width:768px){
          .desktop-sidebar{display:none!important}
          .mobile-progress{display:block}
          .hex-bg{margin-left:0!important}
          .chat-padding{padding:16px!important}
          .status-wrap{padding:12px 16px 0!important}
          .input-padding{padding:12px 16px 20px!important}
          .firm-questions-wrap{padding:0 16px!important}
          .header-title{font-size:13px!important}
          .header-sub{display:none!important}
          /* The chat header on a phone (owner 16/10/2026): the Powered by credit
             is never cut off. The tagline gives way, the side padding matches
             the chat's, and a long name ends in an ellipsis; a firm with a logo shows the
             logo alone. */
          .chat-header{padding:0 16px!important}
          .header-logo{min-width:0}
          .header-tagline{display:none!important}
          .header-brand-name{white-space:nowrap;overflow:hidden;text-overflow:ellipsis;min-width:0}
          .header-logo.has-logo .header-brand-name{display:none!important}
          .header-brand-logo{max-height:30px!important;max-width:120px!important}
          /* Return-visit two-column layout stacks on narrow screens: the checklist
             panel comes FIRST (full width), the chat below it, and the whole row
             scrolls as one instead of clipping under a fixed viewport height. */
          /* Return-visit two-column stacks on narrow screens but stays PINNED to
             the viewport (no whole-page scroll that loses the checklist off the
             top): the row keeps its fixed height and clips, the checklist takes
             the top slice and scrolls within itself, the chat fills the rest and
             scrolls within itself with the input fixed at its bottom. */
          .return-2col{flex-direction:column!important}
          .return-2col .return-panel-col{flex:0 0 auto!important;width:auto!important;min-width:0!important;max-width:none!important;max-height:42vh!important;border-right:none!important;border-bottom:1px solid rgba(132,241,237,0.1)!important}
          .return-2col .hex-bg{flex:1 1 auto!important;min-height:0!important;max-width:none!important}
        }
      `}</style>

      {/* Header */}
      <div className="chat-header" style={{ background:"#071727", borderBottom:"1px solid rgba(132,241,237,0.15)", padding:"0 32px", display:"flex", alignItems:"center", justifyContent:"space-between", height:64, flexShrink:0, position:"sticky", top:0, zIndex:100 }}>
        <div style={{ display:"flex", alignItems:"center", gap:12 }} className={"header-logo" + (brandLogo ? " has-logo" : "")}>
          {/* The firm's logo, as on the code entry and documents first screens
              (owner 16/10/2026). The CompliantID wordmark shows only when the
              firm has neither a display name nor a logo, the same rule. On a
              phone the logo stands alone and the tagline gives way (see
              .chat-header in the media query), so the credit is never cut off. */}
          {brandLogo && <img src={brandLogo} alt={brandName || "logo"} className="header-brand-logo" style={{ maxHeight:36, maxWidth:140, objectFit:"contain", display:"block", flexShrink:0 }} />}
          {brandName ? (
            <span className="header-brand-name" style={{ fontFamily:"'Playfair Display',serif", fontSize:20, fontWeight:700, color:"#f5f5f0", letterSpacing:"0.02em" }}>{brandName}</span>
          ) : brandLogo ? null : (
            <a href="https://www.compliantid.com" target="_blank" rel="noreferrer" title="Visit CompliantID" style={{ display:"flex", alignItems:"center", cursor:"pointer", textDecoration:"none" }}><span style={{ fontFamily:"'Playfair Display',serif", fontSize:20, fontWeight:700, color:"#f5f5f0", letterSpacing:"0.02em" }}>Compliant<span style={{ color:"#13a4a4" }}>ID</span></span></a>
          )}
<div className="header-tagline" style={{ fontSize:11, color:"#84f1ed", letterSpacing:"0.8px", textTransform:"uppercase", marginLeft:4 }}>{brandTagline}</div>
        </div>
        {!hidePoweredBy && <a href="https://compliantid.com" target="_blank" rel="noreferrer" style={{ display:"flex", alignItems:"center", gap:6, padding:"4px 10px 4px 7px", background:"rgba(255,255,255,0.04)", border:"1px solid rgba(255,255,255,0.1)", borderRadius:20, userSelect:"none", flexShrink:0, textDecoration:"none", cursor:"pointer" }}>
          <svg width="13" height="13" viewBox="0 0 13 13" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M6.5 1L2 3v3.5c0 2.5 1.9 4.8 4.5 5.5C9.1 11.3 11 9 11 6.5V3L6.5 1z" fill="rgba(132,241,237,0.15)" stroke="#84f1ed" strokeWidth="0.8" strokeLinejoin="round"/>
            <path d="M4.5 6.5l1.3 1.3 2.7-2.6" stroke="#84f1ed" strokeWidth="0.9" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          <span style={{ fontSize:10, color:"rgba(255,255,255,0.35)", fontFamily:"inherit", letterSpacing:"0.2px" }}>Powered by</span>
          <span style={{ fontSize:10, fontWeight:600, color:"rgba(132,241,237,0.7)", fontFamily:"inherit", letterSpacing:"0.3px" }}>CompliantID</span>
        </a>}

      </div>

      {/* Mobile-only progress bar: the sidebar (with the progress panel) is
          hidden under 768px, so this keeps completion visible on phones. */}
      {!isSowInterview && !submitted && consentGiven && stage !== "mlro_followup" && !statusData && (
        <div className="mobile-progress" style={{ position:"sticky", top:64, zIndex:99, height:3, background:"rgba(255,255,255,0.06)" }}>
          <div style={{ height:"100%", width:progressPct+"%", background:"linear-gradient(90deg,#13a4a4,#84f1ed)", transition:"width 0.5s ease" }} />
        </div>
      )}

      <div className={twoColReturn ? "return-2col" : ""} style={{ display:"flex", flex:"1 1 auto", minHeight:0, overflow:"hidden" }}>
          {/* Sidebar */}
          <div className="desktop-sidebar" style={{ width:220, background:"#071727", borderRight:"1px solid rgba(132,241,237,0.1)", padding:"24px 0", display: (isSowInterview || stage === "mlro_followup" || statusData) ? "none" : "flex", flexDirection:"column", overflowY:"auto", position:"fixed", top:64, left:0, height:"calc(100vh - 64px)", flexShrink:0, zIndex:10 }}>
            {!isDocsFirstChat && (
            <div style={{ padding:"0 16px 16px", borderBottom:"1px solid rgba(19,164,164,0.1)", marginBottom:8 }}>
              <div style={{ fontSize:11, color:"#888888", textTransform:"uppercase", letterSpacing:"0.8px", marginBottom:8 }}>Progress</div>
              <div style={{ height:4, background:"rgba(255,255,255,0.06)", borderRadius:2, overflow:"hidden" }}>
                <div style={{ height:"100%", width:progressPct+"%", background:"linear-gradient(90deg,#13a4a4,#84f1ed)", borderRadius:2, transition:"width 0.5s ease" }} />
              </div>
              <div style={{ fontSize:12, color:"#888888", marginTop:6, display:"flex", justifyContent:"space-between" }}>
                <span>{progressPct}% complete</span>
                {stage !== "report" && stage !== "summary" && <span>~{estMinutes} min left</span>}
              </div>
            </div>
            )}
            {!isDocsFirstChat && activeStages.filter(s => s !== "report").map((s, i) => {
              const isActive = s === stage;
              const isDone = stageProgress[s] || activeStages.indexOf(stage) > i;
              return (
                <div key={s} style={{ padding:"8px 16px", display:"flex", alignItems:"center", gap:10, background:isActive?"rgba(132,241,237,0.06)":"transparent", borderLeft:isActive?"3px solid #84f1ed":"3px solid transparent" }}>
                  <div style={{ width:20, height:20, borderRadius:"50%", border:"2px solid " + (isDone?"#13a4a4":isActive?"#84f1ed":"rgba(255,255,255,0.15)"), background:isDone?"#13a4a4":"transparent", display:"flex", alignItems:"center", justifyContent:"center", fontSize:10, color:"white", flexShrink:0 }}>
                    {isDone ? "✓" : isActive ? "●" : ""}
                  </div>
                  <span style={{ fontSize:13, color:isActive?"#84f1ed":isDone?"rgba(132,241,237,0.5)":"rgba(255,255,255,0.25)", fontWeight:isActive?500:400 }}>{STAGE_LABELS[s]}</span>
                </div>
              );
            })}
            {(uploadedDocs.length > 0 || stage === "documents") && (
              <div style={{ margin:"16px 16px 0", padding:12, background:"rgba(255,255,255,0.02)", borderRadius:8, border:"1px solid rgba(132,241,237,0.1)" }}>
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:8 }}>
                  <div style={{ fontSize:11, color:"#888888", textTransform:"uppercase", letterSpacing:"0.8px" }}>Documents</div>
                  {consentGiven && !submitted && (
                    <button onClick={() => { uploadForItemRef.current = null; fileInputRef.current?.click(); }} disabled={loading} title="Upload documents (multiple files supported)" style={{ fontSize:13, color: loading ? "rgba(132,241,237,0.3)" : "rgba(132,241,237,0.7)", background:"transparent", border:"1px solid rgba(132,241,237,0.2)", borderRadius:5, padding:"2px 7px", cursor: loading ? "not-allowed" : "pointer", lineHeight:1 }}>📎 Upload</button>
                  )}
                </div>
                {uploadedDocs.length === 0 && (
                  <div style={{ fontSize:11, color:"rgba(255,255,255,0.2)", fontStyle:"italic" }}>No documents uploaded yet</div>
                )}
                {uploadedDocs.map((doc,i) => (
                  <div key={i} style={{ fontSize:12, color:"#888888", marginBottom:4, display:"flex", alignItems:"center", gap:6 }}>
                    <span title={clientDocVerdict(doc.analysis?.assessment) || undefined} aria-label={clientDocVerdict(doc.analysis?.assessment) || undefined} style={{ color:docDot(doc.analysis?.assessment) }}>●</span>
                    <span style={{ overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{doc.name}</span>
                  </div>
                ))}
              </div>
            )}
            <div style={{ margin:"16px 16px 0", padding:12, background:"rgba(255,255,255,0.02)", borderRadius:8, border:"1px solid rgba(132,241,237,0.1)" }}>
              <div style={{ fontSize:11, color:"#888888", textTransform:"uppercase", letterSpacing:"0.8px", marginBottom:6 }}>Need help?</div>
              <a href={"mailto:" + supportEmail} style={{ fontSize:12, color:"#84f1ed", textDecoration:"none" }}>{supportEmail}</a>
            </div>
          </div>

          {/* Return-visit checklist column (v1_724): on a return with open request
              items, the panel is the primary, wider LEFT column so the client acts
              on it directly. It scrolls independently of the chat. On narrow screens
              the .return-2col media query stacks it above the chat. */}
          {twoColReturn && statusPanelProps && (
            <div className="return-panel-col" style={{ flex:"1.4 1 0", minWidth:340, minHeight:0, overflowY:"auto", padding:"22px 20px", background:"#071727", borderRight:"1px solid rgba(132,241,237,0.1)" }}>
              <ClientStatusPanel {...statusPanelProps} collapsed={false} />
            </div>
          )}

          {/* Chat */}
          <div className="hex-bg" style={{ flex: twoColReturn ? "1 1 0" : 1, maxWidth: twoColReturn ? 560 : "none", minWidth:0, minHeight:0, display:"flex", flexDirection:"column", overflow:"hidden", marginLeft: (isSowInterview || stage === "mlro_followup" || statusData) ? 0 : 220 }}>
            {statusData && !isSowInterview && !twoColReturn && (
              statusCollapsed ? (
                <button onClick={() => setStatusCollapsed(false)} className="status-wrap" style={{ position:"fixed", top:76, right:16, zIndex:400, padding:"8px 16px", borderRadius:20, border:"1px solid rgba(132,241,237,0.4)", background:"#0a1f33", color:"#84f1ed", fontSize:12, fontWeight:600, cursor:"pointer", fontFamily:"inherit", boxShadow:"0 4px 20px rgba(0,0,0,0.4)" }}>
                  Application status {statusData.status === 'approved' ? '· Approved' : statusData.status === 'declined' ? '· Decision made' : '· In review'}
                </button>
              ) : (
                <div className="status-wrap" style={{ position:"fixed", top:76, right:16, zIndex:400, width:"min(430px, calc(100vw - 32px))", maxHeight:"calc(100vh - 100px)", overflowY:"auto", boxShadow:"0 8px 40px rgba(0,0,0,0.55)", borderRadius:12 }}>
                  <ClientStatusPanel {...statusPanelProps} collapsed={false} onToggle={() => setStatusCollapsed(true)} />
                </div>
              )
            )}
            {/* Production fault (29/10/2026, 27 documents): the card sat between the
                messages and the reply box with every document listed, so a long file
                filled the chat column and hid Alex's question and the reply box. It is
                now one line ABOVE the chat ("27 documents received, show list"), and
                its list opens in a box with a height limit: it never takes the
                chat's place. */}
            {dfPrefiled && !submitted && prefiledFirmDocs.some(d => d.state === 'awaiting' || d.state === 'needs_current') && (
              <div className="firm-questions-wrap" data-prefiled-wrap style={{ padding: "12px 40px 0", flexShrink: 1, minHeight: 0, maxHeight: "50%", overflowY: "auto" }}>
                <PrefiledDocsCard compact collapsible docs={docsOnFile} onConfirm={confirmPrefiled} onReplace={replacePrefiled} busy={prefiledBusy} />
                {prefiledNotice && <div role="alert" data-prefiled-notice style={{ fontSize: 12.5, color: '#ffcf8a', lineHeight: 1.6, margin: '-8px 0 10px' }}>{prefiledNotice}</div>}
              </div>
            )}
            <div className="chat-padding" ref={chatScrollRef} onScroll={onChatScroll} style={{ flex:1, minHeight:0, overflowY:"auto", padding:"32px 40px" }}>
              {messages.map((msg, i) => (
                <div key={i} className="msg" style={{ display:"flex", justifyContent:msg.role==="user"?"flex-end":"flex-start", marginBottom:16 }}>
                  {msg.role === "assistant" && (
                    <div style={{ width:28, height:28, background:"linear-gradient(135deg,#13a4a4,#84f1ed)", borderRadius:8, display:"flex", alignItems:"center", justifyContent:"center", fontSize:12, fontWeight:700, color:"white", marginRight:10, flexShrink:0, marginTop:2 }}>A</div>
                  )}
                  <div style={{ maxWidth:"70%", padding:"12px 16px", borderRadius:msg.role==="user"?"16px 16px 4px 16px":"4px 16px 16px 16px", background:msg.role==="user"?"rgba(19,164,164,0.18)":"rgba(17,17,17,0.85)", border:"1px solid " + (msg.role==="user"?"rgba(19,164,164,0.3)":"rgba(132,241,237,0.1)"), fontSize:14, lineHeight:1.7, color:msg.role==="user"?"#a5c8f8":"rgba(255,255,255,0.82)", whiteSpace:"pre-wrap" }}>
                    {/* v1_813: Alex's words are cleaned of dashes for display; the client's own words are shown as typed. */}
                    {renderMarkdown(msg.role === 'assistant' ? plainText(msg.content) : msg.content)}
                  </div>
                </div>
              ))}

              {/* Document in flight. Named file, size, which of how many, the phase, and a clock, so
                  a long wait on a large scan reads as work in progress rather than a dead page. The
                  bar is indeterminate: the storage client does not report byte progress, and an
                  invented percentage that stalls at 90% is worse than an honest moving bar. */}
              {loading && docProgress && (
                <div style={{ display:"flex", alignItems:"flex-start", gap:10, marginBottom:16 }}>
                  <div style={{ width:28, height:28, background:"linear-gradient(135deg,#13a4a4,#84f1ed)", borderRadius:8, display:"flex", alignItems:"center", justifyContent:"center", fontSize:12, fontWeight:700, color:"white", flexShrink:0, marginTop:2 }}>A</div>
                  <div style={{ maxWidth:"70%", minWidth:280, padding:"12px 16px", borderRadius:"4px 16px 16px 16px", background:"rgba(17,17,17,0.85)", border:"1px solid rgba(132,241,237,0.15)" }}>
                    <div style={{ fontSize:13.5, color:"rgba(255,255,255,0.82)", lineHeight:1.5 }}>
                      {docProgress.phase === 'uploading' ? 'Receiving' : docProgress.phase === 'reading' ? 'Reading' : 'Filing'} {docProgress.name}
                    </div>
                    <div style={{ fontSize:11.5, color:"rgba(255,255,255,0.4)", marginTop:3 }}>
                      {docProgress.sizeMb >= 0.1 ? docProgress.sizeMb.toFixed(1) + ' MB' : 'small file'}
                      {docProgress.total > 1 ? ` \u00b7 document ${docProgress.index} of ${docProgress.total}` : ''}
                      {progressElapsed >= 3 ? ` \u00b7 ${progressElapsed}s` : ''}
                    </div>
                    <div style={{ marginTop:9, height:3, borderRadius:2, background:"rgba(255,255,255,0.08)", overflow:"hidden" }}>
                      <div className="progress-sweep" style={{ height:"100%", width:"38%", borderRadius:2, background:"linear-gradient(90deg,#13a4a4,#84f1ed)" }} />
                    </div>
                    {progressElapsed >= 20 && (
                      <div style={{ fontSize:11, color:"rgba(255,255,255,0.35)", marginTop:8, lineHeight:1.5 }}>
                        {docProgress.sizeMb >= 8
                          ? 'This is a large document, so it is taking a little longer. Please leave this page open.'
                          : 'Still working on it. Please leave this page open.'}
                      </div>
                    )}
                  </div>
                </div>
              )}
              {loading && !docProgress && (
                <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:16 }}>
                  <div style={{ width:28, height:28, background:"linear-gradient(135deg,#13a4a4,#84f1ed)", borderRadius:8, display:"flex", alignItems:"center", justifyContent:"center", fontSize:12, fontWeight:700, color:"white" }}>A</div>
                  <div style={{ display:"flex", gap:4 }}>
                    {[0,1,2].map(d => <div key={d} className="dot" style={{ width:8, height:8, borderRadius:"50%", background:"#84f1ed", animationDelay:(d*0.2)+"s" }} />)}
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* 29/10/2026: the cards between the messages and the reply box (the
                analysis strip, the closing request items, the firm's own questions)
                share one area that scrolls and never takes more than 45 percent of
                the column, and the reply box never shrinks: whatever stacks up, the
                reply box stays on screen (the probe found the firm question card
                pushing it off a 720px screen with the documents list open). */}
            <div data-chat-extras style={{ flexShrink: 1, minHeight: 0, maxHeight: "45%", overflowY: "auto" }}>
              {collectedData && collectedData.documentsFirstChat && analysisProgress.length > 0 && (
                <AnalysisStrip entries={analysisProgress} complete={!!collectedData.boardReady} />
              )}
              {!submitted && consentGiven && softCloseItems({ requestItems: collectedData?.requestItems, lastMessage: messages[messages.length - 1] }) && (
                <div style={{ padding: "0 40px" }}>
                  <RequestItemsCard mode="close" sessionId={sessionIdRef.current} token={tokenRef.current}
                    items={softCloseItems({ requestItems: collectedData.requestItems, lastMessage: messages[messages.length - 1] })}
                    onAddNow={(i) => { uploadForItemRef.current = i.id; fileInputRef.current?.click(); }}
                    onChanged={(it) => { if (!it) return; setCollectedData(prev => ({ ...prev, requestItems: (prev.requestItems || []).map(x => x.id === it.id ? { ...x, state: it.state, dueAt: it.dueAt, note: it.note, ...(it.owner ? { owner: { kind: 'delegate', name: it.owner.name } } : {}) } : x) })); }} />
                </div>
              )}
              {/* v1_833: the questions the firm asks every client, below the chat
                  from the documents stage on (and throughout a documents first chat),
                  until each is answered. */}
              {!submitted && consentGiven && !isSowInterview && (collectedData?.documentsFirstChat || ['documents', 'summary'].includes(stage))
                && firmQuestionRows(firmQuestionList, collectedData).some(r => !r.answered && !r.retired) && (
                <div style={{ padding: "0 40px" }} className="firm-questions-wrap">
                  <FirmQuestionsCard questions={firmQuestionList} collected={collectedData} sessionId={sessionIdRef.current} token={tokenRef.current} via="interview" oneAtATime
                    intro="We ask these of every client, whatever the business. Type your answer and it is saved to your file; the next question follows."
                    onSaved={(answers) => setCollectedData(prev => ({ ...prev, firmQuestions: answers }))} />
                </div>
              )}
            </div>
            {/* v1_729: jump-to-latest. Appears only when the client has scrolled up
                from the bottom of the transcript, floats just above the input, and
                clears itself at the bottom (onChatScroll). */}
            {showJumpLatest && (
              <div style={{ position:"relative", height:0 }}>
                <button onClick={() => { scrollChatToBottom('smooth'); chatAtBottomRef.current = true; setShowJumpLatest(false); }}
                  aria-label="Jump to latest message"
                  style={{ position:"absolute", bottom:8, left:"50%", transform:"translateX(-50%)", zIndex:6, padding:"6px 14px", borderRadius:20, border:"1px solid rgba(132,241,237,0.4)", background:"#0a1f33", color:"#84f1ed", fontSize:12, fontWeight:600, cursor:"pointer", fontFamily:"inherit", boxShadow:"0 4px 20px rgba(0,0,0,0.45)", display:"flex", alignItems:"center", gap:6, whiteSpace:"nowrap" }}>
                  <span style={{ fontSize:13, lineHeight:1 }}>↓</span> Latest
                </button>
              </div>
            )}
            <div className="input-padding" style={{ padding:"16px 40px 24px", borderTop:"1px solid rgba(132,241,237,0.1)", background:"#071727", flexShrink:0 }}>

              <div style={{ display:"flex", gap:10, alignItems:"flex-end" }}>
                <button onClick={() => { uploadForItemRef.current = null; fileInputRef.current?.click(); }} disabled={!consentGiven || loading} style={{ padding:"10px 14px", background: consentGiven && !loading ? "rgba(132,241,237,0.08)" : "rgba(255,255,255,0.03)", border: consentGiven && !loading ? "1px solid rgba(132,241,237,0.35)" : "1px solid rgba(132,241,237,0.1)", borderRadius:8, color: consentGiven && !loading ? "rgba(132,241,237,0.85)" : "rgba(255,255,255,0.15)", cursor: consentGiven && !loading ? "pointer" : "not-allowed", fontSize:18, flexShrink:0, height:44 }} title={submitted ? "Upload additional documents" : "Upload documents (multiple files supported)"}>📎</button>
                <input ref={fileInputRef} type="file" multiple style={{ display:"none" }} onChange={handleFileUpload} accept={UPLOAD_ACCEPT} />
                <textarea value={input} onChange={e => setInput(e.target.value)} onKeyDown={e => { if(e.key==="Enter"&&!e.shiftKey&&!loading){e.preventDefault();handleSend();}}} placeholder={loading ? "Alex is responding, please wait..." : submitted ? "Your application has been finalised." : consentGiven ? "Type your response..." : "Please accept the privacy notice to continue..."} rows={3} disabled={!consentGiven || submitted || loading} style={{ flex:1, padding:"10px 14px", background:"rgba(255,255,255,0.04)", border:`1px solid ${loading ? "rgba(132,241,237,0.05)" : "rgba(132,241,237,0.12)"}`, borderRadius:8, color:"#e8e8e0", fontSize:14, fontFamily:"inherit", resize:"vertical", lineHeight:1.6, minHeight:60, maxHeight:200, opacity: consentGiven && !loading ? 1 : 0.4, transition:"all 0.2s" }} />
                <button className="send-btn" onClick={handleSend} disabled={loading||!input.trim()||!consentGiven||submitted} style={{ padding:"10px 20px", background: loading ? "rgba(19,164,164,0.4)" : "#13a4a4", border:"none", borderRadius:8, color:"white", fontSize:14, fontWeight:600, cursor: consentGiven && !loading ? "pointer" : "not-allowed", fontFamily:"inherit", height:44, flexShrink:0, transition:"background 0.2s", opacity: consentGiven && !loading ? 1 : 0.4 }}>Send</button>
              </div>
              {/* THE INTERVIEW ENDS ON THE CLIENT'S CLICK, NOT ON A PHRASE
                  (v1_561). SoW completion fired only when the assistant's
                  reply happened to contain one of a hardcoded list of English
                  pleasantries — so an interview that wrapped up in any other
                  words left the client certain they had finished while the
                  file stayed at sow_interview forever (both UBOs on one live
                  case, 14/08/26). The main flow uses an explicit typed SUBMIT
                  for exactly this reason. The button appears once the
                  conversation has substance; the phrase triggers remain as a
                  convenience path, never the only path. Answers already given
                  persist — clicking finish on a reopened link submits them. */}
              {isSowInterview && consentGiven && !submitted && !loading && messages.filter(m => m.role === 'user').length >= 3 && (
                <button onClick={() => { setSubmitted(true); setTimeout(() => generateSowReport(), 300); }} style={{ marginTop:8, width:"100%", padding:"12px 20px", background:"#0d7377", border:"1px solid #13a4a4", borderRadius:8, color:"white", fontSize:14, fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}>
                  ✓ Finish and submit my answers
                </button>
              )}
              {consentGiven && !submitted && !loading && stage === "summary" && messages.length > 0 && messages[messages.length - 1]?.role === "assistant" && invitesSubmit(messages[messages.length - 1]?.content) && (
                <div style={{ marginTop:10, textAlign:"center" }}>
                  <button onClick={() => handleSend("SUBMIT")} style={{ padding:"12px 36px", background:"#13a4a4", border:"none", borderRadius:8, color:"#071727", fontSize:14, fontWeight:700, cursor:"pointer", fontFamily:"inherit", letterSpacing:"0.3px" }}>Submit my file to the compliance team</button>
                  <div style={{ fontSize:11, color:"rgba(255,255,255,0.3)", marginTop:6 }}>Please check the summary above is accurate first. You can also type SUBMIT.</div>
                </div>
              )}
              {consentGiven && !submitted && (
                <div style={{ display:"flex", justifyContent:"space-between", marginTop:6, paddingLeft:2 }}>
                  <div style={{ fontSize:11, color:"rgba(132,241,237,0.4)" }}>📎 Click the paperclip to upload documents. You can select multiple files at once</div>
                  <div style={{ fontSize:11, color:"rgba(255,255,255,0.2)" }}>Shift+Enter for new line · Enter to send</div>
                </div>
              )}
            </div>
          </div>
        </div>
    </div>
  );
}
